import {useCallback, useEffect, useMemo, useRef} from "react";
import {v4 as uuid4} from "uuid";
import {observable, runInAction, toJS, makeAutoObservable, isObservableArray} from "mobx";
import {toast} from "react-toastify";
import {useStreamConnector} from "../hooks/useStreamConnector";
import {captureMessage, isImage, safeParseJSON, saveBlob, parseContentDisposition, wait} from "../lib/utils";
import {DEV, getConfig, getVersionInfo} from "../lib/configMgr";
import {cancelStream, fetchTurnRunning} from "../queries/search";
import {fetchChatCompletion, fetchHelp, oneshot} from "../queries/autosuggest";
import {LLM_REQUEST_TIMEOUT} from "../queries/request";
import {ROLE_SYSTEM, ROLE_USER, ROLE_ASSISTANT} from "./roles";
import {ConversationTree} from "./ConversationTree";
import {forkLineage} from "./forkLineage";
import {INTERRUPTED_TOOL_TEXT, isInterruptionStandIn, resumeRefusal, withoutDisplayOnly} from "./resume";
import useWatchdog from "../hooks/useWatchdog";
import {TEXT_PLACEHOLDER} from "../templates/slides";
import {memoryStore} from "../stores/MemoryStore";
import {visualizationStore} from "../stores/VisualizationStore";
import {customWidgetsEnabled} from "../lib/customWidgets";
import Analytics from "../lib/Analytics";

const { ExternalRESTAPI } = getConfig();

// Avoid inadvertent timeouts while debugging
export const STREAM_TIMEOUT = DEV ? 6000000 : 180000;
// Timeout for the initial POST before the stream_id is returned. Referenced
// lazily (POST_TIMEOUT below) rather than aliased at module-eval time: request.js
// sits in a large import cycle (lib/utils -> lib/links -> markdown/interpolation
// -> ... -> lib/SearchContext -> queries/request -> lib/utils), so Rollup's
// module ordering can run this file's top-level code before request.js's own
// LLM_REQUEST_TIMEOUT declaration executes, throwing a TDZ ReferenceError.
// Must exceed MAX_GATEWAY_RETRIES * 30s backoff in request.js (currently 2 * 30s = 60s plus request time).
const POST_TIMEOUT = () => LLM_REQUEST_TIMEOUT;
// Retry budget for a single alive=false status reading before trusting it as terminal
// (see checkAgentAlive in handleStreamingError below) — covers transient backend blips,
// not a real outage, so kept short; the watchdog still catches genuinely dead runs.
const ALIVE_CHECK_RETRIES = 2;
const ALIVE_CHECK_RETRY_DELAY = 1000;
// PLEX-6303 (Tim, 2026-10-06): a stream the client lost reconnects instead of ending the
// turn. useStreamConnector has already retried the connection itself (MAX_RECONNECTS)
// by the time it reports the error; while the backend says the turn is running, or
// can't say, the connection is reopened from its cursor this many more times, this far
// apart, before the engine detaches from the turn (see _handleStreamError).
export const STREAM_RECONNECT_LIMIT = 2;
export const STREAM_RECONNECT_DELAY_MS = 5000;

// Per-event verbose debug logging. Off by default — these fire on every
// SSE delta (~30 Hz during streaming) and each call does a JSON.stringify
// of the event payload, which is noticeable in dev mode when React
// DevTools is hooked. Toggle for a session via:
//   localStorage.setItem("sseVerbose", "1"); location.reload();
// Or for one tab without reload:  window.__sseVerbose = true
// Disable with localStorage.removeItem("sseVerbose") or = false.
const _sseVerboseEnabled = () => {
  try {
    if (typeof window !== "undefined") {
      if (window.__sseVerbose === true) return true;
      if (window.__sseVerbose === false) return false;
      if (typeof window.localStorage !== "undefined"
          && window.localStorage.getItem("sseVerbose") === "1") return true;
    }
  } catch (_) { /* private mode / SSR */ }
  return false;
};
const _sseDebug = (...args) => {
  if (_sseVerboseEnabled()) console.debug(...args);
};

export const SSE_EVENT_TYPES = [
  "search_update",
  "search_results",
  "message_start",
  "content_block_start",
  "content_block_delta",
  "content_block_stop",
  "interrupt",
  "message_delta",
  "message_stop",
  "message",
  "ping",
  "heartbeat",
  "metadata",
  "memory-reflect",
  "memory-recall",
  "memory-retain",
  "query-rewrite",
  "visualization",
  "plan",
  "file_block",
  "file_suggestion",
  "toast",
  // PLEX-6261: the first frame of a /sse stream, naming its conversation.
  "conversation_id",
];

// Tools whose results must be preserved in full — they carry ID→label mappings
// that the guide relies on to correctly attribute search results.
const PRESERVE_TOOL_RESULT_NAMES = new Set(['resolve', 'fetch_entities', 'validate_plex_ids']);

// Optimize sub-agent cached messages for persistence:
// - toolResult blocks must always be present (Bedrock schema requires pairing with toolUse)
// - For large-output tools: keep the shell but strip content to save space
// - For resolve/fetch_entities: preserve content in full (ID→label mappings are critical)
// Exported so any conversationWithContext()-style save-payload builder (e.g.
// ChatSession.js's own, separate from this class's method) can serialize
// `client.subAgentTurns` the same way — see PLEX-6300 follow-up.
export const optimizeAgentMessages = (messages) => {
  if (!messages || messages.length === 0) return null;

  // Build toolUseId → tool name map from this message array
  const toolNames = {};
  messages.forEach(msg => {
    (msg.content || []).forEach(block => {
      if (block.toolUse) toolNames[block.toolUse.toolUseId] = block.toolUse.name;
    });
  });

  const optimized = messages.map(msg => {
    if (!msg.content) return msg;
    const optimizedContent = msg.content.map(block => {
      if (!block.toolResult) return block;
      const toolName = toolNames[block.toolResult.toolUseId];
      if (PRESERVE_TOOL_RESULT_NAMES.has(toolName)) return block;
      // Keep shell (required for schema pairing), strip content
      return {...block, toolResult: {toolUseId: block.toolResult.toolUseId, content: []}};
    });
    return {...msg, content: optimizedContent};
  });
  return optimized.length > 0 ? optimized : null;
};

// PLEX-6478: the note a stopped turn ends with, the root's as well as a sub-agent's.
export const INTERRUPTED_NOTE = "*(Response was interrupted)*";

// PLEX-6478: a sub-agent transcript that was still streaming when the user stopped the
// turn, finalized the way one that reached end_turn is (a plain, optimized array), with
// the root's interrupted note at the end, so it shows where it stopped. Partial flags
// are cleared, as for the root's own interrupted turn. Null when there's nothing to keep.
// Does not modify its argument.
export const stoppedSubAgentTrace = (messages) => {
  const trace = optimizeAgentMessages(messages);
  if (!trace) return null;
  const turns = trace.map(({partial, ...turn}) => ({
    ...turn,
    content: (turn.content || []).map(({partial: _blockPartial, ...block}) => block),
  }));
  const last = turns[turns.length - 1];
  if (last.role === ROLE_ASSISTANT) {
    const content = [...last.content];
    const lastText = content.map(b => b.text != null).lastIndexOf(true);
    if (lastText >= 0) {
      content[lastText] = {...content[lastText], text: `${content[lastText].text}\n\n${INTERRUPTED_NOTE}`};
    } else {
      content.push({text: INTERRUPTED_NOTE});
    }
    turns[turns.length - 1] = {...last, content};
  } else {
    turns.push({role: ROLE_ASSISTANT, content: [{text: INTERRUPTED_NOTE}]});
  }
  return turns;
};

export const cleanTurn = (turn, forPersistence = false) => {
  if (!turn) return turn;
  // subAgentTurns lives at the top level of the client/conversation now, never
  // nested per-turn — nothing to strip/optimize here. See
  // LLMClient.conversationWithContext for where the top-level dict is optimized.
  if (turn.toolUseInfo){
    delete turn.toolUseInfo;
  }
  // Strip streaming-only flags that must never be persisted or sent to the API.
  // `partial` on a turn or block means the stream was still open when this was saved;
  // leaving it in storage causes the conversation to reload with a permanently-stuck
  // partial-message UI state.
  // `partial` is deliberately NOT stripped here. loadConversation owns that decision:
  // it demotes partial to stopReason: "interrupted" on a plain reload, or KEEPS it when
  // demotePartial is false so a resuming SSE replay can extend the still-open block.
  // Stripping it here (and on persist) made both paths dead code and let a conversation
  // interrupted mid-stream reload as though the answer had finished.
  if (turn.content) {
    turn.content.forEach(block => {
      if (block.suppressFinalAnswer !== undefined) delete block.suppressFinalAnswer;
    });
  }
  return turn;
};

// One-off back-compat (PLEX-6183): rewrite the pre-rename "search" wire name to the
// canonical TOOL_SEARCH_ANALYST on any turn loaded from storage, so every other
// read-side consumer (visibility gates, retry detection, export/download text) only
// ever has to match the canonical name. See the TOOL_SEARCH_ANALYST comment above.
// PLEX-6303: rd #244 answers a dispatch for a conversation that already has a turn running
// with 409 "This conversation already has a response in progress", which RequestBuilder
// turns into a ServerError carrying the status.
export const isLiveTurnConflict = (error) =>
  error?.status === 409 && /already has a response in progress/i.test(error?.message || "");

// PLEX-6303: could this stored conversation's last turn still be running? Yes when its last
// assistant turn asks for a tool whose result isn't there yet, or a turn is still flagged
// partial. Only then is it worth asking the backend; any other conversation loads as before.
export const mayBeMidTurn = (turns) => {
  const list = (turns || []).filter(t => t != null);
  if (list.some(t => t.partial || (Array.isArray(t.content) && t.content.some(b => b?.partial)))) {
    return true;
  }
  let last = -1;
  for (let k = list.length - 1; k >= 0; k--) {
    if (list[k].role === ROLE_ASSISTANT) { last = k; break; }
  }
  if (last < 0) return false;
  const asked = (list[last].content || []).filter(b => b?.toolUse).map(b => b.toolUse.toolUseId);
  if (!asked.length) return false;
  const answered = new Set(list.slice(last + 1).flatMap(t => (t.content || [])
    .filter(b => b?.toolResult?.content?.length).map(b => b.toolResult.toolUseId)));
  return asked.some(id => !answered.has(id));
};

const normalizeLegacyToolNames = (turn) => {
  (turn?.content || []).forEach(block => {
    if (block.toolUse?.name === LEGACY_TOOL_SEARCH_ANALYST) {
      block.toolUse.name = TOOL_SEARCH_ANALYST;
    }
  });
  return turn;
};

// One-off back-compat (PLEX-6183): major-version threshold for the "search" ->
// TOOL_SEARCH_ANALYST rename, which landed exactly at the 9.1.x -> 10.0.x boundary.
// An unknown/not-yet-loaded version fails toward the canonical (new) name, since
// that's the long-term state once every cell has redeployed. Delete alongside
// LEGACY_TOOL_SEARCH_ANALYST once no cell runs a <=9.1.x backend.
const isLegacyBackendVersion = (version) => {
  const major = parseInt(/^(\d+)\./.exec(version || "")?.[1], 10);
  return Number.isFinite(major) && major < 10;
};

export const useLLMClient = (sessionAgent, searchParams, auth, userStore = null, appStatusStore = null) => {
  // Create client once per sessionAgent - don't recreate on searchParams changes
  // searchParams is only used when sending prompts, not during construction
  const client = useMemo(() =>
                           new LLMClient({
                                           sessionAgent,
                                           auth,
                                           userStore,
                                           appStatusStore,
                           }),
                         [sessionAgent]);
  const {streamId} = client;
  const [resetWatchdog, stopWatchdog] = useWatchdog(client._handleTimeout, STREAM_TIMEOUT);
  // PLEX-6303: useStreamConnector's reconnect(), and how many times this stream used it.
  const reconnectRef = useRef(null);
  const reconnectsRef = useRef(0);
  useEffect(() => { reconnectsRef.current = 0; }, [streamId]);
  useEffect(() => {
    if (streamId === null) {
      stopWatchdog();
    }
  }, [streamId, stopWatchdog]);
  const handleStreamingResponse = useCallback((e) => {
    // This callback closes over `streamId` as of the render that created it, and
    // useStreamConnector's effect is keyed on the same value — so this instance is
    // only ever handed to the connection opened for THIS streamId. If a chunk from
    // that connection was already resolved/parsed before abort() took effect on a
    // streamId transition (see useStreamConnector's dispatchEventsInBatches), it can
    // still reach here after `client.streamId` has moved on. Drop it rather than
    // apply a stale connection's event to the now-current turn (WEBAPP-7F/7G).
    if (streamId !== client.streamId) {
      console.debug(`[LLMClient] Dropping event from abandoned stream ${streamId?.slice(0, 4)} (current: ${client.streamId?.slice(0, 4)})`);
      return;
    }
    // Reset the watchdog on every SSE event — pings prove the connection and server
    // are alive. During long parallel tool execution the backend sends only pings;
    // filtering them out starved the watchdog and caused 180s stalls (PLEX-5825).
    // Guard: only reset while a stream is active. Sub-agent SSE connections can outlive
    // the main stream close; stale events (streamId=null AND active=false) must not restart
    // a stopped watchdog and cause a spurious 180s timeout (WEBAPP-3S).
    // Also reset when active=true but streamId=null — this window exists between old-stream
    // close and new stream_id receipt during tool-result round-trips; sub-agent events in
    // that window are valid and must keep the watchdog alive.
    if (client.streamId || client.active) {
      resetWatchdog();
    }
    client.handleStreamEvent(e);
  }, [streamId]);
  const handleStreamingError = useCallback((e) => {
    // Same abandoned-connection guard as handleStreamingResponse — an error from a
    // stream this render no longer considers current shouldn't affect client state.
    if (streamId !== client.streamId) {
      return;
    }
    // On SSE disconnect while a stream is active, ask whether the turn is still
    // running, so we can surface a failure immediately rather than waiting out the
    // full watchdog timeout. PLEX-6259: by conversation (else stream) id, not agent id
    // (/agent/{id}/status is retired). A resumed stream belongs to an agent id this
    // engine never had, so the old lookup could not find it.
    if (client.streamId && client.active) {
      // PLEX-6303 (Tim, 2026-10-06): reconnect rather than end the turn. Reopen the
      // stream from its cursor (STREAM_RECONNECT_LIMIT times, STREAM_RECONNECT_DELAY_MS
      // apart); after that, hand the error to the engine, which detaches from the turn
      // without cancelling it.
      const reconnectOrDetach = () => {
        if (reconnectsRef.current < STREAM_RECONNECT_LIMIT && reconnectRef.current) {
          reconnectsRef.current += 1;
          const attempt = reconnectsRef.current;
          wait(STREAM_RECONNECT_DELAY_MS).then(() => {
            if (streamId !== client.streamId) {
              return;
            }
            console.debug(`[LLMClient] reconnecting stream ${streamId?.slice(0, 8)} (${attempt}/${STREAM_RECONNECT_LIMIT})`);
            if (!reconnectRef.current?.()) {
              stopWatchdog();
              client.handleStreamEvent(Object.assign(e, { turnMayBeRunning: true }));
            }
          });
          return;
        }
        stopWatchdog();
        client.handleStreamEvent(Object.assign(e, { turnMayBeRunning: true }));
      };
      // A single alive=false reading can reflect a momentary backend hiccup (Redis
      // latency, thread scheduling) rather than the agent actually having stopped —
      // re-check a couple times before trusting it and surfacing a terminal error to
      // the user (WEBAPP-10 / PLEX-5837). Mirrors the gateway 502/503 retry in request.js.
      const checkAgentAlive = (attempt = 0) => {
        fetchTurnRunning(client.historyId || client.streamId, client._auth).then(running => {
          // Re-check abandonment: the user may have cancelled/interrupted while this
          // fetch was in flight, nulling client.streamId. Without this, a stale error
          // for an already-cancelled stream still gets processed and reported as a
          // spurious "Connection failed" Sentry error (WEBAPP-2).
          if (streamId !== client.streamId) {
            return;
          }
          // null (couldn't tell) is not trusted as dead, as a failed agent-status
          // fetch never was.
          if (running === false) {
            if (attempt < ALIVE_CHECK_RETRIES) {
              wait(ALIVE_CHECK_RETRY_DELAY).then(() => {
                if (streamId !== client.streamId) {
                  return;
                }
                checkAgentAlive(attempt + 1);
              });
              return;
            }
            stopWatchdog();
            // PLEX-6439: the backend saves this conversation (the dispatch answered
            // history_persisted), so the turn that ended while the stream was down is in
            // its stored record: load it instead of reporting the connection as lost.
            if (client.historyPersisted && client.historyId) {
              client.markTurnEnded();
              return;
            }
            client._handleError(
              "Connection lost and agent is no longer running.\n\n" +
              "The request may have failed on the server. Please try again."
            );
          } else {
            reconnectOrDetach();
          }
        }).catch(() => {
          if (streamId !== client.streamId) {
            return;
          }
          reconnectOrDetach();
        });
      };
      checkAgentAlive();
    } else {
      stopWatchdog();
      client.handleStreamEvent(e);
    }
  }, [streamId]);
  const { pollingMode, getLoopHealth, reconnect } = useStreamConnector({
    streamId,
    auth: client._auth,
    onEvent: handleStreamingResponse,
    onError: handleStreamingError,
    // PLEX-6528: detecting the timeout isn't enough — without this the only
    // recovery was the 180s conversation watchdog, by which point the backend
    // had usually already finished and failed to notify (stream_end published
    // to 0 subscribers), so the stream just sat dead until a hard cancel.
    // Route into the same reconnect/detach path handleStreamingError uses.
    onInterChunkTimeout: useCallback((lastEventId) => {
      client._captureStreamAnomaly("SSE inter-chunk timeout: proxy silently dropped mid-stream", {
        level: "warning",
        fingerprint: ["sse-inter-chunk-timeout"],
        tags: { lastEventId: lastEventId ?? null },
      });
      handleStreamingError(Object.assign(new Event("error"), { reason: "inter_chunk_timeout", lastEventId }));
    }, [handleStreamingError]),
    // Poll 404 is ambiguous (channel gone: clean completion vs. disconnect grace
    // window lapsed while the turn is still running — WEBAPP-3S). Ask whether the
    // turn is still running before trusting it as "done", same as handleStreamingError
    // does.
    checkAgentAlive: useCallback(async () => {
      return (await fetchTurnRunning(client.historyId || client.streamId, client._auth)) === true;
    }, []),
    // PLEX-6110: a stream resumed after a reload continues from the cursor
    // attemptStreamResume seeded; a new stream's setter has cleared it to "".
    getResumeCursor: useCallback(() => client._lastEventId || null, []),
  });
  reconnectRef.current = reconnect;
  // Expose on client so ChatSession can include it in analytics events
  client.pollingMode = pollingMode;
  // PLEX-6310: expose the read-loop health snapshot for _handleTimeout to attach to
  // its Sentry capture. See recordTick's comment in useStreamConnector.js — this is
  // deliberately independent of console/breadcrumb logging.
  useEffect(() => { client._getStreamLoopHealth = getLoopHealth; }, [getLoopHealth]);
  useEffect(() => {
    const handleBeforeUnload = event => {
      client.dispose();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, [client])
  useEffect(() => {
    return () => {
      client.dispose();
    }
  }, []);
  useEffect(() => {
    if (streamId) {
      resetWatchdog();
    }
    else {
      stopWatchdog();
    }
  }, [streamId]);

  return client;
};

export const TOOL_MEMBERS = "members";
export const TOOL_RESOLVE = "resolve";
export const TOOL_SEARCH_ANALYST = "run_search_analyst";
// Pre-rename wire name (see commit 9a2c31ec, PLEX-6183) — rd@c7be7173d renamed this
// tool from "search" to TOOL_SEARCH_ANALYST. This is a one-off, not a general pattern:
// stored conversations from before the rename are normalized to the canonical name on
// read (see normalizeLegacyToolNames below), and outbound history replayed to a backend
// still on the pre-rename release is translated back (see _canonicalTurns' use of
// isLegacyBackendVersion). Everywhere else in the codebase should only ever compare against
// TOOL_SEARCH_ANALYST. Delete this and both call sites once no cell runs a <=9.1.x
// backend and no <=9.1.x-era conversation remains in storage.
const LEGACY_TOOL_SEARCH_ANALYST = "search";
export const TOOL_RUN_AGENT = "run_agent";
// The search-analyst's own low-level search-execution call (ts_run_search() in
// rd/search plex/search/llm/tools.py) — carries the real, already-expand_members-resolved
// ids/params that were actually searched, unlike the outer TOOL_SEARCH_ANALYST call's input.
export const TOOL_RUN_SEARCH = "run_search";
// Analyst-specific tools — shown as progress labels to non-power users
export const ANALYST_TOOLS = new Set(["fetch_totals", "fetch_results", "fetch_entities", "fetch_neighbors"]);
// Suffixes recognized by bedrock; assume text if not in one of these
// Claude also accepts odt, rtf, epub, and json; pass these as "txt"
// See https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_Message.html
// See https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_DocumentBlock.html
// See https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_ImageBlock.html
export const FILE_INPUT_ACCEPT = [
  ".pdf", "application/pdf",
  ".csv", "text/csv",
  ".doc", "application/msword",
  ".docx", "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".xls", "application/vnd.ms-excel",
  ".xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".html", "text/html",
  ".txt", "text/plain",
  ".md", "text/markdown",
  ".plex", "application/json",
  ".png", "image/png",
  ".jpeg", "image/jpeg",
  ".gif", "image/gif",
  ".webp", "image/webp"
];
export const MIME2FORMAT = {
  "application/pdf": "pdf",
  "text/csv": "csv",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "text/html": "html",
  "text/plain": "txt",
  "text/tab-separated-values": "csv",
  "text/markdown": "md",
  "application/json": "txt",
  "image/png": "png",
  "image/jpeg": "jpeg",
  "image/jpg": "jpeg",
  "image/gif": "gif",
  "image/webp": "webp"
};
export const DOC_LIMIT = 5;
export const DOC_SIZE_LIMIT = 4.5 * 1024 * 1024;
export const IMAGE_LIMIT = 20;
export const IMAGE_SIZE_LIMIT = 3.75 * 1024 * 1024;
// Canonical LLM conversation roles — defined in ./roles to avoid a circular
// import with autosuggest.js (autosuggest -> LLMClient -> autosuggest hit a TDZ
// error on LLM_REQUEST_TIMEOUT in the production bundle, WEBAPP-XXX)
export {ROLE_SYSTEM, ROLE_USER, ROLE_ASSISTANT};
// Special role for preserving conversation w/customization context
export const ROLE_CONTEXT = "context"
// Available "personalities"
export const AGENT_HELP = "help";
export const AGENT_GUIDE = "guide";
export const AGENT_ANALYST = "search-analyst";
export const AGENT_COACH = "coach";

// stopReason values on an assistant message. Mirrors the backend source of
// truth at plex/search/llm/client.py::StopReason — keep the two in sync.
// Bedrock native values are lowercase/unprefixed. The underscore-prefixed
// members are Plex relabels layered on top, not native ones. "error" is also
// Plex-invented but deliberately left unprefixed (established, high-traffic
// wire value; out of scope for this rename).
export const StopReason = Object.freeze({
  // --- Bedrock native (unprefixed) ---
  END_TURN: "end_turn",
  TOOL_USE: "tool_use",
  MAX_TOKENS: "max_tokens",
  STOP_SEQUENCE: "stop_sequence",
  GUARDRAIL_INTERVENED: "guardrail_intervened",
  CONTENT_FILTERED: "content_filtered",
  MODEL_CONTEXT_WINDOW_EXCEEDED: "model_context_window_exceeded",

  // --- Plex-synthesized relabels (underscore-prefixed) ---
  TRUNCATED_TOOL_USE: "_truncated_tool_use",
  ITERATIONS_EXCEEDED: "_iterations_exceeded",
  MALFORMED_TOOL_USE: "_malformed_tool_use",
  TIME_LIMIT: "_time_limit",
});

// Predicate: true when a turn was finalized by the W2 runaway-budget steerer.
// The turn carries a real (possibly truncated) answer — render it as a normal
// complete answer with a subtle "bounded" badge, NOT as an error.
export const isIterationsExceeded = (turn) => turn?.stopReason === StopReason.ITERATIONS_EXCEEDED;

export class LLMClient {
  _turns = [];
  _compaction = null;
  _context = null;
  _agentId = uuid4();
  _streamId = null;
  // Branching tree that backs the v2 Claude.ai-style chat. The legacy flat
  // `turns` array remains the canonical render path (the current cursor's
  // root→leaf walk); the tree captures the off-path branches so the UI can
  // surface ◀ N/M ▶ arrows and `branchEdit`/`regenerate` can preserve
  // overwritten content. Kept in sync via `_appendTurnToTree` /
  // `_truncateTreeToPathLength` helpers wrapped around the mutation points.
  _tree = new ConversationTree();
  _skipNextTreeAppend = false;
  // PLEX-6383: tree nodes the engine created for turns of a dispatch the backend is
  // writing, still under their local ids. Each takes the backend's id when its turn's
  // "message" event arrives (_adoptNodeId); until then it is left out of the saves, so a
  // node the backend keys differently is never stored twice. Not observable.
  _unsyncedNodeIds = new Set();
  _clientEventLog = [];

  // Is there a conversation active?
  _active = false;
  // Has a prompt been sent but server not responded?
  _waitingForResponse = false;
  // PLEX-6303: this conversation's turn is still running on the backend, but this engine
  // isn't attached to it (the chat was reloaded or opened elsewhere mid-turn). The last
  // turn's tool call is then waiting for its result, not failed: the view says the
  // response is still running instead of offering Resume, and nothing is dispatched.
  _turnRunning = false;
  // PLEX-6439: the turn is known to have ended (markTurnEnded), so the running-turn watch
  // should ask again at once instead of after RUNNING_TURN_POLL_MS. Taken by the watch.
  _recheckTurnNow = false;
  // Monotonic counter bumped at the start of every new send cycle (_sendPrompt,
  // _continueFromToolResults) and again by handleInterrupt. Lets a late-resolving
  // fetch (already in flight when the user interrupted) recognize it's stale and
  // avoid reviving streamId/busy state for a cycle that's already been
  // interrupted (WEBAPP interrupt-loop bug).
  _generation = 0;
  // True once handleInterrupt has fully processed an interrupt for the current
  // send cycle. The server independently raises UserInterrupt in the root
  // agent's loop AND in every still-running sub-agent's loop for a single user
  // cancel action, each publishing its own "interrupt" SSE event on the same
  // stream_id channel. Without this guard, every one of those echoes re-appends
  // its own "I was interrupted" turn, producing the repeating-bubble bug. Reset
  // to false whenever a new send cycle starts, so a genuinely new interrupt on
  // that cycle is processed normally.
  _interruptHandled = false;
  // Single flat map for the whole conversation's lifetime, keyed by toolUseId.
  // Not nested per-turn — a toolUseId is unique across the conversation, so there's
  // no reason to shard entries by which root turn originated them.
  _subAgentTurns = new Map();
  // Client-side dedup for the "Turns missing" Sentry fallback: fire captureMessage
  // at most once per (streamId, agentId) pair, so a flood of early SSE frames for a
  // not-yet-registered sub-agent can't spam Sentry (PLEX-4164/4166).
  _reportedMissingAgents = new Set();
  // Fpr use with help
  _sessionId = null;
  _model = null;

  _inputTokens = 0;
  _outputTokens = 0;
  _totalTokens = 0;
  _streamStartTime = null;
  _cacheReadInputTokens = 0;
  _cacheWriteInputTokens = 0;
  _latencyMs = 0;

  _sessionAgent = null;
  _auth = null;
  _userStore = null;
  _visualizationStore = null;
  _onAssistantTurnComplete = null;
  _closeStream = null;
  // SSE Last-Event-ID for resume. Declared here (not assigned ad-hoc in
  // handleStreamEvent) so makeAutoObservable picks it up as an observable —
  // streamCursor's autorun reads this and needs to re-fire on each
  // incoming event, not just on streamId changes.
  _lastEventId = "";
  // Coach's enumerated plan ({steps, currentStep}) from the `set_plan` tool's
  // SSE "plan" event, or null when there's no active plan. Declared here (not
  // assigned ad-hoc in handleStreamEvent) so makeAutoObservable picks it up as
  // an observable — useCoachPlan reads it live during streaming. Cleared on a
  // new question (streamId null→value) and on clear(); see useCoachPlan.js for
  // handoff/completion handling.
  plan = null;
  // Marker set by useConversationPersistence.flushSave when it mints a new chatId
  // and navigates to its URL. useChatLoader checks this to skip the
  // DynamoDB fetch (and the destructive client.clear that goes with it)
  // for a chat the engine itself just created. See fix #2 in
  // docs/persistence-resilience-design.md.
  _mintedChatId = null;
  // Stream origin context — set by useConversationPersistence when streamId
  // transitions falsy→truthy, cleared on truthy→falsy. flushSave reads
  // this to keep persisting to the chat/project the stream came from,
  // even after the user navigates away mid-stream ("+ New chat" or
  // cross-project click). See fixes #4 and #5 in the design doc.
  // Shape: { programId, projectId, chatId } | null.
  _streamCtx = null;
  // V2 chat surface stamps these before each sendPrompt so the backend's
  // /api/search call carries program_id/project_id. The coach file tools
  // (list_project_files, read_project_file) read this scope from
  // search_context so they can fire on the very first turn — before the
  // conversation row exists in DDB.
  _v2ProgramId = null;
  _v2ProjectId = null;
  // PLEX-6383: the conversation (history row) this engine's dispatches belong to.
  // Every dispatch sends it -- including the ones that take no explicit id (edit,
  // regenerate, replay, continue-from-tool-results) -- so that once the backend
  // writes the record, every turn of a chat lands in the same row. Set by the
  // first dispatch that carries an explicit id, by the v2 loader for a loaded
  // chat, and from the dispatch response when the backend minted the id itself.
  // Reset by clear().
  historyId = null;
  // PLEX-6187: counts the conversations this engine has shown (loadConversation, clear()).
  // The classic chat keeps one engine for every conversation it opens, so a resume answered
  // after another was opened belongs to the one it was sent for (resumeFrom).
  _loads = 0;
  // PLEX-6491: the id of a loaded conversation this user doesn't own (a shared chat),
  // set by the v2 and classic loaders. Its first dispatch forks it (_forkFirst).
  // Observable, so SharedCopyNotice can say that replying makes the user's own copy;
  // reset by clear().
  _forkSourceId = null;
  // True when the backend took the history write for the CURRENT dispatch (the
  // dispatch response's history_persisted). Reset at the start of every dispatch,
  // so it never outlives the dispatch it describes.
  historyPersisted = false;
  // Event id at the time of the most recent SUCCESSFUL flushSave.
  // Used by the streamCursor autorun: the cursor's lastEventId must
  // be aligned with what persisted state already covers, otherwise
  // a mid-stream reload would resume past a message_start that
  // hasn't been saved yet — leaving the engine with no partial turn
  // for replayed content_block_delta events to extend. flushSave
  // captures `_lastEventId` at save-invocation time and writes it
  // here once the save promise resolves.
  _lastSavedEventId = "";

  _appStatusStore = null;
  constructor({
                sessionAgent = AGENT_HELP,
                auth = null,
                userStore = null,
                vizStore = null,
                appStatusStore = null,
  }) {
    makeAutoObservable(this, {
      _sessionAgent: false,
      _onAssistantTurnComplete: false,
      _unsyncedNodeIds: false,
      _onFork: false,
      _closeStream: false,
      _visualizationStore: false,
      // Shallow: entries are swapped wholesale (live observable.array while a sub-agent
      // streams, plain array once it reaches end_turn) rather than deep-observed in place.
      // Deep (the makeAutoObservable default) would keep every string/object node in a
      // finished sub-agent's turns wrapped in MobX Proxies/Atoms forever, even though
      // nothing observes them again after that sub-agent's toolResult closes.
      _subAgentTurns: observable.shallow,
    });
    this._sessionAgent = sessionAgent;
    this._auth = auth;
    this._userStore = userStore;
    // Allow injection for testing; fall back to module-level singleton
    this._visualizationStore = vizStore ?? visualizationStore;
    this._appStatusStore = appStatusStore;
  }

  /**
   * Register a callback fired whenever a complete turn is added or finalized
   * in the conversation — user prompts, assistant responses, and tool-result
   * turns (sub-agent updates).  Used to persist incremental progress.
   */
  set onTurnComplete(fn) { this._onAssistantTurnComplete = fn; }
  /**
   * PLEX-6491: register `(sourceId) => Promise<forkId>`, called before the first dispatch
   * on a conversation this user doesn't own. useConversationPersistence saves the user's copy, and
   * resolves with the id the backend gave it; the engine moves to that id and dispatches.
   */
  set onFork(fn) { this._onFork = fn; }
  // PLEX-6383: true while the backend persists the in-flight dispatch. The backend
  // writes the record at every message_stop itself, so a client that saves on
  // onTurnComplete can skip those saves and save once when the dispatch settles.
  get deferTurnSaves() {
    return this.historyPersisted && !!(this.streamId || this.waitingForResponse);
  }
  get active() {return this._active;}
  set active(isActive) {this._active = isActive;}
  get turns() {
    return this._turns;
  }
  // ------- Branching tree helpers -------------------------------------
  // Every mutation of `this._turns` goes through one of these so the tree
  // mirrors the linear path. Branch ops set _skipNextTreeAppend before
  // calling code that ultimately pushes a turn — the next push then skips
  // the auto-append because the tree was already mutated explicitly.
  _pushTurn = (turn) => {
    this._turns.push(turn);
    if (this._skipNextTreeAppend) {
      this._skipNextTreeAppend = false;
      return;
    }
    // Read the POST-PUSH reference from _turns — see _mirrorPushToTreeIfMain
    // for why (MobX proxy / shared-array-reference correctness).
    const stored = this._turns.length ? this._turns.at(-1) : turn;
    try {
      this._trackUnsynced(this._tree.append({ role: stored.role, content: stored.content }));
    } catch (e) {
      // Tree drift shouldn't take down chat — log and recover by rebuilding
      // from the canonical turns array.
      console.warn("[LLMClient] tree.append failed; rebuilding tree", e);
      this._rebuildTreeFromTurns();
    }
  };
  _setTurnsLength = (length) => {
    // Delegate the array truncation so discarded sub-agents are released (and any
    // still streaming are cancelled) rather than left as unreachable entries in
    // subAgentTurns; then bring the branching tree's cursor back in step.
    this._truncateTurns(length);
    // Walk cursor up until path length <= target length.
    while (this._tree.cursorId) {
      const path = this._tree.getCurrentPath();
      if (path.length <= length) break;
      const cursorNode = this._tree.nodes.get(this._tree.cursorId);
      if (!cursorNode || !cursorNode.parent) {
        this._tree.cursorId = null;
        this._tree.rootId = null;
        break;
      }
      this._tree.cursorId = cursorNode.parent;
    }
  };
  _resetTurnsAndTree = () => {
    this._turns.length = 0;
    this._tree = new ConversationTree();
    this._unsyncedNodeIds.clear();
  };
  _rebuildTreeFromTurns = () => {
    this._tree = ConversationTree.fromTurns(
      this._turns.map((t) => ({ role: t.role, content: t.content })),
    );
    this._unsyncedNodeIds.clear();
  };
  // PLEX-6383: a node created for a turn of a dispatch the backend writes has a local id
  // until the turn's "message" event names the backend's (_adoptNodeId).
  _trackUnsynced = (node) => {
    if (node && this.historyPersisted) this._unsyncedNodeIds.add(node.id);
  };
  // PLEX-6383: the backend keys a turn's node by the id of the "message" event that
  // published it (rd history/tree.py). Re-key the newest unsynced node of that role, so
  // the engine's tree and the stored one agree and its saves name nodes the backend has.
  _adoptNodeId = (role, eventId) => {
    if (!eventId || !this._unsyncedNodeIds.size) return false;
    const candidates = [...this._unsyncedNodeIds].reverse();
    const nodeId = candidates.find((id) => this._tree.nodes.get(id)?.role === role);
    if (!nodeId || !this._tree.rekey(nodeId, eventId)) return false;
    this._unsyncedNodeIds.delete(nodeId);
    return true;
  };
  // PLEX-6383: where this dispatch's prompt sits in the v2 tree, for the backend writer:
  // the prompt's node (new, merged into, or reused by a regenerate or a continue -- in
  // every case the cursor when the dispatch goes out) and its parent. Classic chat sends
  // none: its records stay flat.
  _treeAnchor = () => {
    if (!this._v2ProjectId) return {};
    const nodeId = this._tree?.cursorId;
    const node = nodeId ? this._tree.nodes.get(nodeId) : null;
    if (!node || node.role !== ROLE_USER || this._unsyncedNodeIds.has(nodeId)) return {};
    return node.parent ? {userNodeId: nodeId, parentNodeId: node.parent} : {userNodeId: nodeId};
  };
  /**
   * PLEX-6383: the tree as a save may send it. Nodes still under a local id for a
   * backend-written turn are left out (with their place in children and preferredChild):
   * the backend stores that turn under its own id. A root on such a node moves to its
   * nearest saved ancestor. A cursor on one is left out too (Tim on plex-search-ui #107:
   * mid-stream, the cursor is the in-flight answer's local id), and not moved to an
   * ancestor (PLEX-6187): the backend takes any cursor it has, so a mid-stream save that
   * lands after the answer is stored would move the record's cursor back to before it.
   * With none named, the backend keeps the cursor its writer set. While the backend writes
   * the dispatch (deferTurnSaves), a save names no cursor at all (Tim on PLEX-6187): between
   * a tool result taking its id and the next message_start the cursor is on a stored node,
   * and a save built then can land after the answer too. The answer's own "message" event
   * ends that, so the first save after names the answer. Everything else is the tree as is.
   */
  treeSnapshotForSave = () => {
    const tree = this._tree.toJSON();
    const snapshot = this.deferTurnSaves ? {...tree, cursorId: null} : tree;
    if (!this._unsyncedNodeIds.size) return snapshot;
    const drop = this._unsyncedNodeIds;
    const nodes = {};
    for (const [id, node] of Object.entries(snapshot.nodes)) {
      if (!drop.has(id)) nodes[id] = {...node, children: node.children.filter((c) => !drop.has(c))};
    }
    const preferredChild = Object.fromEntries(
      Object.entries(snapshot.preferredChild).filter(([k, v]) => !drop.has(k) && !drop.has(v)));
    const saved = (id) => {
      let cur = id;
      while (cur && drop.has(cur)) cur = snapshot.nodes[cur]?.parent ?? null;
      return cur ?? null;
    };
    return {...snapshot, nodes, preferredChild,
            cursorId: drop.has(snapshot.cursorId) ? null : snapshot.cursorId, rootId: saved(snapshot.rootId)};
  };
  // Shared helper used by code paths that push directly to a turns array
  // (rather than going through _pushTurn). When the array IS this._turns
  // (i.e. main-agent path, not a sub-agent slot in subAgentTurns), the
  // tree gets a mirror node so getCurrentPath().length stays in sync with
  // _turns.length. Sub-agent pushes intentionally skip — sub-agent events
  // live in subAgentTurns[agentId] and are persisted via the parent
  // turn's agentMessagesCache.
  //
  // CRITICAL: read content from `turns.at(-1)` (the POST-PUSH reference),
  // NOT from the caller's local turn variable. makeAutoObservable wraps
  // pushed objects in MobX proxies, and the proxy's `.content` is a wrapped
  // observable array — distinct from the raw array on the caller's local.
  // Subsequent content_block_delta mutations route through `lastMessage`
  // (= the proxy), so mutating into the raw local array doesn't propagate
  // to the tree node. Capturing the proxy here keeps the tree node's
  // content reference identical to what the SSE handlers mutate.
  _mirrorPushToTreeIfMain = (turns /* , turn (ignored — see comment) */) => {
    if (turns !== this._turns) return;
    if (this._skipNextTreeAppend) {
      this._skipNextTreeAppend = false;
      return;
    }
    const stored = turns.length ? turns.at(-1) : null;
    if (!stored) return;
    try {
      this._trackUnsynced(this._tree.append({ role: stored.role, content: stored.content }));
    } catch (e) {
      console.warn("[LLMClient] tree.append failed; rebuilding from turns", e);
      this._rebuildTreeFromTurns();
    }
  };
  // Public: how many siblings does the turn at `turnIndex` have? Returns
  // { index, total } where total >= 1 (the turn itself counts). The UI
  // suppresses BranchNav when total === 1.
  getSiblingInfoAtTurn = (turnIndex) => {
    const path = this._tree.getCurrentPath();
    const node = path[turnIndex];
    if (!node) return { index: 0, total: 1 };
    const info = this._tree.getSiblings(node.id);
    return { index: info.index, total: info.total };
  };
  // Public: move the cursor among the siblings of the node at `turnIndex`.
  // After the switch we rebuild `this._turns` from the new cursor's path.
  switchSiblingAtTurn = (turnIndex, direction) => {
    const path = this._tree.getCurrentPath();
    const node = path[turnIndex];
    if (!node) return;
    const leaf = this._tree.switchSibling(node.id, direction);
    if (!leaf) return;
    const newPath = this._tree.getCurrentPath();
    runInAction(() => {
      this._turns.length = 0;
      newPath.forEach((n) => this._turns.push({ role: n.role, content: n.content }));
    });
    // Force a persistence save: cursorId is the user-visible state that
    // changed, but turns.length / streamId / waitingForResponse are
    // unchanged so the useConversationPersistence autorun won't fire. Without this
    // explicit trigger, navigating between sibling branches doesn't
    // reach DynamoDB until the next turn-bearing event — and the next
    // reload silently snaps the user back to the previously-saved sibling.
    if (typeof this._onAssistantTurnComplete === "function") {
      try { this._onAssistantTurnComplete(); } catch (_) { /* save errors handled by the hook */ }
    }
  };
  // Public: edit the user prompt at `turnIndex` → branch a sibling and replay.
  // Preserves the original turn (and its downstream conversation) in the tree.
  branchEditUserTurn = (turnIndex, newText) => {
    const path = this._tree.getCurrentPath();
    const target = path[turnIndex];
    if (!target || target.role !== ROLE_USER) {
      // Fall back to legacy linear replay for content the tree doesn't
      // recognize (e.g. nodes lost to a defensive rebuild).
      return this._legacyEditUserTurn(turnIndex, newText);
    }
    // PLEX-4226: editing the ROOT prompt no longer silently overwrites it. The
    // old code fell back to `_legacyEditUserTurn` here (LLMClient.js ~494-502),
    // which mutated the first turn in place and threw away the original — a
    // footgun with no ◀ to recover it. ConversationTree.branchSibling now forks
    // a *sibling root* (parent === null) for a root target, so a first-prompt
    // edit behaves exactly like any other edit: a navigable branch. Both the
    // original and edited first prompt survive persistence and toggle via
    // ◀ N/M ▶. Root and non-root edits therefore share this one code path.
    const newTurn = { role: ROLE_USER, content: [{ text: newText }] };
    try {
      this._tree.branchSibling(target.id, newTurn);
    } catch (e) {
      console.warn("[LLMClient] branchSibling failed; falling back to linear edit", e);
      return this._legacyEditUserTurn(turnIndex, newText);
    }
    runInAction(() => {
      this._turns.length = turnIndex;
      this._skipNextTreeAppend = true; // tree already has the new node
    });
    this._sendPrompt(newTurn, {});
  };
  // Public: regenerate the assistant turn at `turnIndex` → branch a sibling
  // and replay the prior user prompt.
  regenerateAssistantTurn = (turnIndex) => {
    const path = this._tree.getCurrentPath();
    const assistantNode = path[turnIndex];
    if (!assistantNode || assistantNode.role !== ROLE_ASSISTANT) return;
    if (!assistantNode.parent) return;
    // Walk up the tree past any system-error turns to find the nearest
    // user ancestor. This handles the "regenerate after a stream error"
    // case where the chain is: user → assistant(tool_use) → user(toolResult)
    // → system(error) → assistant(error). The assistant-error's direct
    // parent is the system-error, not a user, so the legacy early-return
    // here silently bailed and the button felt "broken." Walking up
    // restores the canonical user-ancestor regen path.
    let parentNode = this._tree.nodes.get(assistantNode.parent);
    let parentUserIdxInPath = -1;
    while (parentNode && parentNode.role !== ROLE_USER) {
      if (!parentNode.parent) { parentNode = null; break; }
      parentNode = this._tree.nodes.get(parentNode.parent);
    }
    if (!parentNode || parentNode.role !== ROLE_USER) return;
    const parentUserNode = parentNode;
    // Find the user ancestor's index in the current path — that's the
    // _turns index we'll truncate to so the re-push lands cleanly.
    for (let i = 0; i < path.length; i++) {
      if (path[i]?.id === parentUserNode.id) { parentUserIdxInPath = i; break; }
    }
    if (parentUserIdxInPath < 0) return;

    // Snapshot the user turn BEFORE we mutate _turns. We then drop the user
    // turn from _turns (length = parentUserIdxInPath) and let _sendPrompt
    // re-push a fresh copy. parentUserIdxInPath may differ from
    // turnIndex - 1 when error-chain turns were folded above the assistant.
    //
    // Why the snapshot + drop + re-push: _sendPrompt's user/user-append
    // branch (line ~2283) checks `lastTurn.role === ROLE_USER && prompt.role
    // === ROLE_USER` and pushes prompt.content into lastTurn.content. If we
    // pass the still-resident userTurn and the array's last element IS that
    // same object, the loop appends every block back into itself — content
    // doubles on each regen.
    //
    // The re-push is also a tree mutation; we set _skipNextTreeAppend so the
    // tree keeps the original user node (cursor already on parent). The
    // assistant that subsequently streams in then attaches as a sibling of
    // the original assistant under the existing user node — the whole point
    // of "regenerate creates a branch."
    const userTurn = this._turns[parentUserIdxInPath];
    if (!userTurn) return;
    const userTurnSnapshot = {
      ...userTurn,
      content: Array.isArray(userTurn.content) ? userTurn.content.map((b) => ({ ...b })) : userTurn.content,
    };
    delete userTurnSnapshot.ts;
    runInAction(() => {
      this._tree.setCursor(parentUserNode.id);
      this._turns.length = parentUserIdxInPath;
      this._skipNextTreeAppend = true;
    });
    this._sendPrompt(userTurnSnapshot, {});
  };
  // Legacy fallback used when the tree can't service an edit (e.g. node not
  // found because the tree was rebuilt linearly after drift). Replicates the
  // pre-branching behavior of mutating in place and replaying.
  _legacyEditUserTurn = (turnIndex, newText) => {
    const turn = this._turns[turnIndex];
    if (!turn) return;
    const blocks = Array.isArray(turn.content) ? turn.content : [];
    let replaced = false;
    blocks.forEach((b) => {
      if (b && b.text != null && !replaced) {
        b.text = newText;
        replaced = true;
      }
    });
    if (!replaced) turn.content = [{ text: newText }, ...blocks];
    this.replayFrom(turnIndex);
  };
  get compaction() {return this._compaction;}
  set compaction(v) {this._compaction = v;}
  get agentId() {return this._agentId;}
  get streamId() {return this._streamId;}
  set streamId(streamId) {
    const cancelAgents = this.active
                         && this._streamId !== null && streamId === null;
    // PLEX-6259: the turn being stopped; _streamId is already null when it is cancelled.
    const endingStream = this._streamId;
    if (streamId === null && this._streamId !== null) {
      this._closeStream?.();
    }
    // When a new stream starts (transition from null → new id), clear the
    // stale Last-Event-ID from the previous stream. Without this, the
    // resumable SSE wiring would send the prior chat's cursor as
    // Last-Event-ID on the new connection — the backend replays past it
    // and the new chat starts with zero events delivered. The cursor is
    // only meaningful within a single stream's lifetime.
    if (this._streamId === null && streamId !== null) {
      this._lastEventId = "";
      // A fresh or resumed stream is active again — drop the preserve flag so
      // the stale-event guard resumes normal cancel behavior for real orphans.
      this._preserveStreamActive = false;
      // PLEX-4224: clear the resume-scoped flags on the null->truthy edge so a
      // genuinely-fresh stream (e.g. sendPrompt) never adopts a real sub-agent
      // id as the main one, and its completion is never mis-flagged as a resume.
      // attemptStreamResume re-sets these AFTER assigning streamId, so a real
      // resume's values survive this reset.
      this._adoptAgentIdOnResume = false;
      this._resumedAt = null;
      // A new question is starting (the prior turn nulled streamId; tool-use
      // continuations keep it non-null, so this fires once per question).
      // Drop the previous turn's coach plan so it doesn't linger as a stale,
      // stuck checklist over the new response. useCoachPlan then falls back to
      // the (empty) new chain until a fresh set_plan arrives.
      this.plan = null;
    }
    this._streamId = streamId;
    if (streamId !== null) {
      this._clientEventLog = [];
      this._staleEventReported = false;
      this._streamStartTime = Date.now();
    }
    if (streamId === null) {
      this.active = false;
      // Stream ended — a pending adopt can no longer apply to this episode.
      // (_resumedAt is consumed by useBackgroundCompletion when the stream
      //  STARTS, and re-cleared on the next fresh null->truthy edge above, so
      //  it needs no reset here.)
      this._adoptAgentIdOnResume = false;
      this._reportedMissingAgents.clear();
      // Do not clear subAgentTurns here: it holds every sub-agent's turns for the
      // whole conversation, not just the stream that just ended. A stream ending
      // normally (end_turn) doesn't mean those turns are stale — the next
      // user-submitted turn reuses the same client instance and conversation.
      // Anything still present with a live (still-mobx-observable) array here
      // either genuinely finished without a matching cleanup (a bug worth its
      // own fix, not papered over by a blanket clear) or is mid-cancellation, in
      // which case _cancelAgents (below) needs it intact to know which agent IDs
      // to send cancel requests for — clearing it first made that always see an
      // empty map.
    }
    if (cancelAgents) {
      this._cancelAgents(endingStream);
    }
  }
  get waitingForResponse() {return this._waitingForResponse;}
  get turnRunning() {return this._turnRunning;}
  set waitingForResponse(waiting) {this._waitingForResponse = waiting;}
  get subAgentTurns() {return this._subAgentTurns;}
  get sessionAgent() {return this._sessionAgent;}
  get auth() {return this._auth;}
  get context() {return this._context;}
  get model() {return this._model;}
  set model(modelId) {this._model = modelId;}
  get latencyMs() {return this._latencyMs;}
  set latencyMs(ms) {this._latencyMs = ms;}
  get inputTokens() {return this._inputTokens;}
  set inputTokens(tokens) {this._inputTokens = tokens;}
  get outputTokens() {return this._outputTokens;}
  set outputTokens(tokens) {this._outputTokens = tokens;}
  get totalTokens() {return this._totalTokens;}
  set totalTokens(tokens) {this._totalTokens = tokens;}
  get cacheReadInputTokens() {return this._cacheReadInputTokens;}
  set cacheReadInputTokens(tokens) {this._cacheReadInputTokens = tokens;}
  get cacheWriteInputTokens() {return this._cacheWriteInputTokens;}
  set cacheWriteInputTokens(tokens) {this._cacheWriteInputTokens = tokens;}

  /**
   * Serialize the current conversation state to a JSON string.
   * Returns the JSON string and the parsed object.
   */
  _serializeConversation = () => {
    const obj = {
      streamId: this.streamId,
      active: this.active,
      sessionAgent: this.sessionAgent,
      agentId: this.agentId,
      turns: toJS(this._turns).map(turn => turn ? ({
        ...turn,
        content: (turn.content || []).filter(b => !(b.toolResult && !b.toolResult.content?.length)),
      }) : turn),
      compaction: toJS(this._compaction),
      subAgentTurns: Object.fromEntries(
        Array.from(this.subAgentTurns.entries()).map(([k, v]) => [k, toJS(v)])
      ),
    };
    return { obj, json: JSON.stringify(obj, null, 2) };
  };

  /**
   * Build context/attachment options for captureMessage based on conversation size.
   *
   * If the serialized conversation fits within INLINE_THRESHOLD bytes, it goes into
   * Sentry context (visible inline in the UI). Otherwise a summary goes into context
   * and the full JSON goes as an attachment.
   *
   * @param {Object} errorContext - Extra fields to merge into the Sentry context object.
   * @returns {{ context: Object, attachments?: Array, data?: undefined }}
   */
  _buildConversationCapture = (errorContext = {}) => {
    const INLINE_THRESHOLD = 8192;
    const { obj, json } = this._serializeConversation();

    const turns = obj.turns || [];
    const summarizeTurn = t => {
      const block = t.content?.[0];
      let preview = null;
      if (block?.text) preview = block.text.slice(0, 150);
      else if (block?.toolUse) preview = `toolUse: ${block.toolUse.name}`;
      else if (block?.toolResult) preview = `toolResult: ${String(block.toolResult.content).slice(0, 150)}`;
      return { role: t.role, preview };
    };

    const summary = {
      turnCount: turns.length,
      agentId: obj.agentId,
      sessionAgent: obj.sessionAgent,
      streamId: obj.streamId,
      lastTurns: turns.slice(-2).map(summarizeTurn),
      ...errorContext,
    };

    if (json.length <= INLINE_THRESHOLD) {
      // Small enough to include fully inline — still use summary format for consistency,
      // but attach the full object too so nothing is lost.
      return {
        context: { conversation: { ...summary, full: obj } },
      };
    }

    // Large conversation — summary in context, full JSON as attachment.
    return {
      context: { conversation: summary },
      attachments: [{
        filename: "conversation.json",
        data: json,
        contentType: "application/json",
      }],
    };
  };

  _handleTimeout = () => {
    // Guard: if the stream is already finished, this is a stale timer (e.g. tab-visibility
    // race where visibilitychange reschedules before the React stopWatchdog effect fires).
    if (!this.active && !this.streamId) return;

    // PLEX-6111: distinguish a backgrounded/throttled tab (pings kept arriving server-side,
    // resetWatchdog just never ran because the tab was hidden) from a genuinely dead
    // connection, without having to reconstruct it after the fact from separate breadcrumbs.
    const visibilityState = typeof document !== "undefined" ? document.visibilityState : "unknown";
    // console.debug, not .warn (see PLEX-6334 / WEBAPP-DJ): this embeds
    // streamId per call, so Sentry's captureConsoleIntegration can't group
    // occurrences and spawns a fresh noise issue per firing (WEBAPP-9Z/AB/AC).
    console.debug(
      `[LLMClient] Watchdog firing: streamId=${this.streamId} active=${this.active} ` +
      `openSubAgents=${Array.from(this.subAgentTurns.keys()).filter(id => isObservableArray(this.subAgentTurns.get(id))).length} ` +
      `documentVisibility=${visibilityState}`
    );

    // Check what the agent was doing when it timed out to provide context
    const lastTurn = this.turns.length > 0 ? this.turns[this.turns.length - 1] : null;
    let context = "";
    let operation = "unknown operation";

    if (lastTurn?.role === ROLE_USER) {
      // Check for tool use to identify the operation
      const toolUse = lastTurn.content?.find(b => b.toolResult);
      if (toolUse?.toolResult) {
        const toolUseId = toolUse.toolResult.toolUseId;
        // Try to find the original tool use from a previous turn
        for (let i = this.turns.length - 2; i >= 0; i--) {
          const turn = this.turns[i];
          const foundToolUse = turn.content?.find(b => b.toolUse?.toolUseId === toolUseId);
          if (foundToolUse) {
            operation = foundToolUse.toolUse.name || "tool execution";
            break;
          }
        }
      }
    } else if (lastTurn?.role === ROLE_ASSISTANT) {
      // Check if assistant was invoking a tool
      const toolUse = lastTurn.content?.find(b => b.toolUse);
      if (toolUse) {
        operation = toolUse.toolUse.name || "tool execution";
      }
    } else if (lastTurn?.role === ROLE_SYSTEM) {
      const systemMsg = lastTurn.content?.[0]?.text;
      if (systemMsg) {
        operation = systemMsg.toLowerCase().includes("search") ? "search" : "processing";
      }
    }

    context = ` during ${operation}`;

    this._handleError(
      `Watchdog timeout: No streaming events received for 180 seconds${context}.\n\n` +
      `This usually indicates:\n\n` +
      `- The server is processing a very complex query\n` +
      `- There may be an issue with the data being requested\n` +
      `- Network connectivity problems\n\n` +
      `Try simplifying your query or trying again.`,
      { includeEventsSnapshot: true, extraTags: { documentVisibility: visibilityState } }
    );
  }

  _handleError = (error, { includeEventsSnapshot = false, extraTags = {} } = {}) => {
    // PLEX-6303: the backend refused this dispatch because the conversation already has a
    // turn running (rd #244). That is not an error to show: the turn is still running.
    if (isLiveTurnConflict(error)) {
      this.markTurnRunning();
      return;
    }
    const timestamp = new Date().toISOString();
    const errorMessage = typeof error === 'string' ? error : error?.message || String(error);

    // Categorize error type for better fingerprinting and grouping
    let errorType = "generic";
    let fingerprint = ["llm-error"];
    let entityId = null;
    let entityType = null;

    // Check for entity-related errors (SMILES, ChEMBL, etc.)
    // Patterns: "Error: 'smiles:...'" or "KeyError: 'chembl:...'" or bare entity IDs
    const entityErrorMatch = errorMessage.match(/(?:Error|KeyError):\s*'?([a-z]+:[^'\s,)]+)'?/) ||
                             errorMessage.match(/\b(smiles|chembl|unichem|pubchem|uniprot|ensembl):[^\s'"`,)]+/i);

    // Data-inconsistency takes precedence over entity-error when the
    // message says "graph does not match" / "Not all IDs found" — same fix
    // as _handleStreamError. Entity exists in search results but not in
    // graph DB; user needs "refresh / use different compound", not
    // "entity may not be in DB / try alternative identifier".
    if (errorMessage.includes("graph does not match") || errorMessage.includes("Not all IDs found")) {
      errorType = "data-inconsistency";
      fingerprint = ["llm-error", "data-inconsistency"];
    } else if (entityErrorMatch) {
      errorType = "entity-error";
      fingerprint = ["llm-error", "entity-error"];
      entityId = entityErrorMatch[1] || entityErrorMatch[0];

      // Determine entity type from ID prefix
      if (entityId.startsWith("smiles:")) {
        entityType = "compound";
        fingerprint.push("compound");
      } else if (entityId.startsWith("chembl:") || entityId.startsWith("unichem:") || entityId.startsWith("pubchem:")) {
        entityType = "compound";
        fingerprint.push("compound");
      } else if (entityId.startsWith("uniprot:") || entityId.startsWith("ensembl:")) {
        entityType = "target";
        fingerprint.push("target");
      } else {
        fingerprint.push("unknown-entity-type");
      }
    } else if (errorMessage.includes("timeout")) {
      errorType = "timeout";
      fingerprint.push("timeout");
    } else if (errorMessage.includes("attachment") || errorMessage.includes("attached file")) {
      errorType = "attachment";
      fingerprint.push("attachment-error");
    } else if (errorMessage.includes("duplicate document names")) {
      errorType = "duplicate-documents";
      fingerprint.push("duplicate-document-names");
    } else if (errorMessage.includes("ValidationException")) {
      errorType = "validation";
      fingerprint.push("bedrock-validation");
    } else if (error?.status === 400 || errorMessage.includes("Invalid API call")) {
      errorType = "bad-request";
      fingerprint = ["llm-error", "bad-request"];
    } else if (error?.name === "NotAuthorized" || errorMessage.includes("Token refresh failed") ||
               errorMessage.includes("Token refresh timed out") ||
               errorMessage.includes("same token returned") || errorMessage.includes("Max auth retries exceeded") ||
               errorMessage.includes("Already failed once with this token")) {
      errorType = "session-expired";
      fingerprint = ["llm-error", "session-expired"];
    } else {
      fingerprint.push("unknown");
    }

    const transientTypes = new Set(["generic", "timeout"]);
    const level = transientTypes.has(errorType) && !this.active ? "warning" : "error";

    const sentryOpts = {
      level,
      asException: true,
      errorName: errorMessage.substring(0, 120),
      captureStackTrace: false,
      fingerprint,
      tags: {
        errorType,
        streamId: this.streamId,
        agentId: this.agentId,
        active: this.active,
        sessionAgent: this.sessionAgent,
        ...(entityId && { entityId }),
        ...(entityType && { entityType }),
        ...extraTags
      },
      ...this._buildConversationCapture({ error, timestamp }),
    };

    if (includeEventsSnapshot && this.streamId) {
      // Capture server-side SSE event queue as extra context on this Sentry event.
      // Must actually WAIT for the snapshot response before nulling streamId: nulling
      // streamId synchronously triggers _cancelAgents (below), which POSTs a cancel
      // that the backend processes almost immediately (channel_clear()/close() empty
      // the Redis queue this snapshot reads). fetch() is async and does not block the
      // next line, so nulling it right after merely *starting* the fetch let the cancel
      // race ahead of the snapshot on every real occurrence — the snapshot GET landed
      // server-side after the queue was already torn down, making `snapshot.events`
      // near-empty on every past capture (confirmed via WEBAPP-3S/PLEX-6310: server
      // event_count and client clientEvents both showed just 1 stale entry). Holding
      // the null/cancel until the snapshot settles fixes that without changing when
      // the user-visible error/cancel happens by more than one network round trip.
      const clientEvents = this._clientEventLog.slice();
      // PLEX-6310: read-loop health at fire time, from useStreamConnector — captured
      // synchronously (unlike snapshot/clientEvents, no fetch/race needed) so it
      // reflects the exact instant the watchdog fired. See recordTick's comment in
      // useStreamConnector.js: this exists specifically because a real incident left
      // both server-side snapshot and client console breadcrumbs uninformative, and
      // this is independent of both.
      const streamLoopHealth = this._getStreamLoopHealth?.() ?? null;
      const headers = this.auth?.jwtToken ? { Authorization: `Bearer ${this.auth.jwtToken}` } : {};
      // Bounded by a race against `wait`: a hung/slow snapshot endpoint must not delay
      // the user-visible cancel indefinitely. On timeout, snapshot is just omitted —
      // the cancel/error path still proceeds normally.
      Promise.race([
        fetch(`/api/stream/${this.streamId}/snapshot`, { headers })
          .then(r => r.ok ? r.json() : null)
          .catch(() => null),
        wait(3000).then(() => null),
      ])
        .then(snapshot => {
          captureMessage(errorMessage, {
            ...sentryOpts,
            extra: { ...sentryOpts.extra, snapshot: snapshot || null, clientEvents, streamLoopHealth },
          });
          this.streamId = null;
        });
    } else {
      captureMessage(errorMessage, sentryOpts);
      this.streamId = null;
    }

    // Create user-friendly error message
    let userMessage = errorMessage;
    if (errorType === "entity-error" && entityId) {
      const shortId = entityId.length > 80 ? entityId.substring(0, 77) + "..." : entityId;
      userMessage = `Unable to process ${entityType || "entity"}: ${shortId}\n\n` +
                    `This may indicate:\n\n` +
                    `- The ${entityType || "entity"} is not in the Plex database\n` +
                    `- The identifier may be malformed or invalid\n` +
                    `- There is no biological data available for this ${entityType || "entity"}\n\n` +
                    `Try:\n\n` +
                    (entityType === "compound"
                      ? `- Searching for a similar compound using structure search\n` +
                        `- Using a different identifier (ChEMBL ID, compound name)\n` +
                        `- Verifying the SMILES string is correctly formatted`
                      : `- Searching for a related ${entityType || "entity"}\n` +
                        `- Using an alternative identifier\n` +
                        `- Checking the identifier format`);
    } else if (errorType === "attachment") {
      userMessage = "There was an issue with an attached file. Please try re-attaching the file.";
    } else if (errorType === "duplicate-documents") {
      userMessage = "Cannot send duplicate documents. Please remove duplicate attachments.";
    } else if (errorType === "data-inconsistency") {
      userMessage = "Data inconsistency detected in the search results.\n\n" +
                    "This is a known issue that occurs when:\n\n" +
                    "- A compound exists in search results but not in the graph database\n" +
                    "- There is a temporary sync issue between data sources\n\n" +
                    "The backend team has been notified. Please try:\n\n" +
                    "- Refreshing your search\n" +
                    "- Using a different compound from the results";
    } else if (errorType === "bad-request") {
      userMessage = "An error occurred. Please try again.";
    } else if (errorType === "session-expired") {
      userMessage = "Your session has expired. Please sign in again.";
      if (this._userStore) {
        this._userStore.tokenExpired = true;
      }
    }

    this._pushTurn({
                      role: ROLE_SYSTEM,
                      content: [{text: `[${timestamp}] ${userMessage}`}],
                      status: "error",
                      stopReason: "error",
                      error,
                      ts: Date.now()});
  }

  _tagMissingAttachment = (turns, errorMessage) => {
    // Check if this is an attachment-related error and tag the problematic attachments
    const isNotFoundError = errorMessage.includes("Attachment") && errorMessage.includes("not found");
    const isAccessDeniedError = errorMessage.includes("Access denied to attachment");

    if (isNotFoundError || isAccessDeniedError) {
      // Extract key/filename from error messages like:
      // "Attachment 'filename.pdf' not found"
      // "Access denied to attachment at key `...': The attachment may not exist..."
      let identifier = null;
      let errorType = null;
      let userMessage = null;

      if (isAccessDeniedError) {
        // Extract key from "Access denied to attachment at key `<key>`"
        const keyMatch = errorMessage.match(/at key [`']([^`']+)[`']/);
        identifier = keyMatch ? keyMatch[1] : null;
        errorType = "access_denied";
        userMessage = "Error reading attachment, please re-attach";
      } else {
        // Extract filename from "Attachment 'filename.pdf' not found"
        const filenameMatch = errorMessage.match(/Attachment '([^']+)'/);
        identifier = filenameMatch ? filenameMatch[1] : null;
        errorType = "not_found";
        userMessage = "Attachment not found, please re-attach";
      }

      if (identifier) {
        // Find and tag the attachment in previous turns
        for (let i = turns.length - 1; i >= 0; i--) {
          const turn = turns[i];
          if (turn.role === ROLE_USER && turn.content) {
            let found = false;
            turn.content.forEach(block => {
              const attachmentBlock = block.document || block.image;
              if (attachmentBlock?.source?.file) {
                const file = attachmentBlock.source.file;
                // Match by name or uploadId/key
                if (file.name === identifier || file.uploadId === identifier) {
                  attachmentBlock.error = errorType;
                  attachmentBlock.errorMessage = userMessage;
                  found = true;
                }
              }
            });
            if (found) break; // Stop after finding the attachment
          }
        }
      }
    }
  }

  // Sentry's default data scrubber ("@password:filter" etc.) scans string VALUES
  // for key:value-shaped substrings, not just structured object keys. Once the
  // snapshot below is JSON.stringify'd to a single string (needed to survive
  // normalizeDepth), one matching substring anywhere in the ~30-turn window
  // causes Sentry to replace the ENTIRE string with "[Filtered]" — silently
  // destroying the forensic snapshot this capture exists to provide (WEBAPP-B2).
  // Pre-redact known-sensitive keys ourselves so nothing in the surviving JSON
  // can trigger that blanket filter. This must also scrub leaf string VALUES
  // (e.g. LLM message/tool-call text that echoes "Authorization: Bearer ...")
  // since Sentry's scrubber matches those regardless of the surrounding key —
  // redacting only object keys (as the original WEBAPP-B2 fix did) still leaves
  // that path open and the whole snapshot still collapses to "[Filtered]".
  _SENSITIVE_KEY_RE = /password|secret|token|api[-_]?key|authorization|credential/i;
  _SENSITIVE_VALUE_RE = /(password|secret|token|api[-_]?key|authorization|credential)(\s*[:=]\s*)(?:bearer\s+)?\S+/gi;

  _redactSensitiveKeys = (value) => {
    if (Array.isArray(value)) {
      return value.map(this._redactSensitiveKeys);
    }
    if (value && typeof value === "object") {
      const out = {};
      for (const [key, val] of Object.entries(value)) {
        out[key] = this._SENSITIVE_KEY_RE.test(key) ? "[redacted]" : this._redactSensitiveKeys(val);
      }
      return out;
    }
    if (typeof value === "string") {
      return value.replace(this._SENSITIVE_VALUE_RE, "$1$2[redacted]");
    }
    return value;
  }

  _serializeEvent = (event) => {
    // Serialize SSE event to avoid "[object Object]" in Sentry logs
    const data = (event.data ? safeParseJSON(event.data) : null) || {};
    return {
      type: event.type,
      lastEventId: event.lastEventId,
      data: data,
      timestamp: event.timeStamp
    };
  }

  _captureStreamAnomaly = (message, sentryOpts) => {
    // Fetch the backend snapshot first, then emit a single Sentry event with both
    // the anomaly details and the server-side event queue for forensic correlation.
    // clientEvents (from _clientEventLog) lets us diff what the client received vs
    // what the server published.
    const clientEvents = this._clientEventLog.slice();
    if (!this.streamId) {
      captureMessage(message, { ...sentryOpts, extra: { ...sentryOpts?.extra, clientEvents } });
      return;
    }
    const headers = this.auth?.jwtToken ? { Authorization: `Bearer ${this.auth.jwtToken}` } : {};
    // Named (not inline-anonymous) so Sentry stack traces show a real function
    // name here instead of "<anonymous>" for captureMessage calls raised from
    // this callback (see WEBAPP-8R / WEBAPP-8X).
    const captureWithSnapshot = snapshot => {
      // Keep only events around the anomaly: anchor on the last message_start in the
      // server queue (the errant event), then retain ±2 full turns (~30 events each
      // side). Events outside that window are replaced with stubs so Sentry's payload
      // limit is respected while index context is preserved.
      let snapshotExtra = snapshot || null;
      if (snapshot?.events?.length > 60) {
        const events = snapshot.events;
        // Find the last message_start in the queue as the anchor point
        let center = events.length - 1;
        for (let i = events.length - 1; i >= 0; i--) {
          if (events[i]?.type === "message_start") { center = i; break; }
        }
        const WINDOW = 30;
        const start = Math.max(0, center - WINDOW);
        const end = Math.min(events.length, center + WINDOW + 1);
        const trimmed = [
          ...(start > 0 ? [{ _stub: true, skipped: start }] : []),
          ...events.slice(start, end),
          ...(end < events.length ? [{ _stub: true, skipped: events.length - end }] : []),
        ];
        snapshotExtra = { ...snapshot, events: trimmed, truncated: true };
      }
      // Serialize snapshot to a JSON string: Sentry's normalizeDepth=3 would
      // otherwise flatten nested event objects (content_block, delta, etc.) to
      // "[Object]", making the snapshot unreadable. A string bypasses that limit.
      const snapshotStr = snapshotExtra ? JSON.stringify(this._redactSensitiveKeys(snapshotExtra)) : null;
      captureMessage(message, {
        ...sentryOpts,
        extra: { ...sentryOpts?.extra, snapshot: snapshotStr, clientEvents },
      });
    };
    fetch(`/api/stream/${this.streamId}/snapshot`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(captureWithSnapshot)
      .catch(() => captureMessage(message, { ...sentryOpts, extra: { ...sentryOpts?.extra, clientEvents } }));
  }

  _handleStreamError = (turns, index, event, messageText = null) => {
    // Transient SSE drop where useSSEListener will retry: do NOT tear
    // down the stream. The backend's 30s grace window (flask_sse.py:27)
    // keeps the agent alive, ResumableEventSource.reconnect() reissues
    // the GET with Last-Event-ID, and the queue replay (flask_sse.py:371)
    // delivers any events we missed. Nulling streamId here would
    // synchronously cancel the turn (DELETE /api/stream/<id>) via the streamId
    // setter's cancelAgents branch — defeating the whole resume design.
    // Only escalate to a fatal error when retries are exhausted (willRetry
    // false on the final error event) or this is a non-transport error.
    if (event?.willRetry) {
      return null;
    }
    // console.debug, not .warn: Sentry's captureConsoleIntegration captures
    // "warn"/"error"/"assert" levels and turns each call into its own Sentry
    // issue. This diagnostic line embeds index/mainAgentId, so every call
    // produced near-unique text Sentry couldn't group — spawning a fresh
    // noise issue per stream error (e.g. WEBAPP-DJ) alongside the real,
    // properly-classified captureMessage() call a few lines below.
    console.debug(
      `[LLMClient] _handleStreamError: index=${index} messageText=${JSON.stringify((messageText || "").slice(0, 150))} ` +
      `eventType=${event?.type} eventAgentId=${event?.data ? (() => { try { return JSON.parse(event.data)?.agent_id; } catch { return undefined; } })() : undefined} mainAgentId=${this.agentId}`
    );
    // PLEX-6303: decide first whether the turn goes on (see the turnGoesOn comment below).
    // A turn the server is still running keeps what it has streamed so far: it is not closed
    // or stamped "interrupted" here (Tim, plex-search-ui #106), and it stays partial, so a
    // reload asks the backend about it (mayBeMidTurn) instead of showing it as finished.
    const data = (event.data ? safeParseJSON(event.data) : null) || {};
    const serverErrorMessage = data.error || data.content?.map(b => b.text || "").join("") || null;
    const connectionState = event.connectionState;
    const transportError = !!(connectionState || event?.isTimeout || event?.reason);
    const turnGoesOn = this.active && !serverErrorMessage
      && (!transportError || event?.turnMayBeRunning === true);

    // Capture partial state for forensics BEFORE cleaning it up
    const lastTurn = turns[index];
    const forensicData = {
      index,
      event: this._serializeEvent(event),
      partialState: null
    };

    if (lastTurn?.partial) {
      // Save the partial turn state for debugging
      forensicData.partialState = {
        role: lastTurn.role,
        partial: true,
        contentBlocks: lastTurn.content.map((block, i) => ({
          index: i,
          partial: !!block.partial,
          type: block.text ? 'text' : block.toolUse ? 'toolUse' : block.toolResult ? 'toolResult' : 'unknown',
          preview: block.text?.substring(0, 100) || JSON.stringify(block).substring(0, 100)
        }))
      };

      if (!turnGoesOn) {
        // Clean up the partial flags so conversation can continue
        delete lastTurn.partial;
        lastTurn.content.forEach(block => {
          if (block.partial) {
            delete block.partial;
          }
        });

        // Append a truncation notice to the last text block so the user knows
        // the message was cut short (covers network drops and server exceptions).
        // This is visible to all users; the separate error turn below carries
        // full detail for admins only.
        const lastTextBlock = [...lastTurn.content].reverse().find(b => b.text != null);
        if (lastTextBlock) {
          lastTextBlock.text += "\n\n*(Response was interrupted)*";
        } else {
          lastTurn.content.push({ text: "*(Response was interrupted)*" });
        }
      }
    }

    // terminate streaming and register the error
    const timestamp = new Date().toISOString();
    const eventType = data.type || event.type;
    if (!messageText) {
      messageText = `Improper agent message ${data.agent_id}:${eventType} for '${turns[index]?.role ?? 'unknown'}' at message index ${index}`;
    }

    // Create better Sentry title and fingerprint based on error type
    let sentryTitle = messageText;
    let sentryFingerprint = ["llm-stream-error", eventType || "unknown"];
    let errorCategory = "unknown";
    let entityId = null;
    let entityType = null;

    // Check for entity-related errors in server message or event data
    const entityErrorMatch = (serverErrorMessage || messageText).match(/(?:Error|KeyError):\s*'?([a-z]+:[^'\s,)]+)'?/) ||
                             (serverErrorMessage || messageText).match(/No valid nodes within \[?'?([a-z]+:[^'\s,\])]+)/) ||
                             (serverErrorMessage || messageText).match(/\b(smiles|chembl|unichem|pubchem|uniprot|ensembl):[^\s'"`,)]+/i);

    // Data-inconsistency is a SUPERSET of entity-error when the message
    // explicitly says "graph does not match" / "Not all IDs found": the
    // entity DOES exist (user saw it in search results) but the graph DB
    // is out of sync. Check it FIRST so the user gets "refresh / use a
    // different compound from results" instead of the entity-error
    // guidance ("entity may not be in DB / try alternative identifier"),
    // which is a dead end for the actual cause.
    if ((serverErrorMessage || messageText).includes("graph does not match") ||
        (serverErrorMessage || messageText).includes("Not all IDs found")) {
      errorCategory = "data-inconsistency";
      sentryTitle = "LLM streaming: Data inconsistency error";
      sentryFingerprint = ["llm-stream-error", "data-inconsistency"];

      // Try to extract entity ID from "Not all IDs found for compound X/Y, graph does not match ES (['smiles:...'])"
      const graphMismatchMatch = (serverErrorMessage || messageText).match(/\(\['([^']+)'\]\)/);
      if (graphMismatchMatch) {
        entityId = graphMismatchMatch[1];
        entityType = "compound";
      }
    } else if (entityErrorMatch) {
      errorCategory = "entity-error";
      entityId = entityErrorMatch[1] || entityErrorMatch[0];
      sentryTitle = `LLM streaming: Entity processing error (${entityId.substring(0, 50)}...)`;
      sentryFingerprint = ["llm-stream-error", "entity-error"];

      // Determine entity type
      if (entityId.startsWith("smiles:") || entityId.startsWith("chembl:") ||
          entityId.startsWith("unichem:") || entityId.startsWith("pubchem:")) {
        entityType = "compound";
        sentryFingerprint.push("compound");
      } else if (entityId.startsWith("uniprot:") || entityId.startsWith("ensembl:")) {
        entityType = "target";
        sentryFingerprint.push("target");
      }
    } else if (event.isTimeout) {
      // Client-side timeout - no data received for extended period
      errorCategory = "client-timeout";
      sentryTitle = "LLM streaming: Client timeout waiting for data (SSE)";
      sentryFingerprint = ["llm-stream-error", "client-timeout", connectionState];
    } else if (connectionState === "CONNECTING") {
      // Connection failed before establishment - likely server error (500, timeout, etc)
      errorCategory = "connection-failed";
      sentryTitle = "LLM streaming: Connection failed to establish (SSE)";
      sentryFingerprint = ["llm-stream-error", "connection-failed", connectionState];

      // Try to detect if this is a server error vs network error
      if (event.connectionDuration && event.connectionDuration < 5000) {
        // Fast failure likely means server rejected (500, 503, etc)
        sentryTitle = "LLM streaming: Server error during connection (HTTP 500/503)";
        errorCategory = "server-error";
        sentryFingerprint = ["llm-stream-error", "server-error", connectionState];
      }
    } else if (connectionState === "OPEN") {
      // Connection was open but then failed - likely network interruption or server crash
      errorCategory = "connection-interrupted";
      sentryTitle = "LLM streaming: Connection interrupted mid-stream (SSE)";
      sentryFingerprint = ["llm-stream-error", "connection-interrupted", connectionState];
    } else if (connectionState === "CLOSED") {
      // Connection was already closed
      errorCategory = "connection-closed";
      sentryTitle = "LLM streaming: Connection already closed (SSE)";
      sentryFingerprint = ["llm-stream-error", "connection-closed", connectionState];
    } else if (messageText.includes("Improper agent message")) {
      // Protocol error - malformed SSE message
      errorCategory = "protocol-error";
      sentryTitle = "LLM streaming: Malformed SSE message from server";
      sentryFingerprint = ["llm-stream-error", "protocol-error", eventType || "unknown"];
    } else if (messageText.includes("Unexpected event")) {
      // Unexpected event type
      errorCategory = "unexpected-event";
      sentryTitle = "LLM streaming: Unexpected event type from server";
      sentryFingerprint = ["llm-stream-error", "unexpected-event", eventType || "unknown"];
    } else if (messageText.includes("attachment") || messageText.includes("attached file")) {
      // Attachment-related error
      errorCategory = "attachment-error";
      sentryTitle = "LLM streaming: Attachment error";
      sentryFingerprint = ["llm-stream-error", "attachment-error"];
    } else if (messageText.includes("duplicate document names")) {
      // Duplicate document names (should be prevented by frontend now)
      errorCategory = "duplicate-documents";
      sentryTitle = "LLM streaming: Duplicate document names";
      sentryFingerprint = ["llm-stream-error", "duplicate-documents"];
    }

    // Build comprehensive error context message
    let contextMessage = messageText;
    let userMessage = messageText; // User-friendly version of the error

    // Create user-friendly message for entity errors
    if (errorCategory === "entity-error" && entityId) {
      const shortId = entityId.length > 80 ? entityId.substring(0, 77) + "..." : entityId;
      userMessage = `Unable to process ${entityType || "entity"}: ${shortId}\n\n` +
                    `This may indicate:\n\n` +
                    `- The ${entityType || "entity"} is not in the Plex database\n` +
                    `- The identifier may be malformed or invalid\n` +
                    `- There is no biological data available for this ${entityType || "entity"}\n\n` +
                    `Try:\n\n` +
                    (entityType === "compound"
                      ? `- Searching for a similar compound using structure search\n` +
                        `- Using a different identifier (ChEMBL ID, compound name)\n` +
                        `- Verifying the SMILES string is correctly formatted`
                      : `- Searching for a related ${entityType || "entity"}\n` +
                        `- Using an alternative identifier\n` +
                        `- Checking the identifier format`);
      contextMessage = `${messageText}\nServer message: ${serverErrorMessage || "entity error"}`;
    } else if (errorCategory === "data-inconsistency") {
      userMessage = "Data inconsistency detected in the search results.\n\n" +
                    "This is a known issue that occurs when:\n\n" +
                    "- A compound exists in search results but not in the graph database\n" +
                    "- There is a temporary sync issue between data sources\n\n" +
                    "The backend team has been notified. Please try:\n\n" +
                    "- Refreshing your search\n" +
                    "- Using a different compound from the results";
      if (entityId) {
        userMessage += `\n\nProblematic entity: ${entityId}`;
      }
      contextMessage = `${messageText}\nServer message: ${serverErrorMessage || "data inconsistency"}`;
    } else if (serverErrorMessage && errorCategory !== "entity-error" && errorCategory !== "data-inconsistency") {
      errorCategory = "backend-error";
      contextMessage = `${messageText}\nServer message: ${serverErrorMessage}`;
      sentryTitle = `LLM streaming: ${serverErrorMessage.substring(0, 100)}`;
      userMessage = serverErrorMessage; // Show server's error message to user

      // Do NOT set a custom sentryFingerprint here. This fallback covers
      // arbitrary, uncategorized backend error messages (see WEBAPP-7Y:
      // "Connection lost", entity-lookup failures, and unrelated Python
      // exceptions all merged into one Sentry issue under the static
      // ["llm-stream-error", eventType] fingerprint, since eventType is
      // almost always "error"). Clear sentryFingerprint (set to that static
      // default at declaration above) so Sentry falls back to its default
      // message-based grouping, which splits by the actual error text
      // instead of lumping every uncategorized backend error together.
      sentryFingerprint = undefined;
    } else if (connectionState === "CONNECTING" && !serverErrorMessage) {
      contextMessage = `${messageText}\nNote: Server returned error without descriptive message. Backend should provide error details in SSE event data.`;
      // Create user-friendly message based on error category
      if (errorCategory === "server-error") {
        userMessage = "Server error (HTTP 500/503). Please try again.";
      } else if (errorCategory === "connection-failed") {
        userMessage = "Connection failed. Please check your network and try again.";
      } else if (errorCategory === "client-timeout") {
        const minutes = Math.round((event.timeSinceLastData || 0) / 60000);
        userMessage = `Connection timeout after ${minutes} minute${minutes !== 1 ? 's' : ''} with no response. The server may be experiencing issues. Please try again.`;
      }
    } else if (event.isTimeout) {
      // Handle timeout in other connection states
      const minutes = Math.round((event.timeSinceLastData || 0) / 60000);
      contextMessage = `${messageText}\nConnection timed out after ${minutes} minutes with no data received.`;
      userMessage = `Connection timeout after ${minutes} minute${minutes !== 1 ? 's' : ''} with no response. Please try again.`;
    }

    const conversationCapture = this._buildConversationCapture({ forensicData, timestamp });
    captureMessage(sentryTitle, {
      level: (errorCategory === "connection-closed" || errorCategory === "connection-interrupted") && !this.active ? "warning" : "error",
      asException: true,
      errorName: sentryTitle.substring(0, 120),
      captureStackTrace: false,
      fingerprint: sentryFingerprint,
      tags: {
        eventType,
        agentId: data.agent_id,
        mainAgentId: this.agentId,
        streamId: this.streamId,
        active: this.active,
        sessionAgent: this.sessionAgent,
        connectionState,
        errorCategory,
        hasServerMessage: !!serverErrorMessage,
        connectionDuration: event.connectionDuration,
        isTimeout: !!event.isTimeout,
        timeSinceLastData: event.timeSinceLastData,
        ...(event.status != null && { status: event.status }),
        ...(entityId && { entityId }),
        ...(entityType && { entityType })
      },
      context: {
        error_details: {
          originalMessage: messageText,
          serverMessage: serverErrorMessage,
          fullContext: contextMessage,
          connectionState,
          eventType,
          url: event.url,
          connectionDuration: event.connectionDuration,
          status: event.status,
          ...(entityId && { entityId }),
          ...(entityType && { entityType })
        },
        // PLEX-6110/WEBAPP-7G: only present on the duplicate-message_start
        // ("protocol-error") branch — raw SSE event history + this agent's
        // own turn tail, to pin down whether an intervening toolResult
        // event was dropped/misrouted during a burst-catchup.
        ...(event._duplicateStartDebug && { duplicate_start_debug: event._duplicateStartDebug }),
        ...conversationCapture.context,
      },
      attachments: conversationCapture.attachments || [],
    });
    // PLEX-6303: an error in a sub-agent's events stays with that sub-agent. Its partial
    // turn is closed above; the root stream, and the turn the server is still running,
    // carry on. Sub-agent turns are only displayed: they are never sent back to the model.
    if (turns !== this.turns) {
      return null;
    }
    // PLEX-6303 (Tim, 2026-10-06): an error the client detects never cancels the server's
    // turn. Clearing `active` first keeps the streamId setter from sending its DELETE
    // (the idiom end_turn and dispose() use).
    //
    // When the turn is known to go on, say so instead of adding error turns for an answer
    // that may yet come: markTurnRunning shows "still being worked on" and hides Resume,
    // and the running-turn watch reloads the conversation once the turn ends. That is
    // a protocol error (the client misread the stream; the server knows nothing of it),
    // or a lost connection the backend has just said is still running
    // (event.turnMayBeRunning, set by useLLMClient after its reconnects). Errors the
    // server reported, and connection errors nobody checked, keep their error turns.
    this.active = false;
    this.streamId = null;
    if (turnGoesOn) {
      this.markTurnRunning();
      return null;
    }

    // Tag any missing attachments mentioned in the error
    this._tagMissingAttachment(turns, messageText);

    const systemError = {
      role: ROLE_SYSTEM,
      status: "error",
      stopReason: "error",
      content: [{text: `[${timestamp}] ${userMessage}`}],
      ts: Date.now(),
      details: forensicData
    };

    // Check if we need to add an ASSISTANT turn to maintain alternation
    // Look at the last non-SYSTEM turn
    const lastNonSystemTurn = turns.slice().reverse().find(t => t != null && t.role !== ROLE_SYSTEM);
    const isAdmin = this._userStore?.isAdmin || false;

    // If the last assistant turn has unpaired toolUse blocks, inject a toolResult turn
    // so the conversation stays structurally valid for resume. Without this, the frontend
    // stores a role=system error turn that the backend has to strip, but the toolUse
    // remains unpaired and causes Bedrock ValidationException on the next request.
    const unpairedToolUseIds = (lastNonSystemTurn?.role === ROLE_ASSISTANT)
      ? (lastNonSystemTurn.content || [])
          .filter(b => b.toolUse)
          .map(b => b.toolUse.toolUseId)
          .filter(id => {
            // Check if there's already a toolResult for this id in any following turn
            const idx = turns.indexOf(lastNonSystemTurn);
            return !turns.slice(idx + 1).some(t =>
              (t.content || []).some(b => b.toolResult?.toolUseId === id)
            );
          })
      : [];

    // Route every synthetic push through _mirrorPushToTreeIfMain so the
    // branching tree stays in sync when these recovery turns land in
    // this._turns. Without the mirror, a stream error in the middle of a
    // conversation makes turns.length > tree.path.length, the loader's
    // mismatch fallback fires on the next reload, and any sibling branches
    // the user had created upstream are silently discarded.
    const pushAndMirror = (t) => {
      turns.push(t);
      this._mirrorPushToTreeIfMain(turns, t);
    };
    if (unpairedToolUseIds.length > 0) {
      // Inject a user turn with error toolResults covering every unpaired toolUse
      pushAndMirror({
        role: ROLE_USER,
        content: unpairedToolUseIds.map(id => ({
          toolResult: {
            toolUseId: id,
            content: [{text: `Tool call failed: ${userMessage}`}],
            status: "error"
          }
        })),
        stopReason: "error",
        status: "error",
        ts: Date.now()
      });
      // Follow with an assistant error acknowledgment to keep alternation intact
      pushAndMirror({
        role: ROLE_ASSISTANT,
        content: [{ text: "I'm sorry, I ran into an error and wasn't able to complete that request. Please try again." }],
        stopReason: "error",
        status: "error",
        ts: Date.now()
      });
      if (isAdmin) {
        // Append system error for admin technical detail (doesn't affect API turn structure)
        pushAndMirror(systemError);
      }
      return isAdmin ? systemError : null;
    } else if (lastNonSystemTurn?.role === ROLE_USER) {
      // Last message was USER, so next should be ASSISTANT
      // For non-admins: Only add ASSISTANT error acknowledgment (skip SYSTEM error as it's redundant)
      // For admins: Add both SYSTEM error (for technical details) and ASSISTANT acknowledgment
      if (isAdmin) {
        pushAndMirror(systemError);
      }
      pushAndMirror({
        role: ROLE_ASSISTANT,
        content: [{ text: "I'm sorry, I ran into an error and wasn't able to complete that request. The AI service may be temporarily unavailable. Please try again in a moment." }],
        stopReason: "error",
        status: "error",
        ts: Date.now()
      });
      // For non-admins, return null to prevent duplicate error messages at call sites
      // For admins, return systemError so they see technical details
      return isAdmin ? systemError : null;
    } else {
      // Last message was ASSISTANT (or no messages), just add SYSTEM error
      pushAndMirror(systemError);
      return systemError;
    }
  }

  // PLEX-4099 §8 — the turn a coach file block hangs on. File events fire
  // during the coach's tool phase, so the current turn is whatever the engine
  // is building (assistant tool-use turn, or the user toolResult turn). We
  // attach to the most-recent turn and let ConversationView render each turn's
  // file blocks in place — no chain collection needed, natural stream order.
  _fileBlockTargetTurn = () => (this._turns.length ? this._turns[this._turns.length - 1] : null);

  /**
   * Append a `file_block` payload to the current turn's `fileBlocks` array.
   * De-duped by block_id so an SSE resume/replay after reload (the buffer is
   * re-delivered on reconnect) doesn't double-render. No-op when there's no
   * turn yet (never happens on a live coach stream — a message_start always
   * precedes the tool phase).
   */
  _attachFileBlock = (eventData) => {
    const turn = this._fileBlockTargetTurn();
    if (!turn) return;
    if (!Array.isArray(turn.fileBlocks)) turn.fileBlocks = [];
    const block = {
      block_id: eventData.block_id || null,
      file_id: eventData.file_id || null,
      render_hint: eventData.render_hint || "auto",
      caption: eventData.caption || null,
      content: typeof eventData.content === "string" ? eventData.content : "",
      content_encoding: eventData.content_encoding || "utf-8",
    };
    if (block.block_id && turn.fileBlocks.some((b) => b.block_id === block.block_id)) return;
    turn.fileBlocks.push(block);
  };

  /**
   * Append a `file_suggestion` payload to the current turn's `fileSuggestions`
   * array. De-duped by (prompt + file ids) since the payload carries no id.
   */
  _attachFileSuggestion = (eventData) => {
    const turn = this._fileBlockTargetTurn();
    if (!turn) return;
    if (!Array.isArray(turn.fileSuggestions)) turn.fileSuggestions = [];
    const suggestions = Array.isArray(eventData.suggestions)
      ? eventData.suggestions
          .filter((s) => s && s.file_id)
          .map((s) => ({ file_id: s.file_id, reason: s.reason || "" }))
      : [];
    const suggestion = { prompt: eventData.prompt || "", suggestions };
    const key = `${suggestion.prompt}::${suggestions.map((s) => s.file_id).join(",")}`;
    const seen = turn.fileSuggestions.some(
      (s) => `${s.prompt}::${(s.suggestions || []).map((x) => x.file_id).join(",")}` === key,
    );
    if (seen) return;
    turn.fileSuggestions.push(suggestion);
  };

  /**
   * Single normalizer for `visualization` SSE payloads. Both entry paths —
   * handleSSE (explicit dispatch / tests) and handleStreamEvent (live stream) —
   * route here so the widget/sar/else branching AND the viz.rendered telemetry
   * can never drift apart (PLEX-4227). Scoped strictly to viz ingestion.
   */
  _ingestVisualization = (data) => {
    const { chart_id, kind, title } = data || {};
    if (!chart_id || !kind) return;
    runInAction(() => {
      if (kind === "widget") {
        this._visualizationStore.addChart(chart_id, {
          kind: "widget",
          title: title || "",
          spec: data.html,
          streaming: false,
          toolUseId: chart_id.split(":")[1], // reconciles pending entry
        });
      } else if (kind === "sar") {
        this._visualizationStore.addChart(chart_id, {
          kind: "sar",
          title: title || "",
          spec: data.spec,
          streaming: false,
        });
      } else {
        // plotly / network / molecule — the publish payload carries one of
        // {spec, graph, items} depending on kind; normalize to `spec`.
        const normalizedSpec = data.spec ?? data.graph ?? data.items ?? null;
        this._visualizationStore.addChart(chart_id, {
          kind,
          title: title || "",
          spec: normalizedSpec,
        });
      }
    });
    // Telemetry — viz.rendered (time-to-paint instrumentation deferred)
    try { Analytics.event("viz.rendered", { chart_id, kind, time_to_paint_ms: 0 }); } catch (e) {}
  }

  /**
   * Public entry point for SSE events by type (for testing and explicit dispatch).
   */
  handleSSE = (eventType, data) => {
    if (eventType === "visualization") {
      this._ingestVisualization(data);
    }
  }

  handleStreamEvent = (event) => {
    // Track the most-recently-seen SSE event id for the resilience layer.
    // useSSEListener reads this on transport-error reconnect to send
    // Last-Event-ID so the backend can replay the buffer from this cursor.
    if (event?.lastEventId) {
      this._lastEventId = event.lastEventId;
    }

    // NOTE: the streaming message types handled here correspond to the results
    // of bedrock's converse_stream API
    const eventData = (event.data ? safeParseJSON(event.data) : null) || {};
    const mtype = eventData?.type || event.type;
    const roleInEvent = 'role' in eventData;
    const {role = ROLE_SYSTEM, agent = this.sessionAgent, agent_id: agentId = this.agentId} = eventData;
    const stopReason = eventData.stop_reason || eventData.stopReason;
    _sseDebug(`handleStreamingResponse ${mtype}`, event);

    // PLEX-6261: a /sse stream opens with a frame naming its conversation (and turn). The
    // engine adopts the id when it has none yet, which is how it learns one the backend
    // minted. The frame is never content, so nothing else runs for it; before this, an
    // unknown event type became an error turn.
    if (mtype === "conversation_id") {
      if (eventData.conversation_id && !this.historyId) {
        runInAction(() => { this.historyId = eventData.conversation_id; });
      }
      return;
    }

    // PLEX-4224: resume agent-id ADOPTION. When a stream is resumed from a
    // cursor written BEFORE streamCursor tracked agentId (it round-trips ""),
    // attemptStreamResume can't seed the original main agent_id, so
    // `this._agentId` is an unrelated fresh uuid minted on this page load.
    // The backend replays the in-flight turn carrying its ORIGINAL agent_id;
    // without adopting it, every replayed main-agent event fails the
    // `agentId === this.agentId` routing check below and is mis-filed under a
    // phantom subAgentTurns slot that renders nowhere — so the returning
    // user sees a perpetual spinner instead of the resumed answer.
    //
    // Adopt from the FIRST replayed event that carries an agent_id — NOT only
    // message_start: when the cursor points mid-turn the main turn's
    // message_start was already persisted (and its partial turn rehydrated
    // from DynamoDB), so the replay resumes with content_block_delta events
    // that have no fresh message_start to key off. Adopting here, before the
    // routing below, makes this event and every following one land in
    // this.turns. The flag is one-shot (cleared on adopt, and by the streamId
    // setter on any fresh/ended stream), so a genuine sub-agent id emitted
    // later is never adopted as the main id.
    if (this._adoptAgentIdOnResume && agentId && agentId !== this.agentId) {
      this._agentId = agentId;
      this._adoptAgentIdOnResume = false;
    }

    if (mtype === "memory-reflect" || mtype === "memory-recall" || mtype === "memory-retain") {
      if (eventData.bank_id_prefix) memoryStore.setBankIdPrefix(eventData.bank_id_prefix);
      if (mtype === "memory-reflect") memoryStore.setReflect(eventData);
      else if (mtype === "memory-recall") memoryStore.addRecall(eventData);
      else memoryStore.addRetain(eventData);
      return;
    }

    if (mtype === "visualization") {
      // Shared normalizer — see _ingestVisualization (PLEX-4227). Keeps the
      // live-stream path and handleSSE byte-for-byte identical incl. telemetry.
      this._ingestVisualization(eventData);
      return;
    }

    if (mtype === "query-rewrite") {
      // Coach proposed reframing the user's query. Attach the proposal
      // to the most recent user turn so Conversation renders the
      // QueryRewriteCard in place of the bubble.
      runInAction(() => {
        for (let i = this.turns.length - 1; i >= 0; i--) {
          const t = this.turns[i];
          if (t?.role === ROLE_USER && !t.content?.some(b => b.toolResult)) {
            t.rewrite = {
              rewritten: eventData.rewritten || "",
              changes: eventData.changes || [],
            };
            break;
          }
        }
      });
      return;
    }

    if (mtype === "plan") {
      // Coach called set_plan with {steps, current_step}. Stamp the engine
      // so useCoachPlan picks it up immediately, before the toolUse block
      // is flushed to client.turns. On reload, useCoachPlan falls back to
      // scanning turns for the latest set_plan toolUse — see useCoachPlan.js.
      //
      // Only the main agent's plan drives the top checklist. A sub-agent
      // (delegation) must not hijack client.plan with its own steps — its
      // activity lives in subAgentTurns and renders inside its DelegateCard.
      if (agentId == null || agentId === this.agentId) {
        runInAction(() => {
          this.plan = {
            steps: Array.isArray(eventData.steps) ? eventData.steps : [],
            currentStep: Number.isInteger(eventData.current_step) ? eventData.current_step : 0,
          };
        });
      }
      return;
    }

    // PLEX-4099 §8 — coach file-interaction blocks. Both events are emitted
    // out-of-band on the coach stream (like `plan`), so handle them BEFORE the
    // bedrock content-block accumulator. Rather than a client-level singleton
    // (plan), file blocks live in the CURRENT turn's `fileBlocks` /
    // `fileSuggestions` arrays so they render in stream order inside the coach
    // turn and persist to history (rendered from the saved payload on replay).
    // Only the main agent's file blocks render — a sub-agent's activity lives
    // in its DelegateCard, mirroring the `plan` agent-id guard.
    if (mtype === "file_block") {
      if (agentId == null || agentId === this.agentId) {
        runInAction(() => { this._attachFileBlock(eventData); });
      }
      return;
    }
    if (mtype === "file_suggestion") {
      if (agentId == null || agentId === this.agentId) {
        runInAction(() => { this._attachFileSuggestion(eventData); });
      }
      return;
    }

    // Only log main-agent events: sub-agent events flood the 200-entry cap and
    // crowd out the main-agent context needed for anomaly diagnosis. Sub-agent
    // event history is available via the server-side SSE snapshot in Sentry.
    //
    // PLEX-6310: this used to stop appending once length reached 200 (first-N,
    // never rotated). A dense early burst of content_block_delta events (the
    // guide's first response, before it even dispatches to a sub-agent) fills
    // 200 slots in single-digit seconds on every real session, so by the time a
    // watchdog fires minutes later the log has been dark for the entire silent
    // stretch that actually matters — confirmed on whayes' own capture: 177
    // entries spanning 9s of an 8-minute session, nothing from message ~9s
    // onward. Rotate instead: keep the most recent 200 so a timeout capture
    // actually covers the moments leading up to it.
    if (agentId === this.agentId) {
      this._clientEventLog.push({mtype, role, agentId, ts: event.timeStamp});
      if (this._clientEventLog.length > 200) {
        this._clientEventLog.shift();
      }
    }

    if (!this.streamId && !this.active) {
      // Terminal events from sub-agents (message_stop, ping) arriving after the parent
      // stream closes are expected and harmless — swallow them silently.
      const TERMINAL_EVENTS = ["message_stop", "ping"];
      if (TERMINAL_EVENTS.includes(mtype)) {
        console.debug(`[LLMClient] Stale terminal event ignored: mtype=${mtype} agentId=${agentId}`);
        return;
      }
      // Don't cancel a deliberately-preserved agent: when the user navigates
      // away mid-stream we tear down locally (clear({preserveStream})/dispose)
      // but want the backend agent to keep running so resume can reattach.
      // A late in-flight event hitting this guard must NOT POST a cancel —
      // backend disconnect-grace handles genuine orphans. Without this, the
      // cancel killed the very turn we preserved ("AI service unavailable").
      if (this._preserveStreamActive) {
        console.debug(`[LLMClient] Stale event during preserved stream — closing source, NOT cancelling agent ${agentId}`);
        event.srcElement?.close();
        return;
      }
      // console.debug, not .warn (see PLEX-6334 / WEBAPP-DJ): embeds
      // agentId/streamId per call, so Sentry's captureConsoleIntegration
      // can't group occurrences and spawns a fresh noise issue each time.
      console.debug(`[LLMClient] Stale event guard fired: mtype=${mtype} agentId=${agentId} role=${role} streamId=${this.streamId} active=${this.active}`, event, eventData);
      if (!this._staleEventReported) {
        this._staleEventReported = true;
        Analytics.event(Analytics.EVENTS.CHAT_STALE_EVENT, { mtype, agentId, role });
      }
      // PLEX-6259: the orphan is the conversation's live turn; stop it with its
      // sub-agents. Without a conversation id there is nothing left to address it by.
      this._cancelTurn(null);
      return;
    }

    // Pre-populate agent map for any event arriving for a sub-agent before its
    // toolResult block initialises the entry (race condition in multi-agent streaming).
    // Wrapped in observable.array explicitly (the map itself is observable.shallow) —
    // only actively-streaming sub-agents need MobX tracking for UI reactivity.
    if (agentId && agentId !== this.agentId && !this.subAgentTurns.has(agentId)) {
      this.subAgentTurns.set(agentId, observable.array([]));
    }

    // PLEX-4224: route ANY falsy agent id (null / "" / absent) to the main
    // conversation, not just an exact `=== null`. A replayed event that
    // carries agent_id="" (older/edge backend serialization) would otherwise
    // fall through to `subAgentTurns.get("")` — undefined — and be dropped
    // via the "Turns missing" path below. An empty agent id means "the main
    // agent", so it belongs in this.turns.
    const turns = !agentId || agentId === this.agentId
                  ? this.turns
                  : this.subAgentTurns.get(agentId);
    if (!turns) {
      // Should be unreachable after the pre-population above; kept as a silent fallback.
      // Client-side dedup: at most one Sentry event per (streamId, agentId) pair,
      // preventing per-SSE-frame floods. Fixed fingerprint aggregates all occurrences
      // into a single Sentry issue; agent type and agentId are available as tags for
      // filtering. Static message (no interpolation) so the fingerprint isn't overridden.
      const missingKey = `${this.streamId}:${agentId}`;
      if (!this._reportedMissingAgents.has(missingKey)) {
        this._reportedMissingAgents.add(missingKey);
        captureMessage(`Turns missing for agent`, {
          level: "warning",
          fingerprint: ["llm-turns-missing"],
          tags: {
            requestedAgentId: agentId,
            mainAgentId: this.agentId,
            streamId: this.streamId,
            eventType: mtype,
            agent: agent
          },
        });
      }
      return;
    }
    const lastMessage = turns.length ? turns.at(-1) : null;
    const lastMessageIndex = turns.length ? turns.length - 1 : null;
    // The synthetic search-status turn (role=system, not partial, no LLM content) is
    // a UI sentinel, not an LLM protocol turn. Protocol events should not validate
    // against it — treat it as absent for role/partial checks.
    const lastLLMMessage = (lastMessage?.role === ROLE_SYSTEM && !lastMessage?.partial) ? null : lastMessage;
    const lastLLMMessageIndex = lastLLMMessage ? lastMessageIndex : null;

    if (_sseVerboseEnabled()) {
      // JSON.stringify on every event is expensive; only build the string
      // when verbose logging is active.
      const msgId = `${this.streamId?.slice(0, 4)}:${event.lastEventId?.slice(0, 4) || "error"}`;
      const context = `${agent}:${agentId?.slice(-4)}:${role}:${mtype}`;
      console.debug(`Received ${msgId} => ${context} (convo length: ${turns.length}) ${JSON.stringify(eventData)}`);
    }

    if (mtype === "search_update" || mtype === "search_results") {
      const systemTurnIdx = turns.findIndex(t => t != null && t.role === ROLE_SYSTEM && !t.partial);
      // Use total result count if available; fall back to incrementing a counter.
      // info field is used as a change signal only — not displayed (may be unsanitized).
      const total = eventData.total;
      const progressInfo = eventData.info;
      if (systemTurnIdx >= 0) {
        const prev = turns[systemTurnIdx];
        const prevInfo = prev._lastInfo;
        // Only replace (trigger MobX re-render) when something meaningful changed
        if (progressInfo !== prevInfo || total !== prev._total) {
          const countText = total != null ? ` — ${total} result${total !== 1 ? 's' : ''} found` : "";
          const displayText = `Analyzing search results${countText}`;
          turns.splice(systemTurnIdx, 1, {
            ...prev,
            content: [{ text: displayText }],
            _lastInfo: progressInfo,
            _total: total,
          });
        }
      } else {
        const countText = total != null ? ` — ${total} result${total !== 1 ? 's' : ''} found` : "";
        turns.push({
          role: ROLE_SYSTEM,
          content: [{ text: `Analyzing search results${countText}` }],
          ts: Date.now(),
          _lastInfo: progressInfo,
          _total: total,
        });
      }

      // search_results carries `datasets_used` — the actual datasets that
      // contributed evidence to the top-ranked results. Attach to the most
      // recent assistant turn so the chips render inline below its message.
      if (mtype === "search_results" && Array.isArray(eventData.datasets_used) && eventData.datasets_used.length > 0) {
        for (let i = turns.length - 1; i >= 0; i--) {
          const t = turns[i];
          if (t?.role === ROLE_ASSISTANT) {
            t.datasetsUsed = eventData.datasets_used;
            break;
          }
        }
      }
    }
    else if (mtype === "message_start") {
      // message_start may or may not include information beyond simply "role"
      //{"type": "message_start","message": {"id": "msg_01GnRNG1psEzTMECgZr32tiv","content": [],"model": "claude-3-sonnet-28k-20240229","role": "assistant","stop_reason": null,"stop_sequence": null,"type": "message","usage": {"input_tokens": 11414,"output_tokens": 1}}}
      // For debugging, keep track of the tool use IDs we're expecting
      const duplicateStart = !!lastLLMMessage && eventData.role === lastLLMMessage?.role;
      if (duplicateStart) {
        // Duplicate message_start for the same role — genuine protocol error.
        // PLEX-6110/WEBAPP-7G instrumentation: this fires both for real backend
        // protocol errors AND for a suspected burst-catchup bug where an
        // intervening toolResult turn gets dropped/misrouted. Capture the raw
        // SSE event history (from the EventSource's ring buffer) plus the tail
        // of this agent's own turn history, so a future occurrence shows
        // whether the toolResult event was ever actually delivered.
        event._duplicateStartDebug = {
          agentId,
          // useStreamConnector's events carry the log themselves (the default path);
          // ResumableEventSource's arrive with it on srcElement (next-gen).
          recentRawEvents: event?.getRecentEvents?.() || event?.srcElement?.getRecentEvents?.() || null,
          turnsTail: turns.slice(-6).map((t, i) => ({
            idx: turns.length - 6 + i,
            role: t?.role,
            partial: !!t?.partial,
            stopReason: t?.stopReason,
            contentBlockTypes: (t?.content || []).map(b =>
              b.toolResult ? "toolResult" : b.toolUse ? "toolUse" : b.text != null ? "text" : "unknown"),
          })),
        };
      }
      if (duplicateStart && turns === this.turns) {
        const error = this._handleStreamError(turns, lastLLMMessageIndex, event);
        if (error) turns.push(error);
      }
      else {
        if (duplicateStart) {
          // PLEX-6303: a sub-agent's duplicate message_start is repaired in place, as a
          // role switch on an open turn is below: close the stale turn and open the new
          // one. Its turns are only displayed, so this can't break the alternation the
          // model needs, and the root stream (and the answer) carries on.
          this._captureStreamAnomaly(
            `duplicate message_start for role '${eventData.role}' in a sub-agent: closing the open turn`,
            { level: "warning", fingerprint: ["duplicate-message-start", "sub-agent"],
              tags: { agentId, mainAgentId: this.agentId, streamId: this.streamId },
              extra: { duplicate_start_debug: event._duplicateStartDebug } }
          );
          if (lastLLMMessage.partial) {
            delete lastLLMMessage.partial;
            cleanTurn(lastLLMMessage);
          }
        }
        if (lastLLMMessage?.partial) {
          // Previous turn still open on role-switch — message_stop was not received before
          // the next message_start. Close the stale partial turn so the conversation state
          // machine stays consistent; the new turn opens normally below.
          delete lastLLMMessage.partial;
          cleanTurn(lastLLMMessage);
          this._captureStreamAnomaly(
            `message_start on partial turn: role switching ${lastLLMMessage.role}→${eventData.role}`,
            { level: "warning", extra: { incomingRole: eventData.role, incomingAgentId: agentId } }
          );
        }
        const newTurn = {
          role: eventData.role,
          ts: Date.now(),
          content: [],
          partial: true
        }
        if (agentId) {
          newTurn.agentId = agentId;
        }
        // Drain any datasets_used stashed by the preceding toolResult so the
        // chips render under the visible answer turn (the toolUse-only
        // assistant turn that preceded the toolResult is invisible).
        if (eventData.role === ROLE_ASSISTANT
            && agentId === this.agentId
            && this._pendingDatasetsUsed) {
          newTurn.datasetsUsed = this._pendingDatasetsUsed;
          this._pendingDatasetsUsed = null;
        }
        _sseDebug(`Add new turn for role ${role} to convo length (${turns.length})`, newTurn);
        if (eventData.role === ROLE_ASSISTANT) {
          Analytics.event(Analytics.EVENTS.AGENT_TURN_START, {
            agent_id: agentId,
            is_root: !agentId || agentId === this.agentId,
            stream_id: this.streamId,
            via_polling: this.pollingMode ?? false,
          });
        }
        console.debug(`Add new turn for role ${role} to convo length (${turns.length})`, newTurn);
        turns.push(newTurn);
        // Mirror to the branching tree (main-agent path only). Without this,
        // every streamed assistant landed in _turns but never in the tree,
        // so persisted records carried turns N>1 with a tree path stuck at
        // the last user prompt — the loader's mismatch fallback
        // (loadConversation:2061) rebuilt linearly on every reload.
        this._mirrorPushToTreeIfMain(turns, newTurn);
      }
    }
    else if (mtype === "content_block_start") {
      // NOTE: optional; content_block_delta may be sent immediately after
      // message_start
      // Parallel tool results: backend fires all content_block_start events before executing
      // any tools, so all content[] slots exist before any out-of-order delta/stop arrives.
      // Validate that we have a partial message with matching role
      // If lastMessage is not partial, we're missing a message_start event
      // If role doesn't match, the message_start had the wrong role
      // Bedrock omits role on content_block_start — only check for an open partial message.
      if (!lastLLMMessage?.partial) {
        // message_start was lost (network gap, replay offset, or out-of-order delivery).
        // Synthesize the missing turn so the stream can continue rather than hard-erroring.
        // Log to Sentry as a warning so we can track frequency without disrupting the user.
        const synthRole = roleInEvent ? role : ROLE_SYSTEM;
        this._captureStreamAnomaly(
          `content_block_start without message_start: synthesising turn for role='${synthRole}'`,
          {
            level: "warning",
            fingerprint: ["missing-message-start", "content_block_start"],
            tags: { role: synthRole, agentId, mainAgentId: this.agentId, streamId: this.streamId },
          }
        );
        const synthTurn = { role: synthRole, ts: Date.now(), content: [], partial: true };
        if (agentId) synthTurn.agentId = agentId;
        turns.push(synthTurn);
      }
      // Re-read lastLLMMessage after possible synthesis above, skipping non-partial
      // system turns (search_update sentinels). A search_update arriving between two
      // content_block_start events would make turns.at(-1) return the sentinel instead
      // of the open partial turn, causing content[index] to be written to the wrong
      // turn and leaving it undefined on the real turn → content_block_stop error (WEBAPP-K).
      let openTurnIndex = turns.length - 1;
      while (openTurnIndex >= 0) {
        const t = turns[openTurnIndex];
        if (!(t?.role === ROLE_SYSTEM && !t?.partial)) break;
        openTurnIndex--;
      }
      const openTurn = openTurnIndex >= 0 ? turns[openTurnIndex] : null;
      if (openTurn?.partial) {
        // {"content_block": {"text": "", "type": "text"}, "index": 0, "type": "content_block_start"}
        const {
          start = null,
          contentBlockIndex: index = eventData.index || 0
        } = eventData;
        if (start) {
          // Attach agent messages to the top-level turn which will contain the
          // tool result, to make the agent messages easier to filter out when
          // generating canonical messages, but also convenient for serializing
          // the entire conversation
          if (start.toolResult) {
            const { toolUseId } = start.toolResult;
            // Be sure to use the same Observable object in map and cache.
            // Only initialize if the slot doesn't already exist — the
            // pre-populate path at the top of handleStreamEvent may have
            // created the slot for sub-agent events (search_update etc.)
            // that arrived BEFORE this content_block_start, and those
            // events have already pushed turns into that array. An
            // unconditional `set(toolUseId, [])` here wipes them.
            if (!this.subAgentTurns.has(toolUseId)) {
              this.subAgentTurns.set(toolUseId, observable.array([]));
            }
            if (!lastMessage.agentMessagesCache) {
              lastMessage.agentMessagesCache = {}
            }
          }

          // Mark text blocks that come after tool use as suppressible (final answer)
          const newBlock = {
            ...start,
            partial: true
          };
          if (start.text !== undefined && openTurn.content.some(b => b.toolUse)) {
            newBlock.suppressFinalAnswer = true;
          }

          // Fill any gap below index with empty partial blocks so the array stays dense
          // and renderers never receive undefined slots (WEBAPP-9C).
          if (openTurn.content.length < index) {
            this._captureStreamAnomaly(
              `content_block_start[${index}] skipped indices ${openTurn.content.length}–${index - 1}`,
              {
                level: "warning",
                fingerprint: ["content-block-start-gap"],
                tags: { index, role: openTurn.role, agentId, mainAgentId: this.agentId,
                        streamId: this.streamId },
              }
            );
            while (openTurn.content.length < index) {
              openTurn.content.push({ partial: true });
            }
          }
          openTurn.content[index] = newBlock;

          // Mark render_widget tool_use blocks as streaming in the store so the
          // canvas can show a placeholder while the widget HTML is in-flight.
          // Suppress the ghost when the account lacks the custom-widgets
          // entitlement (PLEX-4237) — no ghost tab, no auto-open for a widget
          // that will never render.
          if (start.toolUse?.name === "render_widget" && start.toolUse?.toolUseId
              && customWidgetsEnabled()) {
            this._visualizationStore.markStreamingByToolUseId(start.toolUse.toolUseId, {});
          }
        } else {
          const error = this._handleStreamError(turns, openTurnIndex, event,
                                                `'start' block missing from content_block_start ${JSON.stringify(
                                                  eventData)}`);
          if (error) turns.push(error);
        }
      }
    }
    else if (mtype === "content_block_delta") {
      // {"delta": {"text": " that", "type": "text_delta"}, "index": 0, "type": "content_block_delta"}
      const {delta, contentBlockIndex: index = eventData.index || 0} = eventData;
      // Validate that we have a partial message with matching role
      // If lastMessage is not partial, we're missing a message_start event
      // If role doesn't match, the message_start had the wrong role
      // When role is absent from the event (roleInEvent=false), skip the role check: delta events
      // are continuations of an open message, so the role was already established by message_start.

      // Re-read the last LLM turn, skipping trailing non-partial ROLE_SYSTEM sentinels
      // (search_update progress turns). A search_update fired between content_block_start
      // and its content_block_delta inserts a system turn after the still-open partial
      // turn; without this skip, turns.at(-1) returns that sentinel and the partial check
      // false-fires as a missing-message_start anomaly (same pattern as content_block_start/
      // stop, WEBAPP-K).
      let openMsgIndex = turns.length - 1;
      while (openMsgIndex >= 0) {
        const t = turns[openMsgIndex];
        if (!(t?.role === ROLE_SYSTEM && !t?.partial)) break;
        openMsgIndex--;
      }
      let openMsg = openMsgIndex >= 0 ? turns[openMsgIndex] : null;
      if (!openMsg?.partial) {
        // message_start was lost — network gap, replay offset, out-of-order delivery, or
        // a state bug that cleared the partial flag prematurely. Always capture: this is
        // never expected regardless of delta type.
        this._captureStreamAnomaly(
          `content_block_delta without message_start: synthesising turn for role='${role}'`,
          {
            level: "warning",
            fingerprint: ["missing-message-start", "content_block_delta"],
            tags: { role, agentId, mainAgentId: this.agentId, streamId: this.streamId,
                    deltaType: delta?.toolResult ? 'toolResult' : delta?.toolUse ? 'toolUse' : 'text' },
          }
        );
        const synthTurn = { role, ts: Date.now(), content: [], partial: true };
        if (agentId) synthTurn.agentId = agentId;
        turns.push(synthTurn);
        openMsg = synthTurn;
      }

      const roleOk = !roleInEvent || role === openMsg.role;
      if (!roleOk || openMsg.content[index] && !openMsg.content[index]?.partial) {
        const errorMsg = !roleOk
          ? `content_block_delta role mismatch: event role='${role}' but last message role='${openMsg.role}'`
          : `content_block_delta for non-partial content block at index ${index}`;
        const error = this._handleStreamError(turns, openMsgIndex, event, errorMsg);
        if (error) turns.push(error);
      }
      else {
        if (!openMsg.content[index]) {
          openMsg.content[index] = {};
        }
        const contentBlock = openMsg.content[index];
        contentBlock.partial = true;
        if (delta.text !== undefined) {
          contentBlock.text = (contentBlock.text || "") + delta.text;
        } else if (delta.toolUse) {
          if (contentBlock.toolUse) {
            contentBlock.toolUse.input = (contentBlock.toolUse.input || "") + delta.toolUse.input;
          } else {
            contentBlock.toolUse = delta.toolUse;
          }
        } else if (delta.toolResult) {
          contentBlock.toolResult = delta.toolResult;
        } else if (delta.reasoningContent !== undefined) {
          // Fable 5 / models with always-on thinking (Converse API): reasoningContent delta.
          // Store on the block so the UI can show a thinking indicator for responsiveness.
          if (!contentBlock.reasoningContent) contentBlock.reasoningContent = {};
          if (delta.reasoningContent.text !== undefined) {
            contentBlock.reasoningContent.text = (contentBlock.reasoningContent.text || "") + delta.reasoningContent.text;
          }
          if (delta.reasoningContent.signature !== undefined) {
            contentBlock.reasoningContent.signature = delta.reasoningContent.signature;
          }
        } else if (delta.citation !== undefined) {
          // Bedrock's CitationsDelta (RAG/grounding citations), kept as a LIST on the block it
          // supports (PLEX-6049; see citations.js): a complete citation (it carries its
          // location) after a complete one is another citation for the same text. Otherwise
          // it updates the citation being built -- title/source/location are set as they
          // arrive and sourceContent text accumulates. Mirrors interactive.py.
          if (!contentBlock.citations) contentBlock.citations = [];
          const citations = contentBlock.citations;
          const last = citations.at(-1);
          if (!last || (delta.citation.location !== undefined && last.location !== undefined)) {
            citations.push({});
          }
          const citation = citations.at(-1);
          if (delta.citation.title !== undefined) citation.title = delta.citation.title;
          if (delta.citation.source !== undefined) citation.source = delta.citation.source;
          if (delta.citation.location !== undefined) citation.location = delta.citation.location;
          if (delta.citation.sourceContent !== undefined) {
            if (!citation.sourceContent) citation.sourceContent = [];
            delta.citation.sourceContent.forEach((chunkDelta, i) => {
              if (citation.sourceContent[i]) {
                if (chunkDelta.text !== undefined) {
                  citation.sourceContent[i].text = (citation.sourceContent[i].text || "") + chunkDelta.text;
                }
              } else {
                citation.sourceContent[i] = { ...chunkDelta };
              }
            });
          }
        } else if (delta.image !== undefined) {
          // Bedrock's ImageBlockDelta (streaming image generation) isn't documented as
          // partial/chunked the way text is, so each delta is treated as carrying the full
          // image source and replaces any prior one on the block. It is merged over what the
          // block already holds rather than replacing it: ImageBlockStart put the image's
          // (required) format there, the delta carries only the source, and a replayed
          // ImageBlock needs both (PLEX-6049).
          contentBlock.image = {...(contentBlock.image || {}), ...delta.image};
        } else {
          const error = this._handleStreamError(turns, openMsgIndex, event, "Unrecognized delta");
          if (error) turns.push(error);
        }
      }
    }
    else if (mtype === "content_block_stop") {
      // {"type": "content_block_stop", "index": 0}
      // Bedrock omits role on content_block_stop (it defaults to ROLE_SYSTEM), so only
      // check partial state — the same pattern as message_stop.
      // Re-read the last LLM turn: apply the same sentinel skip as lastLLMMessage (ignore
      // non-partial system turns, which are search-progress UI sentinels, not LLM protocol
      // turns) AND pick up any turn synthesised by a preceding content_block_delta (stale
      // lastLLMMessage). A search_update fired during tool-result streaming inserts a
      // system turn after the partial user turn; without this skip, turns.at(-1) returns
      // that system turn and the partial check false-fires (WEBAPP-K).
      let openTurnStopIndex = turns.length - 1;
      while (openTurnStopIndex >= 0) {
        const t = turns[openTurnStopIndex];
        if (!(t?.role === ROLE_SYSTEM && !t?.partial)) break;
        openTurnStopIndex--;
      }
      const openTurnStop = openTurnStopIndex >= 0 ? turns[openTurnStopIndex] : null;
      const {contentBlockIndex: index = eventData.index || 0} = eventData;
      if (!openTurnStop?.partial) {
        // No open partial turn — genuine protocol error (missing message_start).
        const error = this._handleStreamError(turns, openTurnStopIndex, event);
        if (error) turns.push(error);
      }
      else if (openTurnStop.content[index] && !openTurnStop.content[index].partial) {
        // A stop for a block that already closed is a duplicate (PLEX-6610: the backend's
        // error-path stop named block 0 whatever block was open). Keep the block as it is and
        // don't close it a second time: its tool input is already parsed, and its tool result's
        // sub-agent turns and datasets_used were already handled.
        this._captureStreamAnomaly(
          `content_block_stop[${index}] on missing/non-partial block — already closed, kept`,
          {
            level: "warning",
            // Its own fingerprint, so duplicate stops are counted apart from a missing block.
            fingerprint: ["content-block-stop-duplicate", "content_block_stop"],
            tags: { index, role: openTurnStop.role, agentId, mainAgentId: this.agentId,
                    streamId: this.streamId },
          }
        );
      }
      else {
        if (!openTurnStop.content[index]) {
          // Turn is open but content block at this index is missing.
          // Possible causes: content_block_start or content_block_delta was dropped
          // (SSE replay gap, network loss, or 100-breadcrumb cap hid the event from
          // diagnostics). Synthesize an empty stub so the stop can close cleanly.
          this._captureStreamAnomaly(
            `content_block_stop[${index}] on missing/non-partial block — synthesising stub`,
            {
              level: "warning",
              fingerprint: ["missing-content-block", "content_block_stop"],
              tags: { index, role: openTurnStop.role, agentId, mainAgentId: this.agentId,
                      streamId: this.streamId },
            }
          );
          openTurnStop.content[index] = { partial: true };
        }
        const contentBlock = openTurnStop.content[index];
        delete contentBlock.partial;
        if (contentBlock.toolResult) {
          const {toolUseId} = contentBlock.toolResult;
          // The sub-agent has reached end_turn — no more UI updates are coming for
          // it, so there's no reason to keep its turns MobX-observable. Replace the
          // live observable.array with a plain optimized array; the map is
          // observable.shallow, so swapping the value doesn't re-wrap it.
          const liveMessages = this.subAgentTurns.get(toolUseId);
          if (liveMessages) {
            const _toJsStart = Date.now();
            // Strip redundant toolResult content immediately — it's already retained
            // in full via this turn's own toolResult content block, so keeping it a
            // second time here just duplicates the largest payloads (fetch_results,
            // fetch_direct_relationships, etc.) for the rest of the session's lifetime.
            const optimized = optimizeAgentMessages(toJS(liveMessages));
            if (optimized) {
              this.subAgentTurns.set(toolUseId, optimized);
            } else {
              this.subAgentTurns.delete(toolUseId);
            }
            const _toJsMs = Date.now() - _toJsStart;
            if (_toJsMs > 50) {
              Analytics.event('chat-tojs-perf', {
                duration_ms: _toJsMs,
                message_count: liveMessages.length,
                tool_use_id: toolUseId,
              });
            }
          }
        }
        // Whenever a toolResult finalizes, peek at its JSON content for a
        // `datasets_used` field. The `search` tool (and any tool that runs a
        // search) appends a `{json: {datasets_used: [...]}}` block alongside
        // its narrative response. We stash the list on the client and drain
        // it onto the *next* assistant turn at message_start, because the
        // toolUse-only assistant turn that preceded this toolResult isn't
        // rendered — the visible answer turn is the one that follows.
        if (contentBlock.toolResult) {
          const items = contentBlock.toolResult.content || [];
          for (const item of items) {
            const j = item?.json;
            if (j && Array.isArray(j.datasets_used) && j.datasets_used.length > 0) {
              this._pendingDatasetsUsed = j.datasets_used;
              break;
            }
          }
        }
        if (contentBlock.toolUse) {
          const serialized = contentBlock.toolUse.input;
          contentBlock.toolUse.input = safeParseJSON(serialized) || {error: serialized};
        }
      }
    }
    else if (mtype === "message_stop") {
      console.debug(`[LLMClient] message_stop agentId=${agentId?.slice(-6)} stopReason=${stopReason} lastMsg.partial=${lastLLMMessage?.partial} lastMsg.role=${lastLLMMessage?.role}`);
      if (lastLLMMessage?.partial) {
        // Close the open partial turn (assistant or user/tool-result).
        lastLLMMessage.stopReason = stopReason;
        delete lastLLMMessage.partial;
        //{"type": "message_stop","amazon-bedrock-invocationMetrics": {"inputTokenCount": 11414,"outputTokenCount": 62,"invocationLatency": 5665,"firstByteLatency": 3598}}
        cleanTurn(lastLLMMessage);
        if (lastLLMMessage.role === ROLE_ASSISTANT) {
          Analytics.event(Analytics.EVENTS.AGENT_TURN_COMPLETE, {
            agent_id: agentId,
            is_root: !agentId || agentId === this.agentId,
            stop_reason: stopReason,
            stream_id: this.streamId,
            via_polling: this.pollingMode ?? false,
            duration_ms: lastLLMMessage.ts ? Date.now() - lastLLMMessage.ts : undefined,
          });
        }
        // Notify on every completed turn (root or sub-agent) so callers can persist
        // incremental progress. Sub-agent message_stop fires here too, giving a save
        // checkpoint after each tool-use stop and each sub-agent turn completion.
        if (this._onAssistantTurnComplete) {
          this._onAssistantTurnComplete();
        }
      } else if (!lastLLMMessage || lastLLMMessage.role !== ROLE_ASSISTANT) {
        // message_stop with no open partial turn and no completed assistant turn — Bedrock sent a
        // terminal event without a preceding message_start (e.g. content_filtered fires before
        // generation begins). Synthesize a closed assistant turn and capture to Sentry.
        if (stopReason) {
          // Stops that don't warrant a Sentry anomaly report. content_filtered is expected
          // (Bedrock refused before generation began) — backend emits the Sentry issue and it
          // routes to #monitoring; no need for a second capture here.
          const SENTRY_SILENT_STOPS = [StopReason.END_TURN, StopReason.TOOL_USE, StopReason.MAX_TOKENS,
                                       StopReason.STOP_SEQUENCE, StopReason.CONTENT_FILTERED];
          const isAbnormalStop = !SENTRY_SILENT_STOPS.includes(stopReason);
          turns.push({
            role: ROLE_ASSISTANT,
            content: [{text: `(${stopReason})`}],
            stopReason,
            status: (isAbnormalStop || stopReason === StopReason.CONTENT_FILTERED) ? "error" : undefined,
            ts: Date.now(),
          });
          if (stopReason === StopReason.CONTENT_FILTERED) {
            const lastUserTurn = [...turns].reverse().find(t => t.role === ROLE_USER);
            const promptText = lastUserTurn?.content?.find(b => b.text)?.text ?? "";
            const { cell } = getConfig();
            Analytics.event(Analytics.EVENTS.CHAT_CONTENT_FILTERED, {
              agent,
              agentId,
              cell,
              user: this._userStore?.userName,
              tenant: this._userStore?.tenantID,
              prompt: promptText,
            });

            const fallbackModel = this._appStatusStore?.aiModelContentFilterFallback;
            const isRetry = !!(this._lastSendParams?.modelOverride);
            if (agentId === this.agentId && fallbackModel && !isRetry && lastUserTurn) {
              runInAction(() => {
                const filteredIdx = turns.findLastIndex(t => t.stopReason === StopReason.CONTENT_FILTERED);
                if (filteredIdx !== -1) turns.splice(filteredIdx, 1);
              });
              const userTurnIdx = turns.lastIndexOf(lastUserTurn);
              this.replayFrom(userTurnIdx, {...(this._lastSendParams ?? {}), modelOverride: fallbackModel});
            }
          } else if (isAbnormalStop) {
            this._captureStreamAnomaly(`Bedrock ${stopReason} on ${agent} (no message_start)`, {
              level: "warning",
              fingerprint: ["bedrock-abnormal-stop", stopReason, agent],
              tags: {
                stopReason,
                agent,
                agentId,
                mainAgentId: this.agentId,
                streamId: this.streamId,
              },
            });
          }
        }
      } else {
        // message_stop without an open partial message — nothing to close
        // (message_stop events often omit role, so we only check partial state)
        const error = this._handleStreamError(turns, lastLLMMessageIndex, event);
        if (error) turns.push(error);
      }
    }
    else if (mtype === "metadata") {
      if (!lastLLMMessage) {
        // Only a synthetic search-status system turn exists — no LLM turn to attach metadata to.
      } else if (role !== lastLLMMessage?.role) {
        const error = this._handleStreamError(turns, lastLLMMessageIndex, event);
        if (error) turns.push(error);
      }
      else {
        const {usage = {}, metrics = {}} = eventData.metadata || {};
        const {
          inputTokens = 0,
          outputTokens = 0,
          totalTokens = 0,
          cacheReadInputTokens = 0,
          cacheWriteInputTokens = 0
        } = usage;
        const {latencyMs = 0} = metrics;
        this.inputTokens += +inputTokens;
        this.outputTokens += +outputTokens;
        this.totalTokens += +totalTokens;
        this.cacheReadInputTokens += +cacheReadInputTokens;
        this.cacheWriteInputTokens += +cacheWriteInputTokens;
        this.latencyMs += +latencyMs;
        lastLLMMessage.metadata = {usage, metrics};
      }
    }
    else if (mtype === "ping" || mtype === "heartbeat") {
    }
    else if (mtype === "toast") {
      toast.info(eventData?.text || "Model enabled", {autoClose: 5000});
    }
    else if (mtype === "interrupt") {
      const { status = "interrupt" } = eventData || {};
      this.handleInterrupt(lastMessage, status === "cancel");
    }
    else if (mtype === "message") {
      // complete toolResult message is also sent when fully assembled
      _sseDebug(`Received assembled message: ${event.lastEventId}: ${agentId}:${role}`, eventData);
      // PLEX-6383: the backend stores this turn's node under this event's id.
      if (agentId === this.agentId && this.historyPersisted) {
        this._adoptNodeId(eventData.role, event.lastEventId);
      }
      // PLEX-6649: the answer that ends the turn carries how long the whole turn took, from
      // the request that opened its stream to this answer. Stored on the final assistant
      // turn before this turn's saves (the streamId reset below and the checkpoint after),
      // so the record keeps it.
      if (eventData.role === ROLE_ASSISTANT && agentId === this.agentId
          && Number.isFinite(eventData.turnElapsedMs)) {
        const finalTurn = [...turns].reverse().find(t => t?.role === ROLE_ASSISTANT);
        if (finalTurn) {
          finalTurn.turnElapsedMs = eventData.turnElapsedMs;
        }
      }
      if (eventData.role === ROLE_ASSISTANT && agentId === this.agentId && stopReason !== StopReason.TOOL_USE) {
        // Conversation has completed normally, set state appropriately
        this.active = false;
        this.streamId = null;
      }
      // When message_stop(content_filtered) fires before generation begins, the client
      // synthesizes a placeholder turn. The server follows up with a message event carrying
      // the full content (remediation text). Patch it in here so the user sees guidance.
      if (stopReason === StopReason.CONTENT_FILTERED && eventData.role === ROLE_ASSISTANT) {
        const serverContent = eventData.content;
        if (serverContent?.length) {
          const lastAssistant = [...turns].reverse().find(t => t.role === ROLE_ASSISTANT && t.stopReason === StopReason.CONTENT_FILTERED);
          if (lastAssistant) {
            const remediationBlocks = serverContent.filter(b => b.text && !b.text.match(/^\(content_filtered\)$/));
            if (remediationBlocks.length) {
              lastAssistant.content = remediationBlocks;
            }
          }
        }
      }
      // Apply server-driven compaction state if the server compacted this turn.
      // summary/offset tell the client which original turns were replaced so it can
      // preserve full turn history for UI display while using the compact form for LLM calls.
      if (agentId === this.agentId && eventData.metadata?.compaction) {
        const {summary, offset} = eventData.metadata.compaction;
        runInAction(() => {
          this._compaction = {summary, offset, compactedAt: Date.now()};
        });
      }
      // A fully-assembled turn (user tool-result or assistant) is a save checkpoint
      if (this._onAssistantTurnComplete) {
        this._onAssistantTurnComplete();
      }
    }
    else if (mtype === "error" && role === ROLE_ASSISTANT && stopReason === "error") {
      // Assistant-reported error (e.g., validation error from server/LLM)
      // Preserve as ASSISTANT error, don't convert to SYSTEM error
      if (lastMessage?.role === ROLE_ASSISTANT && (lastMessage?.partial || lastMessage?.stopReason === "error")) {
        // Add error content to the partial ASSISTANT turn (from message_start), or to a turn
        // already closed by a preceding message_stop(stopReason=error) in the same synthetic sequence
        const errorContent = eventData?.content || [{text: "Unknown error"}];
        errorContent.forEach(block => {
          lastMessage.content.push(block);
        });
        // Complete the partial ASSISTANT turn with error status
        lastMessage.stopReason = stopReason;
        lastMessage.status = "error";
        delete lastMessage.partial;
        cleanTurn(lastMessage);
        console.debug(`Completed ASSISTANT turn with error status at index ${lastMessageIndex}`, lastMessage);
      } else {
        // No partial ASSISTANT turn exists - this can happen with validation errors
        // that occur before message_start is sent. Create a new ASSISTANT error turn.
        const errorContent = eventData?.content || [{text: "Unknown error"}];
        const assistantError = {
          role: ROLE_ASSISTANT,
          content: errorContent,
          stopReason: stopReason,
          status: "error",
          ts: Date.now()
        };
        if (agentId) {
          assistantError.agentId = agentId;
        }
        if (eventData.agent) {
          assistantError.agent = eventData.agent;
        }
        turns.push(assistantError);
        this._mirrorPushToTreeIfMain(turns, assistantError);
        console.debug(`Created new ASSISTANT error turn at index ${turns.length - 1}`, assistantError);
      }
      // Capture to Sentry so server-side errors surface in #issues
      const serverMsg = eventData?.content?.map(b => b.text || "").join("") || "Unknown server error";
      const conversationCapture = this._buildConversationCapture({});
      captureMessage(`Server error: ${serverMsg.substring(0, 150)}`, {
        level: "error",
        fingerprint: ["server-reported-error", agent],
        tags: {
          agent,
          agentId,
          mainAgentId: this.agentId,
          streamId: this.streamId,
          sessionAgent: this.sessionAgent,
          errorCategory: "server-error",
          conversationLength: turns.length,
        },
        ...conversationCapture,
      });
      // Close stream and mark as inactive
      // console.debug, not .warn (see PLEX-6334 / WEBAPP-DJ): embeds
      // agentId/mainAgentId per call, so Sentry's captureConsoleIntegration
      // can't group occurrences and spawns a fresh noise issue each time
      // (this is WEBAPP-AA).
      console.debug(
        `[LLMClient] mtype=error closing root stream: agentId=${agentId} mainAgentId=${this.agentId} ` +
        `agentIdMatchesRoot=${agentId === this.agentId} serverMsg=${JSON.stringify(serverMsg.slice(0, 150))}`
      );
      this.active = false;
      this.streamId = null;
    }
    else {
      let messageText;
      // Resolved connection state: prefer SSE EventSource connectionState; fall back to
      // useStreamConnector's reason field (set on fetch-based streaming error objects).
      const connState = event.connectionState
        || (event.reason === "network_error" ? "OPEN" : null)
        || (event.reason === "bad_response" ? "CONNECTING" : null)
        || (event.reason === "poll_error" ? "POLLING" : null);
      if (mtype !== "error") {
        messageText = "Unexpected event";
      } else {
        // Extract error information from the event
        const errorMessage = eventData?.error
                             || eventData?.content?.map(b => b.text || "").join("")
                             || null;

        // Distinguish between actual connection errors and server-reported errors.
        if (connState && !errorMessage) {
          // True connection error (no error message from server)
          messageText = `Connection error (state: ${connState})`;
        } else if (connState && errorMessage) {
          // Server error delivered via SSE
          messageText = `Server error (state: ${connState}): ${errorMessage}`;
        } else if (errorMessage) {
          // Server error without connection state
          messageText = errorMessage;
        } else {
          // No error message at all
          messageText = event.reason ? `Connection lost (${event.reason})` : "Connection lost";
        }
      }
      const streamInfo = DEV ? ` (${this.streamId})` : "";

      // Propagate connState into the event so _handleStreamError classifies it correctly.
      const effectiveEvent = (connState && !event.connectionState)
        ? { ...event, connectionState: connState }
        : event;
      const error = this._handleStreamError(turns, lastMessageIndex, effectiveEvent,
                                            `${messageText}${streamInfo}`);
      if (error) turns.push(error);
    }
  }

  handleInterrupt(lastMessage = null, cancel = false) {

    // Dedup: the server raises UserInterrupt independently in the root agent's
    // loop AND in every still-running sub-agent's loop for a single user cancel
    // action (each has its own check_for_interrupt() call site in interactive.py),
    // and each publishes its own "interrupt" SSE event on the same stream_id
    // channel. Without this guard, every one of those echoes re-runs the append
    // logic below and produces a repeating "I was interrupted" bubble for what
    // was a single user action. Only the first interrupt for the current send
    // cycle does the turn-mutation work; later ones just make sure
    // busy/connecting state is (still) cleared and stop.
    const alreadyHandled = this._interruptHandled;
    this._interruptHandled = true;
    // Invalidate any in-flight send (_sendPrompt/_continueFromToolResults) for the
    // cycle being interrupted, so its .then()/.catch() recognizes itself as stale
    // and skips reviving streamId/waitingForResponse once it resolves.
    this._generation++;

    this.streamId = null;
    this.waitingForResponse = false;

    if (alreadyHandled) {
      return;
    }

    if (cancel) {
      // Mark the last partial turn as cancelled so the UI can distinguish it from truncation.
      // Append *User Interrupt* and the follow-up prompt — especially important when the user
      // cancelled due to a long delay and was never shown the interrupted-response prompt.
      const cancelTurns = this.turns;
      const lastPartial = cancelTurns.length ? cancelTurns.at(-1) : null;
      if (lastPartial?.partial) {
        lastPartial.partial = false;
        lastPartial.cancelled = true;
        const lastBlock = lastPartial.content?.at(-1);
        const interruptSuffix = "\n\n*User Interrupt*";
        if (lastBlock?.text !== undefined) {
          lastBlock.text = (lastBlock.text || "") + interruptSuffix;
        } else {
          lastPartial.content = [...(lastPartial.content || []), { text: interruptSuffix.trim() }];
        }
      }
      // Add synthetic assistant follow-up so the user knows they can redirect
      cancelTurns.push({
        role: ROLE_ASSISTANT,
        ts: Date.now(),
        content: [{ text: "I was interrupted. What would you like me to do next?" }],
        cancelled: true,
      });
      return;
    }

    const turns = this.turns;
    if (lastMessage === null) {
      lastMessage = turns.length ? turns.at(-1) : null;
    }

    // Helper function to ensure all tool uses have corresponding tool results
    const ensureToolResultsComplete = (userTurn) => {
      const lastAssistantTurn = turns.slice().reverse().find(turn => turn.role === ROLE_ASSISTANT);
      if (lastAssistantTurn) {
        const expectedToolUseIds = lastAssistantTurn.content
          .filter(b => b.toolUse)
          .map(b => b.toolUse.toolUseId);

        const existingToolResultIds = userTurn.content
          .filter(b => b.toolResult)
          .map(b => b.toolResult.toolUseId);

        const missingToolUseIds = expectedToolUseIds.filter(id => !existingToolResultIds.includes(id));

        // Add missing tool results as interrupted
        missingToolUseIds.forEach(toolUseId => {
          userTurn.content.push({
                                   toolResult: {
                                     toolUseId,
                                     content: [{ text: "User interrupt" }],
                                     status: "error"
                                   }
                                 });
        });
      }
    };

    // Clean up conversation state to prepare for new user input
    if (!lastMessage?.partial) {
      // Complete message exists
      if (lastMessage?.role === ROLE_USER) {
        // Ensure all tool results are present
        ensureToolResultsComplete(lastMessage);
        // Add assistant response
        const ack = {
          role: ROLE_ASSISTANT,
          content: [{ text: "I was interrupted. What would you like me to do next?" }],
          stopReason: "user_interrupt"
        };
        turns.push(ack);
        this._mirrorPushToTreeIfMain(turns, ack);
      } else if (lastMessage?.role !== ROLE_ASSISTANT) {
        const ack = {
          role: ROLE_ASSISTANT,
          content: [{ text: "I was interrupted. What would you like me to do next?" }],
          stopReason: "user_interrupt"
        };
        turns.push(ack);
        this._mirrorPushToTreeIfMain(turns, ack);
      } else {
        // Complete assistant turn. If it already finished naturally (end_turn/tool_use),
        // don't append an interrupt message — the LLM completed before the interrupt
        // was processed, producing the contradictory "complete + interrupted" display
        // (PLEX-5826). Only annotate if the turn was stopped for an unexpected reason.
        const alreadyComplete = lastMessage.stopReason === StopReason.END_TURN
          || lastMessage.stopReason === StopReason.TOOL_USE
          || lastMessage.stopReason === StopReason.MAX_TOKENS
          || lastMessage.stopReason === StopReason.STOP_SEQUENCE;
        if (!alreadyComplete) {
          lastMessage.content.push({ text: "I was interrupted. What would you like me to do next?" });
          lastMessage.stopReason = "user_interrupt";
        }
      }
    } else {
      // Partial message exists - need to clean it up
      delete lastMessage.partial;

      if (lastMessage?.role === ROLE_ASSISTANT) {
        // Interrupted during assistant response
        // Mark any partial content as interrupted and update message
        lastMessage.content.forEach(contentBlock => {
          if (contentBlock.partial) {
            delete contentBlock.partial;
            if (contentBlock.text) {
              contentBlock.text += " [interrupted]";
            }
          }
        });

        // Append interruption message only if no toolUse blocks are present.
        // A turn with toolUse blocks that receives a trailing text block produces
        // [text, toolUse, text] — Connexion rejects this (toolUse.input must be last).
        if (!lastMessage.content.some(b => b.toolUse)) {
          lastMessage.content.push({ text: "I was interrupted. What would you like me to do next?" });
        }
        lastMessage.stopReason = "user_interrupt";
      } else if (lastMessage?.role === ROLE_USER) {
        // Interrupted during tool results - complete any partial tool results
        lastMessage.content.forEach(contentBlock => {
          if (contentBlock.partial) {
            delete contentBlock.partial;
            if (contentBlock.toolResult) {
              // Complete tool result with interrupt message
              contentBlock.toolResult.content = [{ text: "User interrupt" }];
              contentBlock.toolResult.status = "error";
            }
          }
        });
        // Find any expected tool results that are missing and add them as interrupted
        const lastAssistantTurn = turns.slice().reverse().find(turn => turn.role === ROLE_ASSISTANT);
        if (lastAssistantTurn) {
          const expectedToolUseIds = lastAssistantTurn.content
            .filter(b => b.toolUse)
            .map(b => b.toolUse.toolUseId);

          const existingToolResultIds = lastMessage.content
            .filter(b => b.toolResult)
            .map(b => b.toolResult.toolUseId);

          const missingToolUseIds = expectedToolUseIds.filter(id => !existingToolResultIds.includes(id));

          // Add missing tool results as interrupted
          missingToolUseIds.forEach(toolUseId => {
            lastMessage.content.push({
                                       toolResult: {
                                         toolUseId,
                                         content: [{ text: "User interrupt" }],
                                         status: "error"
                                       }
                                     });
          });
        }

        // Add assistant response asking for more input (this ensures USER -> ASSISTANT alternation)
        const ack = {
          role: ROLE_ASSISTANT,
          content: [{ text: "I was interrupted. What would you like me to do next?" }],
          stopReason: "user_interrupt"
        };
        turns.push(ack);
        this._mirrorPushToTreeIfMain(turns, ack);
      }
    }

    // Defensive cleanup: remove any null/undefined turns that may have been introduced
    // by race conditions or edge cases in the interrupt handling path.
    const nullCount = turns.filter(t => t == null).length;
    if (nullCount > 0) {
      this._captureStreamAnomaly(
        `cancelStream: ${nullCount} null/undefined turn(s) found in conversation`,
        {
          level: "error",
          fingerprint: ["null-turns-in-conversation"],
          tags: { streamId: this.streamId, agentId: this.agentId },
          extra: {
            nullCount,
            totalTurns: turns.length,
            ...this._buildConversationCapture().context,
          },
        }
      );
      turns.splice(0, turns.length, ...turns.filter(t => t != null));
    }
  }

  _updateMetadata = (turn) => {
    if (!turn) return;
    // Sub-agent turns (subAgentTurns) never contribute their own usage/metrics —
    // they're merged into the parent tool-use turn's totals server-side. No
    // recursion needed; there is no nested structure to walk.
    const metadata = turn.metadata || turn;
    const {usage = {}, metrics = {}} = metadata;
    const {
      inputTokens = 0,
      outputTokens = 0,
      totalTokens = 0,
      cacheReadInputTokens = 0,
      cacheWriteInputTokens = 0
    } = usage;
    const { latencyMs = 0 } = metrics;
    this.inputTokens += +inputTokens;
    this.outputTokens += +outputTokens;
    this.totalTokens += +totalTokens;
    this.cacheReadInputTokens += +cacheReadInputTokens;
    this.cacheWriteInputTokens += +cacheWriteInputTokens;
    this.latencyMs += +latencyMs;
  };

  _calculateMetrics = () => {
    this.inputTokens = this.outputTokens = this.totalTokens = this.cacheReadInputTokens = this.cacheWriteInputTokens = this.latencyMs = 0;
    this.turns.forEach((turn) => {
      this._updateMetadata(toJS(turn));
    });
    // Each sub-agent (run_analyst, etc.) invokes its own LLM call and accrues its own
    // usage/metrics server-side — those turns carry their own metadata and must be
    // folded in separately; they're not included in the root turns' totals.
    this.subAgentTurns.forEach((messages) => {
      toJS(messages).forEach((turn) => this._updateMetadata(turn));
    });
  }

  loadConversation = ({turns: parsed, compaction = null, tree = null,
                      subAgentTurns = null, demotePartial = true,
                      deferTrailingToolRepair = false}) => {
    try {
      // Detect and log null/undefined turns in stored conversation data
      const nullIndices = parsed.reduce((acc, t, i) => (t == null ? [...acc, i] : acc), []);
      if (nullIndices.length > 0) {
        captureMessage("loadConversation: null/undefined turns in stored conversation", {
          level: "warning",
          fingerprint: ["load-conversation-null-turns"],
          tags: {nullCount: nullIndices.length},
          data: {nullIndices, parsedLength: parsed.length, parsedTurns: parsed},
        });
      }
      // Flat accumulator for sub-agent turns, keyed by toolUseId, merged into
      // this.subAgentTurns at the end. Fed from three sources: the new top-level
      // subAgentTurns param, back-compat per-turn turn.agentMessagesCache (older
      // persisted format), and the "search-analyst" role back-compat path below.
      const loadedSubAgentTurns = {...(subAgentTurns || {})};
      const convo = [];
      let currentAgentId = this.agentId;
      parsed.filter(turn => turn != null).forEach(turn => {
        if (!turn.agent) {
          turn.agent = this.sessionAgent;
        }
        if (turn.role === undefined || turn.role === null) {
          captureMessage("loadConversation: turn with missing role", {
            level: "error",
            fingerprint: ["load-conversation-missing-role"],
            tags: {turnIndex: String(parsed.indexOf(turn))},
            data: {
              turn: JSON.stringify(turn),
              parsedLength: parsed.length,
              allRoles: parsed.map((t, i) => ({i, role: t?.role})),
            },
          });
          return; // skip — can't process a turn without a role
        }
        // Backwards-compatibility, temporary
        if (turn.role.startsWith("search-analyst")) {
          turn.agent = AGENT_ANALYST;
          turn.role = turn.role === "search-analyst" ? ROLE_ASSISTANT : ROLE_USER;
          if (!loadedSubAgentTurns[currentAgentId]) {
            loadedSubAgentTurns[currentAgentId] = [];
          }
          loadedSubAgentTurns[currentAgentId].push(cleanTurn(turn));
          return;
        }
        if (turn.role === "tool") {
          // All tool roles become user (tool results)
          turn.role = ROLE_USER;
          turn.agentId = this.agentId;
        }
        // Back-compat: fold an older-format per-turn agentMessagesCache into the
        // flat top-level accumulator, keyed the same way (by toolUseId).
        if (turn.agentMessagesCache) {
          Object.entries(turn.agentMessagesCache).forEach(([toolUseId, messages]) => {
            loadedSubAgentTurns[toolUseId] = messages;
          });
          delete turn.agentMessagesCache;
        }
        if (turn.role === ROLE_ASSISTANT) {
          if (turn.content.some(b => b.toolUse)) {
            currentAgentId = turn.content.reduce((result, b) => result || b.toolUse?.toolUseId, null);
          }
        }
        // End backward compatibility adjustment

        // Modify all top-level agent IDs to be consistent
        turn.agentId = this.agentId;
        normalizeLegacyToolNames(turn);
        cleanTurn(turn);
        convo.push(turn);
      });

      // Repair any assistant turns that have unpaired toolUse blocks (interrupted sessions,
      // or conversations where a prior error left a role=system turn instead of a toolResult).
      // For each such turn, strip the invalid junk following it and inject a proper
      // toolResult(error) + assistant(error-ack) pair so the conversation is structurally
      // valid and the user can continue without hitting a Bedrock ValidationException.
      //
      // We scan all assistant turns (not just the last) because a mid-conversation unpaired
      // toolUse (followed by more turns) is equally invalid from Bedrock's perspective.
      //
      // PLEX-6303: except, with deferTrailingToolRepair, the LAST assistant turn. Its tool
      // call may belong to a turn that is still running (the chat was reloaded mid-handoff),
      // and repairing it would put "Tool call failed" in front of the model for a call that
      // hasn't failed: the coach then tells the user the handoff didn't fire. The v2 loader
      // defers while it finds out, and loads again without deferring once the turn is over.
      let deferAt = -1;
      if (deferTrailingToolRepair) {
        for (let k = convo.length - 1; k >= 0; k--) {
          if (convo[k].role === ROLE_ASSISTANT) { deferAt = k; break; }
        }
      }
      let i = 0;
      while (i < convo.length) {
        const turn = convo[i];
        if (turn.role !== ROLE_ASSISTANT) { i++; continue; }

        const toolUseIds = (turn.content || []).filter(b => b.toolUse).map(b => b.toolUse.toolUseId);
        if (toolUseIds.length === 0) { i++; continue; }
        if (i === deferAt) { i++; continue; }

        // Walk past any role=system turns to find the actual next USER turn —
        // the real toolResult landing site. Computing `missing` from the
        // immediate next turn (which may be a system error injected by a
        // prior _handleStreamError) over-counts: every id looks missing,
        // and the subsequent merge then duplicates the ones the real user
        // turn already covered.
        let insertAt = i + 1;
        while (insertAt < convo.length && convo[insertAt].role === ROLE_SYSTEM) {
          insertAt++;
        }
        const turnAfterGap = insertAt < convo.length ? convo[insertAt] : null;

        // Clean up the actual landing turn (not the system-error noise).
        // Partial flags and content-less toolResult blocks are signs of a
        // stream that was interrupted before cleanup ran.
        if (turnAfterGap?.role === ROLE_USER) {
          turnAfterGap.content = turnAfterGap.content.filter(block => {
            if (block.toolResult && !block.toolResult.content?.length) {
              console.debug(`loadConversation: dropping incomplete toolResult for ${block.toolResult.toolUseId}`);
              return false;
            }
            return true;
          });
          turnAfterGap.content.forEach(block => { delete block.partial; });
          delete turnAfterGap.partial;
        }
        const coveredIds = new Set(
          (turnAfterGap?.role === ROLE_USER
            ? (turnAfterGap.content || []).filter(b => b.toolResult).map(b => b.toolResult.toolUseId)
            : [])
        );
        const missing = toolUseIds.filter(id => !coveredIds.has(id));
        if (missing.length === 0) {
          // Nothing missing, but we may still need to remove the system
          // turns sitting between the assistant and turnAfterGap.
          if (insertAt > i + 1) {
            convo.splice(i + 1, insertAt - (i + 1));
          }
          i++;
          continue;
        }

        // Insert placeholder toolResult(s) for the missing IDs and remove
        // the system gap. The renderer surfaces these as failed calls.
        console.debug(`loadConversation: repairing unpaired toolUse at index ${i} (missing results for ${missing})`);
        const placeholderTurn = {
          role: ROLE_USER,
          content: missing.map(id => ({
            toolResult: {
              toolUseId: id,
              content: [{text: INTERRUPTED_TOOL_TEXT}],
              status: "error"
            }
          })),
          stopReason: "error",
          status: "error",
          ts: Date.now()
        };
        // If a real user turn follows the system gap, merge the placeholder
        // toolResult content into it (Bedrock requires strictly alternating
        // user/assistant). Otherwise insert as a fresh user turn.
        if (turnAfterGap?.role === ROLE_USER) {
          turnAfterGap.content = [...placeholderTurn.content, ...turnAfterGap.content];
          convo.splice(i + 1, insertAt - (i + 1)); // just remove the system turns
        } else {
          convo.splice(i + 1, insertAt - (i + 1), placeholderTurn);
        }
        i += 2; // skip past the assistant turn and the new/merged user turn
      }

      // PLEX-6187: a crash's stuck calls, paired with stand-in results at the end of the
      // conversation (here, or by the conversation GET), are for display only: the record
      // ends at the calls, and nothing the user wrote is in that turn.
      if (convo.length >= 2 && isInterruptionStandIn(convo.at(-1), convo.at(-2))) {
        convo.at(-1).displayOnly = true;
      }

      // Detect a stale-partial last turn — turn completed on the backend but partial flag
      // was not cleared before persistence (e.g. tab backgrounded during final SSE flush,
      // render deferred, or cleanTurn not called on the saved snapshot).
      // Repair: strip partial, append truncation notice so the user knows the response may
      // be incomplete, and fire a backend snapshot + Sentry capture for forensic analysis.
      const lastConvoTurn = convo.length ? convo.at(-1) : null;
      // Gated on demotePartial for the same reason the demotion loop below is: on the
      // resume path a partial last turn is EXPECTED (the SSE replay extends it), so
      // stripping the flag here would break resume and the snapshot would be a false
      // alarm. Without the gate this also ran first and silently neutralised the
      // demotion below, leaving the turn with no stopReason.
      if (demotePartial && (lastConvoTurn?.partial ||
          lastConvoTurn?.content?.some(b => b.partial))) {
        // Fire-and-forget snapshot against the stream that owned this conversation.
        // streamId is not available here (we are loading a stored convo, not a live stream).
        // PLEX-6259: by conversation id, which resolves to its latest turn while that is
        // retained, and with auth. The retired /agent/{id}/snapshot was asked for this
        // engine's own agent id, never the one that wrote the turn, and without auth.
        const agentIdForSnapshot = this.agentId;
        const conversationIdForSnapshot = this.historyId;
        if (agentIdForSnapshot) {
          const headers = this.auth?.jwtToken ? { Authorization: `Bearer ${this.auth.jwtToken}` } : {};
          (conversationIdForSnapshot
            ? fetch(`/api/stream/${encodeURIComponent(conversationIdForSnapshot)}/snapshot`, { headers })
                .then(r => r.ok ? r.json() : null)
            : Promise.resolve(null))
            .then(snapshot => {
              captureMessage("loadConversation: stale partial turn repaired", {
                level: "warning",
                fingerprint: ["load-conversation-stale-partial"],
                tags: {
                  agentId: agentIdForSnapshot,
                  conversationId: conversationIdForSnapshot ?? null,
                  turnIndex: String(convo.length - 1),
                  turnRole: lastConvoTurn.role,
                },
                extra: {
                  partialBlocks: lastConvoTurn.content?.map((b, i) => ({
                    index: i,
                    partial: !!b.partial,
                    type: b.text ? "text" : b.toolUse ? "toolUse" : b.toolResult ? "toolResult" : "unknown",
                    preview: (b.text || JSON.stringify(b) || "").substring(0, 100),
                  })),
                  agentSnapshot: snapshot,
                },
              });
            })
            .catch(() => {
              captureMessage("loadConversation: stale partial turn repaired (no snapshot)", {
                level: "warning",
                fingerprint: ["load-conversation-stale-partial"],
                tags: { agentId: agentIdForSnapshot, turnIndex: String(convo.length - 1) },
              });
            });
        }
        // Repair the turn so the conversation is usable
        delete lastConvoTurn.partial;
        // Same static "interrupted" state the demotion loop below applies, so the UI
        // renders the truncation rather than a turn that merely looks complete.
        if (!lastConvoTurn.stopReason) lastConvoTurn.stopReason = "interrupted";
        lastConvoTurn.content?.forEach(b => { delete b.partial; });
        const lastTextBlock = lastConvoTurn.content
          ? [...lastConvoTurn.content].reverse().find(b => b.text != null)
          : null;
        if (lastTextBlock) {
          lastTextBlock.text += "\n\n*(Response may be incomplete — session was interrupted)*";
        } else if (lastConvoTurn.content) {
          lastConvoTurn.content.push({ text: "*(Response may be incomplete — session was interrupted)*" });
        }
        console.debug("loadConversation: repaired stale partial turn at index", convo.length - 1);
      }

      //console.log("loadConversation: parsed", parsed.length, "turns, returning", convo.length, "turns");
      // Partial-flag demotion (step 7 of the resilience plan). When the
      // chat surface persists a turn mid-stream (via the polling partial
      // save), the saved record carries `partial: true` on the assistant
      // turn AND on its in-progress content blocks. On reload there is
      // no active SSE stream to finish the turn, so the renderer would
      // show a perpetually-blinking cursor as if streaming. Demote any
      // partial turns to a static "interrupted" state so the UI shows
      // the truncation explicitly.
      //
      // SKIPPED when `demotePartial: false` — the v2 chat loader passes
      // false because it's about to call attemptStreamResume. If resume
      // engages, the SSE replay's content_block_delta events expect the
      // partial flag still on the last turn so they can extend the
      // partial content block. The loader explicitly calls
      // demotePartialTurns() if resume does NOT engage.
      if (demotePartial) {
        for (const t of convo) {
          if (t?.partial) {
            delete t.partial;
            if (!t.stopReason) t.stopReason = "interrupted";
          }
          if (Array.isArray(t?.content)) {
            for (const b of t.content) {
              if (b && b.partial) delete b.partial;
            }
          }
        }
      }

      runInAction(() => {
        this._loads += 1;
        this.streamId = null;
        this._turnRunning = false;
        this._unsyncedNodeIds.clear();   // PLEX-6383: the tree below is the stored one
        this._compaction = compaction || null;
        this._turns.length = 0;
        this._turns.push(...convo);
        // Restore the branching tree. If the server provided one (new
        // schema), use it verbatim — including off-path siblings the user
        // can navigate back to. Otherwise rebuild a linear tree from the
        // turns array (legacy records persisted before branching landed).
        //
        // BUT: if the persisted tree's current path is shorter than the
        // flat `convo`, the tree was written from an early/partial save
        // (likely the in-flight branching code that landed before the
        // post-turn tree flush did). The flat turns are authoritative —
        // fall back to rebuilding so regenerate / branch nav work on the
        // full conversation instead of a stub.
        let useTree = tree && tree.nodes && Object.keys(tree.nodes).length > 0;
        if (useTree) {
          const candidate = ConversationTree.fromJSON(tree);
          // PLEX-4226: deterministically repair internal inconsistencies
          // (dangling cursorId/rootId/preferredChild, broken parent↔child
          // links, cursor-path cycles) BEFORE trusting the tree. A partial
          // mid-stream save or a hand-edited/legacy record can leave the cursor
          // pointing at a node that no longer exists; without this,
          // getCurrentPath() silently returns [] and the conversation renders
          // blank. sanitize() never throws, recovers deterministically while
          // preserving the last edit, and returns the repairs it made so a
          // corrupt record is debuggable. If it somehow throws, drop to the
          // linear fromTurns rebuild rather than crashing the reload.
          try {
            const repairs = candidate.sanitize();
            if (repairs.length) {
              console.warn(
                `[LLMClient] hydrated conversation tree needed ${repairs.length} repair(s); recovered deterministically`,
                repairs,
              );
            }
          } catch (e) {
            console.warn("[LLMClient] tree.sanitize threw; rebuilding tree linearly from turns", e);
            useTree = false;
          }
          // Re-check after sanitize: a repaired cursor still has to cover the
          // flat turns (a pre-MobX-proxy-fix record whose streamed assistants
          // never reached the tree yields a path shorter than the turns).
          if (useTree) {
            const pathLen = candidate.getCurrentPath().length;
            if (pathLen >= convo.length) {
              this._tree = candidate;
            } else {
              // Demoted to debug: conversations persisted by pre-MobX-proxy-fix
              // code (where streamed assistants never reached the tree) all
              // hit this branch on reload. The fromTurns fallback is correct
              // and silent — surfacing a per-reload warning was just dev noise.
              // Re-promote to warn() once historical records are aged out (or
              // gate behind a debug flag) if we want loud diagnostics again.
              console.debug(
                `[LLMClient] persisted tree path (${pathLen}) shorter than turns (${convo.length}); rebuilding linearly`,
              );
              useTree = false;
            }
          }
        }
        if (!useTree) {
          this._tree = ConversationTree.fromTurns(
            convo.map((t) => ({ role: t.role, content: t.content })),
          );
        }
        this.subAgentTurns.clear();
        Object.entries(loadedSubAgentTurns).forEach(([toolUseId, messages]) => {
          if (messages?.length) {
            messages.forEach(normalizeLegacyToolNames);
            this.subAgentTurns.set(toolUseId, messages);
          }
        });
        this._calculateMetrics();
      });
      this._visualizationStore.hydrateFromTurns(this.turns);
      return this.turns;
    }
    catch(error) {
      console.error("Couldn't parse the conversation", error);
      console.error("Error details:", error.message, error.stack);
      captureMessage("Error during initialization from JSON",
                     "error", {parsedText: parsed, error: error.message, stack: error.stack});
    }
    return false;
  }

  _formatDocumentContentBlock = fileInfo => {
    // The attachment will actually be injected server-side from a file upload cache
    const format = MIME2FORMAT[fileInfo.type];
    if (!format) {
      const typeDesc = fileInfo.type === "" ? "empty string" :
                       fileInfo.type === null ? "null" :
                       fileInfo.type === undefined ? "undefined" :
                       `"${fileInfo.type}"`;
      // Browsers report an empty/unrecognized MIME type for extensions they don't
      // register (e.g. .plex conversation exports), even though FILE_INPUT_ACCEPT
      // declares a type for them — that's expected, not actionable, so debug not warn.
      console.debug(`File "${fileInfo.name}" has unsupported MIME type ${typeDesc}, using fallback format`);
    }
    // Keep track of the File object's metadata and upload identifier
    const fileBlock = {
      name: fileInfo.name,
      type: fileInfo.type,
      size: fileInfo.size,
      lastModified: fileInfo.lastModified
    };
    // Include upload ID if available (legacy /api/upload flow).
    if (fileInfo.uploadId) {
      fileBlock.uploadId = fileInfo.uploadId;
    }
    // Project-file picker passes (program_id, project_id, file_id) so the
    // backend can resolve the bytes from the customer_files store instead of
    // the legacy attachments bucket. All three fields are required by the
    // backend resolver.
    if (fileInfo.file_id) {
      fileBlock.file_id = fileInfo.file_id;
      if (fileInfo.project_id) fileBlock.project_id = fileInfo.project_id;
      if (fileInfo.program_id) fileBlock.program_id = fileInfo.program_id;
    }
    if (isImage(fileInfo)) {
      return {image: {format: format || "png", source: {file: fileBlock}}};
    }
    return {document: {format: format || "txt", name: fileInfo.name, source: {file: fileBlock}}};
  };

  _turnsForLLM = () => {
    if (this._compaction) {
      return [...this._compaction.summary, ...this._turns.slice(this._compaction.offset)];
    }
    return this._turns;
  };

  _canonicalTurns = turns => {
    // Track all document names seen across the entire conversation
    // to prevent Bedrock ValidationException: "Messages can't contain duplicate document names"
    const globalDocNames = new Set();
    const nameCounters = new Map(); // Track how many times each base name has been used

    return turns
      .filter(turn => turn != null)
      // Exclude content_filtered turns from API history — they're incomplete responses
      // that poison follow-up context (Bedrock would receive a partial assistant turn).
      .filter(turn => turn.stopReason !== StopReason.CONTENT_FILTERED)
      .map(({role, content}) => {
        const processedContent = content.flatMap(block => {
          // reasoningContent blocks are assistant-only thinking blocks returned by the API;
          // they must never be sent back in subsequent requests (Bedrock rejects them).
          if (block.reasoningContent !== undefined) return [];

          // Citations are response-only too (PLEX-6049): a cited block replays as its text,
          // and a citation whose text never arrived (an interrupted stream) leaves an empty
          // block that the API's ContentBlock schema would reject -- dropped below.
          if (block.citations !== undefined || block.citation !== undefined) {
            const {citations: _c, citation: _c1, ...rest} = block;
            if (rest.text === undefined && Object.keys(rest).every(k => k === "partial")) return [];
            block = rest;
          }

          // Strip UI-only rendering hints before sending to the API
          if (block.rawMarkdown !== undefined || block.partial !== undefined || block.admin_detail !== undefined) {
            const {rawMarkdown: _, partial: __, admin_detail: ___, ...apiBlock} = block;
            block = apiBlock;
          }
          // PLEX-5870: stripping those keys can leave NOTHING behind. An
          // interrupted stream leaves blocks that were opened by
          // content_block_start and never received a delta, so their only
          // keys are the UI-only ones removed just above and they collapse to
          // `{}`. The API rejects that outright —
          //   Validation error: [{}, {'text': '*(Response was interrupted)*'}]
          //   is not valid under any of the given schemas - 'messages.N.content'
          // — with a 400 on POST /api/search, before any search runs. Because
          // every later question replays the whole history, ONE interruption
          // makes every subsequent question in that conversation fail with no
          // stream opened at all. Marked here and filtered below.

          // toolUse.input must be an object (api.yaml: type: object). If the stream was
          // interrupted before content_block_stop, input is still a raw JSON string —
          // parse it here so Connexion doesn't reject with a 400.
          if (block.toolUse && typeof block.toolUse.input === 'string') {
            block = {...block, toolUse: {...block.toolUse, input: safeParseJSON(block.toolUse.input) || {}}};
          }

          // toolUse.input must be an object (api.yaml: type: object). If the stream was
          // interrupted before content_block_stop, input is still a raw JSON string —
          // parse it here so Connexion doesn't reject with a 400.
          if (block.toolUse && typeof block.toolUse.input === 'string') {
            block = {...block, toolUse: {...block.toolUse, input: safeParseJSON(block.toolUse.input) || {}}};
          }

          // One-off back-compat (PLEX-6183): a connected backend still on the pre-rename
          // release (<=9.1.x) doesn't recognize TOOL_SEARCH_ANALYST as a tool-use name —
          // translate assistant history back to the legacy "search" name before replaying
          // it, or the LLM tool schema on that backend rejects the turn. Delete once no
          // cell runs a <=9.1.x backend (see the TOOL_SEARCH_ANALYST comment above).
          if (block.toolUse?.name === TOOL_SEARCH_ANALYST &&
              isLegacyBackendVersion(this._appStatusStore?.versionInfo?.version)) {
            block = {...block, toolUse: {...block.toolUse, name: LEGACY_TOOL_SEARCH_ANALYST}};
          }

          // Check if this is a document or image block with a name
          const attachmentBlock = block.document || block.image;
          if (!attachmentBlock?.name) {
            return block;
          }

          const originalName = attachmentBlock.name;

          // If this exact name hasn't been seen, use it as-is
          if (!globalDocNames.has(originalName)) {
            globalDocNames.add(originalName);
            nameCounters.set(originalName, 1);
            return block;
          }

          // Name collision - generate a unique name by appending a counter
          const counter = nameCounters.get(originalName) || 1;
          const nameParts = originalName.match(/^(.+?)(\.[^.]+)?$/);
          const baseName = nameParts[1];
          const extension = nameParts[2] || '';

          let uniqueName = originalName;
          let attempt = counter;
          while (globalDocNames.has(uniqueName)) {
            uniqueName = `${baseName} (${attempt})${extension}`;
            attempt++;
          }

          globalDocNames.add(uniqueName);
          nameCounters.set(originalName, attempt);

          // Create a new block with the renamed document/image
          const renamedBlock = {...block};
          if (block.document) {
            renamedBlock.document = {...block.document, name: uniqueName};
          } else if (block.image) {
            renamedBlock.image = {...block.image, name: uniqueName};
          }

          return renamedBlock;
        })
        // Drop blocks that carry nothing the API can interpret. Keyed on "has
        // no keys at all" rather than on any particular field: a toolUse or
        // document block legitimately has no `.text`, and a text block of ""
        // is legitimate content, so anything narrower would silently discard
        // real turns.
        .filter(block => Object.keys(block).length > 0);

        return {role, content: processedContent};
      })
      .filter(({role}) => role === ROLE_USER || role === ROLE_ASSISTANT)
      // A turn whose content pruned away to nothing is rejected just as firmly
      // as the `{}` was, so pruning must not simply move the failure up a
      // level. In practice the interrupt path always appends the
      // "(Response was interrupted)" text block, so an assistant turn retains
      // at least that; this guards the case where it does not.
      .filter(({content}) => content.length > 0);
  }

  // PLEX-6259: stop the turn on `streamId`, and every sub-agent it started, with one
  // DELETE /api/stream/{id}: the backend flags each member of the turn's channel. It
  // replaces a POST /api/agent/{id}/cancel per sub-agent. Addressed by the conversation
  // id when there is one, with the stream as the turn precondition, so a stop can't
  // land on a later turn; otherwise by the stream id. Never throws.
  _cancelTurn = (streamId = this._streamId) => {
    const target = this.historyId || streamId;
    if (!target) return Promise.resolve(false);
    return cancelStream(target, this.auth, this.historyId ? streamId : null);
  };

  _cancelAgents = (streamId = this._streamId) => {
    Analytics.event(Analytics.EVENTS.CHAT_CANCEL, {
      agent: this.sessionAgent,
      stream_id: streamId,
      elapsed_ms: this._streamStartTime ? Date.now() - this._streamStartTime : null,
      turn_count: this.turns.length,
    });
    this._cancelTurn(streamId)
      .finally(() => runInAction(() => this._finalizeStoppedSubAgents()));
  };

  // PLEX-6478: after a stop, every sub-agent keeps its trace. One still streaming is
  // finalized with the interrupted note (stoppedSubAgentTrace); finished ones are left
  // as they are. Before, every entry of the conversation was deleted, finished ones
  // too, and classic chat's next save dropped them all.
  _finalizeStoppedSubAgents = () => {
    Array.from(this.subAgentTurns.entries()).forEach(([agentId, messages]) => {
      if (!isObservableArray(messages)) return;
      const trace = stoppedSubAgentTrace(toJS(messages));
      if (trace) {
        this.subAgentTurns.set(agentId, trace);
      } else {
        this.subAgentTurns.delete(agentId);
      }
    });
  };


  _extractAgentIds = turns => {
    return turns.reduce((result, m) => {
      result.push(...m.content.reduce((ids, b) => {
        const agentId = (b.toolUse || b.toolResult)?.toolUseId;
        if (agentId) {
          ids.push(agentId);
        }
        return ids;
      }, []));
      return result;
    }, [])
  };

  // PLEX-6383: the conversation id a dispatch carries. An explicit id wins, and an
  // explicit null still sends none (the pre-6383 behaviour for callers that pass
  // one); an omitted id falls back to the engine's, which is what keeps edit,
  // regenerate and replay on the chat they came from.
  _dispatchHistoryId = (requested) => {
    const historyId = requested === undefined ? this.historyId : requested;
    if (historyId) this.historyId = historyId;
    this.historyPersisted = false;
    return historyId ?? null;
  };
  // PLEX-6491: Tim on PLEX-6491 (2026-10-06): until there is a shared-write design,
  // modifying a shared conversation makes an entirely new copy owned by the non-owner; and
  // on plex-search-ui #105, the backend is the sole UUID authority, so the copy's id comes
  // from the server. Send, resume, regenerate, edit and continue all go through
  // _sendPrompt or _continueFromToolResults, so the first of them on a conversation the
  // loader marked (_forkSourceId) asks onFork to save the user's copy first; the backend
  // answers with the copy's id, and the dispatch goes out under it. The turn never runs
  // under the owner's id (the backend refuses that with 404). No fork_of is sent: the
  // backend's fork-from-source reads the source as its owner only.
  _forkFirst = (options, dispatch) => {
    const requested = options?.historyId;
    const sourceId = requested === undefined ? this.historyId : requested;
    if (!sourceId || sourceId !== this._forkSourceId) return dispatch(options);
    this._forkSourceId = null;
    this.waitingForResponse = true;
    // Stop while the copy is being saved bumps the generation (handleInterrupt): the copy
    // is kept, but nothing is dispatched.
    const generation = this._generation;
    return Promise.resolve()
      .then(() => {
        if (!this._onFork) throw new Error("Nothing saves a copy of a conversation shared with you");
        return this._onFork(sourceId);
      })
      .then((forkId) => {
        if (!forkId || forkId === sourceId) {
          throw new Error("Your copy of the shared conversation was not saved under its own id");
        }
        runInAction(() => { this.historyId = forkId; });
        if (generation !== this._generation) {
          runInAction(() => { this.waitingForResponse = false; });
          return null;
        }
        return dispatch({...options, historyId: forkId});
      })
      .catch((error) => {
        runInAction(() => {
          // Still on the shared conversation: the next send forks again.
          if (!this.historyId || this.historyId === sourceId) this._forkSourceId = sourceId;
          this.waitingForResponse = false;
        });
        this._handleError(error);
      });
  };
  // PLEX-6383: a classic fork's first dispatch names its source and how many of the
  // source's turns it keeps (fork_of/fork_base, see forkLineage.js); the backend copies
  // them from the stored source. Nothing is sent for any other dispatch.
  _forkParams = (forkOf, forkBase, forkId) => forkLineage(forkOf, forkId, forkBase);
  // PLEX-6383: record whether the backend took this dispatch's history write, and
  // adopt the id it persisted under -- which it minted, if we sent none. The
  // response carries these only when the backend owns history; otherwise the
  // client keeps writing, exactly as before.
  _adoptDispatchHistory = (resp) => {
    if (resp?.history_persisted !== true) return;
    this.historyPersisted = true;
    if (resp.history_id) this.historyId = resp.history_id;
  };

  sendPrompt = ({
                  text,
                  attachments = null,
                  searchParams = null,
                  historyId = undefined,
                  ...params
                }) => {
    const prompt = {
      role: ROLE_USER,
      content: [{text: text}],
    };
    if (attachments && this.sessionAgent !== AGENT_HELP) {
      attachments.forEach(fileInfo => {
        prompt.content.push(this._formatDocumentContentBlock(fileInfo));
      });
    }
    return this._sendPrompt(prompt, {...params, searchParams, historyId});
  }

  // PLEX-6491: every send forks a conversation shared with the user first (_forkFirst).
  _sendPrompt = (prompt, options = {}) =>
    this._forkFirst(options, (opts) => this._dispatchPrompt(prompt, opts));

  // PLEX-6187: a display-only turn (the "session was interrupted" placeholder after a crash)
  // that a dispatch sends to the model is part of the conversation from then on, and is saved
  // like any other turn. A new prompt is even appended to it.
  _sendDisplayOnlyTurns = () => {
    runInAction(() => {
      this.turns.forEach(t => { if (t?.displayOnly) delete t.displayOnly; });
    });
  };

  _dispatchPrompt = (prompt, {
    chatSettings = {},
    templates = {},
    useMarkdown = true,
    searchParams = null,
    useTools = true,
    historyId: requestedHistoryId = undefined,
    forkOf = null,
    forkBase = null,
    modelOverride = null,
  } = {}) => {
    const historyId = this._dispatchHistoryId(requestedHistoryId);
    this._lastSendParams = {chatSettings, templates, useMarkdown, searchParams, useTools, historyId, forkOf, forkBase, modelOverride};
    this.active = true;
    // PLEX-6303: this engine is attached to a turn again. A refusal because another turn
    // is still running (409) marks it running anew.
    this._turnRunning = false;
    this._staleEventReported = false;
    // Start a new send cycle. If the user interrupts before this cycle's fetch
    // resolves, handleInterrupt bumps past this value (see below), so the late
    // .then() can tell it's stale and must not revive streamId/busy state.
    const generation = ++this._generation;
    this._interruptHandled = false;

    const nullCount = this.turns.filter(t => t == null).length;
    if (nullCount > 0) {
      this._captureStreamAnomaly(
        `_sendPrompt: ${nullCount} null/undefined turn(s) in conversation at prompt start`,
        {
          level: "error",
          fingerprint: ["null-turns-at-send"],
          tags: { streamId: this.streamId, agentId: this.agentId },
          extra: {
            nullCount,
            totalTurns: this.turns.length,
            ...this._buildConversationCapture().context,
          },
        }
      );
      runInAction(() => {
        this.turns.splice(0, this.turns.length, ...this.turns.filter(t => t != null));
      });
    }

    this._sendDisplayOnlyTurns();

    // Check if the last turn is a user message
    // If so, append the new prompt content to it rather than creating a new turn
    // This prevents consecutive user messages which violates the alternation requirement
    const lastTurn = this.turns.length > 0 ? this.turns.at(-1) : null;

    // Only append to existing turn if both are user turns
    // Never append assistant content to a user turn or vice versa
    if (lastTurn?.role === ROLE_USER && prompt.role === ROLE_USER) {
      // Append the new prompt content to the existing user turn
      prompt.content.forEach(block => lastTurn.content.push(block));
    } else {
      // Create a new turn as usual
      this._pushTurn({...prompt, ts: Date.now()});
    }

    this.waitingForResponse = true;
    const _sendStart = Date.now();
    console.debug(`[LLMClient] _sendPrompt: serializing turns`, {turnCount: this.turns.length});
    const _canonicalMessages = this._canonicalTurns(toJS(this._turnsForLLM()));
    const _serializeMs = Date.now() - _sendStart;
    console.debug(`[LLMClient] _sendPrompt: serialized ${_canonicalMessages.length} turns in ${_serializeMs}ms`);
    Analytics.event('chat-submit-perf', {
      serialize_ms: _serializeMs,
      turn_count: this.turns.length,
      canonical_count: _canonicalMessages.length,
      stream_id: this.streamId,
    });
    const postTimeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out waiting for server response")), POST_TIMEOUT()));
    return Promise.race([
      (this.sessionAgent === AGENT_HELP
            ? fetchHelp(toJS(prompt.content.map(el => el.text || "").join("")),
                        this.sessionId && `${this.sessionId}:${this.turns.length - 1}`,
                        this.auth)
            : fetchChatCompletion(searchParams || {},
                                  {
                                    model: modelOverride ?? this.model,
                                    ...chatSettings,
                                    agentId: this.agentId,
                                    stream: true,
                                    messages: _canonicalMessages,
                                    templates: templates,
                                    useMarkdown,
                                    useTools: useTools,
                                    agent: this.sessionAgent,
                                    historyId,
                                    ...this._forkParams(forkOf, forkBase, historyId),
                                    ...this._treeAnchor(),
                                    program_id: this._v2ProgramId,
                                    project_id: this._v2ProjectId,
                                  }, this.auth)),
      postTimeout,
    ])
      .then(resp => {
        // Stale response guard: the user may have clicked interrupt while this
        // fetch was in flight. handleInterrupt() doesn't (and can't) cancel an
        // already-sent fetch — it only resets local state — so this .then() can
        // fire *after* the interrupt already put the UI back to idle. Reviving
        // streamId here would resurrect "Plexy is thinking...", "Still connecting
        // to Plexy...", and the stop button for a request the user already
        // cancelled. Only apply the response if no interrupt happened since this
        // cycle started.
        if (generation !== this._generation) {
          console.debug(`[LLMClient] _sendPrompt: dropping stale response for generation ${generation} (current ${this._generation})`);
          return;
        }
        const {stream_id: streamId = null, session_id: sessionId} = resp;
        runInAction(() => {
          this._adoptDispatchHistory(resp);
          if (this.sessionAgent === AGENT_HELP) {
            this.sessionId = sessionId;
            // FIXME stream the response instead of sync call
            this._pushTurn({role: ROLE_ASSISTANT, content: [{text: resp.response}]});
          }
          this.streamId = streamId;
        });
      })
      .catch(error => {
        if (generation !== this._generation) {
          console.debug(`[LLMClient] _sendPrompt: dropping stale error for generation ${generation} (current ${this._generation})`);
          return;
        }
        this._handleError(error);
      })
      .finally(() => {
        if (generation === this._generation) {
          this.waitingForResponse = false;
        }
      });
  }

  // Coach proposed a query rewrite; the user accepted, edited, or rejected it.
  // Replace the user turn's text with `finalText`, drop the brief
  // "reviewing your query" assistant turn that came after, and re-run
  // the pipeline with the chosen text. Replaces the user's bubble in
  // history so the conversation log is clean.
  //
  // Preserves the proposal as `rewriteApplied` so the bubble can still
  // show a "(reframed)" indicator that expands the original + changes —
  // the teaching content survives the click.
  acceptRewrite = (turnIndex, finalText) => {
    runInAction(() => {
      const turn = this.turns[turnIndex];
      if (!turn) return;
      const original = turn.content?.[0]?.text ?? "";
      const proposal = turn.rewrite || null;
      // Replace the text content (preserve any non-text blocks like attachments)
      const nonTextBlocks = (turn.content || []).filter(b => !("text" in b));
      turn.content = [{ text: finalText }, ...nonTextBlocks];
      delete turn.rewrite;
      if (proposal) {
        turn.rewriteApplied = {
          original,
          rewritten: proposal.rewritten,
          changes: proposal.changes || [],
          finalText,
          // What the user actually did — so the popover can phrase it accurately.
          resolution:
            finalText === original ? "rejected"
            : finalText === proposal.rewritten ? "accepted"
            : "edited",
        };
      }
    });
    this.replayFrom(turnIndex);
  };
  // Truncate turns to newLength and release any subAgentTurns entry that was only
  // referenced by a discarded turn's toolUse block. subAgentTurns is flat and
  // keyed by toolUseId for the whole conversation — a toolUseId discarded here
  // stops being reachable from any remaining toolUse block, so its entry is dead
  // weight. Any that are still actively streaming (a live observable.array, not
  // yet swapped for the finalized plain array at toolResult close) get cancelled
  // rather than silently dropped, per the same rule _cancelAgents follows.
  _truncateTurns = (newLength) => {
    const discardedIds = new Set();
    for (let i = newLength; i < this.turns.length; i++) {
      (this.turns[i]?.content || [])
        .filter(b => b.toolUse)
        .forEach(b => discardedIds.add(b.toolUse.toolUseId));
    }
    for (let i = 0; i < newLength; i++) {
      (this.turns[i]?.content || [])
        .filter(b => b.toolUse)
        .forEach(b => discardedIds.delete(b.toolUse.toolUseId));
    }
    const stillActive = Array.from(discardedIds).filter(id => isObservableArray(this.subAgentTurns.get(id)));
    discardedIds.forEach(id => this.subAgentTurns.delete(id));
    this.turns.length = newLength;
    if (stillActive.length) {
      // PLEX-6259: a sub-agent can't be stopped on its own any more; it runs inside a
      // turn, and the turn is what's cancelled (with all its sub-agents).
      this._cancelTurn();
    }
  }

  // A user turn that only answers tools (or carries the interrupted note): replayFrom
  // continues from it rather than resending it.
  _isToolResultTurn = (turn) => turn?.role === ROLE_USER &&
    !!turn.content?.every(b => b.toolResult || b.text?.match(/^\*\(Response was interrupted\)\*$/));

  // PLEX-6383: how many of the conversation's turns replayFrom(index) keeps: through a
  // tool-result turn, which it continues from, else up to the turn it resends. A replay's
  // fork lineage must name the same count (fork_base), whichever replay button asked.
  replayKeptTurns = (index) => (this._isToolResultTurn(this.turns[index]) ? index + 1 : index);

  replayFrom = (index, params) => {
    this.streamId = null;
    const turn = this.turns[index];

    if (this._isToolResultTurn(turn)) {
      // Keep all turns up through and including the tool results, then ask the LLM to continue.
      this._setTurnsLength(index + 1);
      this._calculateMetrics();
      this._continueFromToolResults(params);
    } else {
      this._setTurnsLength(index);
      this._calculateMetrics();
      if (turn.role === ROLE_USER || turn.stopReason === StopReason.TOOL_USE) {
        this._sendPrompt(turn, params);
      }
    }
  }

  // PLEX-6187 (spec §4.6): Resume on a conversation a crash left mid-turn. The backend re-drives
  // the stuck turn from its stored record (rd #399): this sends the turns up to the stuck tail
  // with resume=true, without the display placeholder, and with no fork or tree anchor, since a
  // resume appends to the record as it stands. What the backend can't resume it refuses with a
  // reason, and each has one answer here. A conversation that isn't stored, or isn't the user's
  // own (it would be forked first), continues as it always has. Resolves to what happened.
  resumeFrom = (index, params = {}) => this._resumeFrom(index, params, false);

  // withDisplayOnly: the record turned out to store the placeholder itself (a client from
  // before PLEX-6187 saved what it loaded), so this resume sends it, as the record's tail.
  _resumeFrom = (index, {
    chatSettings = {},
    templates = {},
    useMarkdown = true,
    searchParams = null,
    useTools = true,
    historyId: requestedHistoryId = undefined,
    modelOverride = null,
  } = {}, withDisplayOnly) => {
    const params = {chatSettings, templates, useMarkdown, searchParams, useTools, historyId: requestedHistoryId, modelOverride};
    const stored = requestedHistoryId === undefined ? this.historyId : requestedHistoryId;
    if (!stored || stored === this._forkSourceId) {
      this.replayFrom(index, params);
      return Promise.resolve("continued");
    }
    // The placeholder only ever trails, so the turns that stay are a prefix.
    const upTo = this.turns.slice(0, index + 1);
    const kept = withDisplayOnly ? upTo : withoutDisplayOnly(upTo);
    this.active = true;
    this._turnRunning = false;   // PLEX-6303: attached to a turn again, as in _sendPrompt
    const historyId = this._dispatchHistoryId(stored);
    this._lastSendParams = {chatSettings, templates, useMarkdown, searchParams, useTools, historyId, forkOf: null, forkBase: null, modelOverride};
    this.waitingForResponse = true;
    // A new send cycle, as in _dispatchContinue.
    const generation = ++this._generation;
    const loads = this._loads;
    const stale = () => generation !== this._generation || loads !== this._loads;
    this._interruptHandled = false;
    const postTimeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out waiting for server response")), POST_TIMEOUT()));
    return Promise.race([
      fetchChatCompletion(searchParams || {},
                          {
                            ...this._continueRequest({chatSettings, templates, useMarkdown, useTools, modelOverride},
                                                     historyId, this._canonicalTurns(toJS(kept))),
                            resume: true,
                          }, this.auth),
      postTimeout,
    ])
      // Stop bumps the generation, and opening another conversation the load count.
      .then(resp => {
        if (stale()) return "interrupted";
        const {stream_id: streamId = null} = resp;
        runInAction(() => {
          if (withDisplayOnly) this._sendDisplayOnlyTurns();
          this._setTurnsLength(kept.length);
          this._calculateMetrics();
          this._adoptDispatchHistory(resp);
          this.streamId = streamId;
        });
        return "resumed";
      })
      .catch(error => {
        if (stale()) return "interrupted";
        const refusal = resumeRefusal(error);
        if (refusal === "stale_view" && kept.length < upTo.length) {
          return this._resumeFrom(index, params, true);
        }
        if (refusal === "not_resumable") {
          this.replayFrom(index, params);
          return "continued";
        }
        if (refusal) {
          this.markTurnEnded();
          return "reload";
        }
        if (isLiveTurnConflict(error)) {
          this.markTurnRunning();
          return "running";
        }
        this._handleError(error);
        return "failed";
      })
      .finally(() => {
        if (generation === this._generation) {
          this.waitingForResponse = false;
        }
      });
  }

  // Ask the LLM to continue from the current turns (which end with tool results).
  // Unlike _sendPrompt, this does not push a new turn — the tool results are already in turns.
  // PLEX-6491: a conversation shared with the user is forked first (_forkFirst).
  _continueFromToolResults = (options = {}) =>
    this._forkFirst(options, (opts) => this._dispatchContinue(opts));

  // The chat request that continues a conversation from its turns as they stand: a continue
  // (_dispatchContinue) and a resume (resumeFrom).
  _continueRequest = ({chatSettings, templates, useMarkdown, useTools, modelOverride}, historyId, messages) => ({
    model: modelOverride ?? this.model,
    ...chatSettings,
    agentId: this.agentId,
    stream: true,
    messages,
    templates,
    useMarkdown,
    useTools,
    agent: this.sessionAgent,
    historyId,
    program_id: this._v2ProgramId,
    project_id: this._v2ProjectId,
  });

  _dispatchContinue = ({
    chatSettings = {},
    templates = {},
    useMarkdown = true,
    searchParams = null,
    useTools = true,
    historyId: requestedHistoryId = undefined,
    forkOf = null,
    forkBase = null,
    modelOverride = null,
  } = {}) => {
    this.active = true;
    this._turnRunning = false;   // PLEX-6303: attached to a turn again, as in _sendPrompt
    this._sendDisplayOnlyTurns();
    const historyId = this._dispatchHistoryId(requestedHistoryId);
    this._lastSendParams = {chatSettings, templates, useMarkdown, searchParams, useTools, historyId, forkOf, forkBase, modelOverride};
    this.waitingForResponse = true;
    // Start a new send cycle — see _sendPrompt for why this guards against a
    // late-resolving fetch reviving state after the user already interrupted.
    const generation = ++this._generation;
    this._interruptHandled = false;
    const postTimeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out waiting for server response")), POST_TIMEOUT()));
    Promise.race([
      fetchChatCompletion(searchParams || {},
                          {
                            ...this._continueRequest({chatSettings, templates, useMarkdown, useTools, modelOverride},
                                                     historyId, this._canonicalTurns(toJS(this._turnsForLLM()))),
                            ...this._forkParams(forkOf, forkBase, historyId),
                            ...this._treeAnchor(),
                          }, this.auth),
      postTimeout,
    ])
      .then(resp => {
        if (generation !== this._generation) {
          console.debug(`[LLMClient] _continueFromToolResults: dropping stale response for generation ${generation} (current ${this._generation})`);
          return;
        }
        const {stream_id: streamId = null} = resp;
        runInAction(() => {
          this._adoptDispatchHistory(resp);
          this.streamId = streamId;
        });
      })
      .catch(error => {
        if (generation !== this._generation) {
          console.debug(`[LLMClient] _continueFromToolResults: dropping stale error for generation ${generation} (current ${this._generation})`);
          return;
        }
        this._handleError(error);
      })
      .finally(() => {
        if (generation === this._generation) {
          this.waitingForResponse = false;
        }
      });
  }

  // Keep the first `keepCount` turns, discard the rest.
  discardAfter = keepCount => {
    runInAction(() => {
      this.streamId = null;
      if (keepCount === 0) {
        this.sessionId = null;
      }
      this._setTurnsLength(keepCount);
      this._calculateMetrics();
    });
  }

  interrupt = () => {
    this.handleInterrupt();
  }

  /**
   * Demote every partial turn (and partial content block) on the
   * engine to a static "interrupted" state. Used by useChatLoader
   * when a loadConversation was deferred-demote (because resume was
   * about to be attempted) but resume did NOT engage — without this
   * the UI would show a perpetual streaming cursor on a turn whose
   * SSE stream is actually dead.
   *
   * This mirrors the demotion that loadConversation does by default
   * (see the `demotePartial` parameter there). Idempotent: turns that
   * are already non-partial are left alone.
   */
  demotePartialTurns = () => {
    runInAction(() => {
      for (const t of this._turns) {
        if (t?.partial) {
          delete t.partial;
          if (!t.stopReason) t.stopReason = "interrupted";
        }
        if (Array.isArray(t?.content)) {
          for (const b of t.content) {
            if (b && b.partial) delete b.partial;
          }
        }
      }
    });
  }

  /**
   * PLEX-6303: the conversation's turn is still running on the backend and this engine
   * isn't attached to it, so the last turn's tool call is waiting for its result. Set by the
   * v2 loader after a deferred load, and on a dispatch the backend refused because a turn
   * is already running. Leaves streamId alone, so it can never cancel anything.
   */
  markTurnRunning = () => {
    runInAction(() => {
      this._turnRunning = true;
      this.active = false;
      this.waitingForResponse = false;
    });
  }

  clearTurnRunning = () => {
    runInAction(() => { this._turnRunning = false; });
  }

  /**
   * PLEX-6439: the stream dropped and the backend says the turn has ended, in a conversation
   * the backend saves, so the turn's result is in its stored record. Detach as for a turn that
   * goes on (active is cleared first, so the streamId setter sends no DELETE), and have the
   * running-turn watch ask again at once, which reloads the record.
   */
  markTurnEnded = () => {
    runInAction(() => {
      this.active = false;
      this.streamId = null;
      this._recheckTurnNow = true;
    });
    this.markTurnRunning();
  }

  // The running-turn watch's one-shot "ask now" (markTurnEnded).
  takeRecheckTurnNow = () => {
    const now = this._recheckTurnNow;
    runInAction(() => { this._recheckTurnNow = false; });
    return now;
  }

  clear = ({ preserveStream = false } = {}) => {
    runInAction(() => {
      this._loads += 1;
      // Clear conversation state for a fresh start.
      //
      // preserveStream: navigating to ANOTHER chat while this one is still
      // streaming. We reset the engine for the new view but must NOT cancel
      // the outgoing backend agent — set `active = false` BEFORE nulling
      // streamId so the streamId-setter's cancelAgents branch stays inactive
      // (same trick as dispose()). The outgoing stream keeps running; its
      // saved cursor lets attemptStreamResume reattach on return. Without
      // this, a chat-switch mid-stream POSTed a cancel and the in-flight
      // answer was lost ("browse away → interrupted → history gone").
      if (preserveStream) {
        // Mark the engine as deliberately preserving a backend agent so a
        // late SSE event (already in flight when we tore down locally)
        // doesn't trip the stale-event guard's cancel and kill the
        // agent we want to resume. Backend disconnect-grace owns orphan
        // cleanup. Cleared when a fresh/resumed stream starts (streamId
        // null->truthy in the setter).
        this._preserveStreamActive = true;
        this.active = false;
      }
      // Cancel the turn behind any sub-agents still actively streaming before dropping
      // their entries (same rule as _truncateTurns/_cancelAgents) rather than a bare
      // clear(). PLEX-6259: that cancel stops the whole turn, so it is skipped for a
      // preserved stream; before, it stopped only the sub-agents, which still cut the
      // preserved answer short.
      const stillActive = Array.from(this.subAgentTurns.entries())
        .filter(([, v]) => isObservableArray(v))
        .map(([id]) => id);
      const conversationId = this.historyId;
      const endingStream = this._streamId;
      this.streamId = null;
      this._resetTurnsAndTree();
      this.historyId = null;
      this._forkSourceId = null;
      this.historyPersisted = false;
      this.subAgentTurns.clear();
      this._reportedMissingAgents.clear();
      this.plan = null;
      this.sessionId = null;
      this._calculateMetrics();
      if (stillActive.length && !preserveStream) {
        cancelStream(conversationId || endingStream, this.auth, conversationId ? endingStream : null);
      }
    });
    memoryStore.clear();
    // The visualization store is a module-level singleton — without
    // an explicit clear, charts created in a previous chat persist
    // into "+ New chat" empty state. loadConversation's hydrateFromTurns
    // already clears+rebuilds when loading an existing chat, but the
    // new-chat path (useChatLoader → clear, no hydrate) needs us
    // to wipe charts here too.
    this._visualizationStore?.clear?.();
  }

  /**
   * Create conversation data with full context for export/replay
   * This includes metadata needed to properly restore, replay, and debug conversations
   */
  conversationWithContext = ({
    searchParams = {},
    editedTemplates = {},
    conversationContext = null,
    conversationTitle = null,
    addCurrentUser = false
  }) => {
    const { cell, env } = getConfig();
    const currentUser = this._userStore?.userName;

    // Determine authors list
    let authors = conversationContext?.authors ? [...conversationContext.authors] : [];

    if (addCurrentUser && currentUser && !authors.includes(currentUser)) {
      authors.push(currentUser);
    }

    if (authors.length === 0 && addCurrentUser && currentUser) {
      authors = [currentUser];
    }

    const context = {
      title: conversationTitle || this._generateFallbackTitle(),
      searchParams,
      editedTemplates,
      user: currentUser,
      userID: this._userStore?.userID,
      tenantID: this._userStore?.tenantID,
      authors: authors.length > 0 ? authors : undefined,
      cell: cell,
      env: env,
      timestamp: new Date().toISOString(),
      version: getVersionInfo(),
      model: this._model,
    };

    // Optimize sub-agent turns for persistence (strip redundant tool results,
    // preserve resolve/fetch_entities/validate_plex_ids in full) — same rule
    // cleanTurn used to apply per-turn, now applied once to the flat top-level map.
    const optimizedSubAgentTurns = {};
    this.subAgentTurns.forEach((messages, toolUseId) => {
      const optimized = optimizeAgentMessages(toJS(messages));
      if (optimized) optimizedSubAgentTurns[toolUseId] = optimized;
    });

    return {
      context,
      turns: this._turns.map(turn => cleanTurn(toJS(turn), true)),
      subAgentTurns: optimizedSubAgentTurns,
    };
  };

  _generateFallbackTitle = () => {
    if (this.turns.length === 0) return "Untitled Conversation";

    // Find first user message
    const firstUserTurn = this.turns.find(t => t != null && t.role === ROLE_USER);
    if (!firstUserTurn) return "Untitled Conversation";

    // Extract text from first user message
    const textBlocks = firstUserTurn.content?.filter(b => b.text) || [];
    if (textBlocks.length === 0) return "Untitled Conversation";

    const text = textBlocks[0].text.trim();
    // Use first 50 chars as title
    return text.length > 50 ? text.substring(0, 47) + "..." : text;
  };

  /**
   * Convert conversation to plain text representation
   */
  conversationAsText = ({includeToolUse = false, dsConfig = null, extractConversationText}) => {
    return this.turns
      .reduce((result, turn) =>
        `${result}\n${extractConversationText(turn.role, turn.content, includeToolUse, dsConfig)}`,
        ""
      );
  };

  /**
   * Download conversation as JSON or text
   */
  downloadConversation = async ({
    asJSON = false,
    asPlex = false,
    searchParams = {},
    editedTemplates = {},
    conversationContext = null,
    conversationTitle = null,
    dsConfig = null,
    extractConversationText = null,
    includeToolUse = false
  }) => {
    const conversationData = this.conversationWithContext({
      searchParams,
      editedTemplates,
      conversationContext,
      conversationTitle
    });

    // Generate a proper title via LLM if we only have the fallback truncated version
    let title = conversationData.context.title;
    const firstUserTurn = this.turns.find(t => t != null && t.role === ROLE_USER);
    const firstText = firstUserTurn?.content?.find(b => b.text)?.text?.trim() || '';
    const isFallbackTitle = firstText && title === (firstText.length > 50 ? firstText.substring(0, 47) + "..." : firstText);
    if (isFallbackTitle && this._auth) {
      const conversationText = firstText.slice(0, 2000);
      const titlePrompt = `Generate a concise, descriptive title for this conversation.
Use natural language with proper capitalization. Maximum 60 characters.
Only return the title, nothing else.

Conversation:
${conversationText}`;
      title = await oneshot(titlePrompt, this._auth, { input_format: "text", output_format: "text" })
        .then(r => r.text())
        .then(t => t.trim().replace(/^["']|["']$/g, ''))
        .catch(() => title);
    }

    const filenameSlug = title
      ? title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60)
      : null;

    const baseFilename = filenameSlug || title || 'conversation';

    if (asJSON) {
      const text = JSON.stringify(conversationData);
      const blob = new Blob([text], { type: "application/json" });
      // Use .plex extension when asPlex flag is true
      const extension = asPlex ? '.plex' : '.json';
      saveBlob(blob, `${baseFilename}${extension}`);
    } else {
      if (!extractConversationText) {
        throw new Error("extractConversationText function required for text export");
      }
      const text = this.conversationAsText({includeToolUse, dsConfig, extractConversationText});
      const blob = new Blob([text], { type: "text/plain" });
      saveBlob(blob, `${baseFilename}.txt`);
    }
  };

  /**
   * Generate and download a report summarizing the conversation.
   *
   * outputFormat is one of "pdf" | "docx" | "pptx". The v2 chat-level report
   * action (pages/workspace/ReportMenu.js, PLEX-4229) drives the "pdf" path; the
   * classic ChatDownloadMenu drives "docx"/"pptx". All three ride the same
   * async /api/oneshot pipeline (201/202 job polling → OneshotTimeoutError).
   *
   * Report scope: whole chat by default. Pass `turnIndex` to scope the report
   * to a single answer — that assistant turn plus the user turn that prompted
   * it (turnIndex indexes the displayed `this.turns`).
   */
  downloadSummary = async ({
    outputFormat = "pptx",
    verbose = false,
    searchParams = {},
    editedTemplates = {},
    conversationContext = null,
    conversationTitle = null,
    aiTemplates = {},
    turnIndex = null,
    onStart = () => {},
    onComplete = () => {},
    onError = () => {}
  }) => {
    onStart(outputFormat);

    const conversationData = this.conversationWithContext({
      searchParams,
      editedTemplates,
      conversationContext,
      conversationTitle
    });

    const title = conversationData.context.title;
    const filenameSlug = title
      ? title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60)
      : null;

    const MIME_BY_FORMAT = {
      pdf: "application/pdf",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    };
    const options = {
      input_format: "markdown",
      output_format: outputFormat,
      mime_type: MIME_BY_FORMAT[outputFormat] || MIME_BY_FORMAT.docx,
      verbose,
      ...(filenameSlug && { filename: filenameSlug }),
    };

    // Report scope. Whole chat uses _turnsForLLM() so compacted conversations
    // send the summary turn instead of the raw pre-compaction turns. A
    // single-answer report slices the displayed turns [question, answer] so the
    // index lines up with what the user sees in the UI.
    const scopeTurns = (turnIndex != null && turnIndex >= 0)
      ? this.turns.slice(Math.max(0, turnIndex - 1), turnIndex + 1)
      : this._turnsForLLM();

    // Prune conversation before sending: keep only text content from user/assistant turns.
    // Mirrors backend prune_conversation() — removes tool use, tool results, sub-agent
    // turns, and metadata that bloat the payload but aren't needed for report generation.
    // Use _turnsForLLM() so that compacted conversations include the summary turn instead of
    // the raw (pre-compaction) turns that conversationData.turns contains.
    const prunedData = {
      context: { title: conversationData.context.title },
      turns: scopeTurns
        .map(turn => {
          const textBlocks = (turn.content || []).filter(b => b.text).map(b => ({ text: b.text }));
          return textBlocks.length > 0 ? { role: turn.role, content: textBlocks } : null;
        })
        .filter(Boolean)
    };
    const conversationJSON = JSON.stringify(prunedData);

    const key = outputFormat === "pptx" ? "presentation-prompt" : "report-prompt";
    const baseTemplate = aiTemplates[key] || `Make a report about the following text\n${TEXT_PLACEHOLDER}`;
    const userCustomText = editedTemplates[key] || "";
    const prompt = userCustomText ? `${baseTemplate}\n\n${userCustomText}` : baseTemplate;
    const fullPrompt = prompt.replace(TEXT_PLACEHOLDER, `<input-text>${conversationJSON}</input-text>`);

    try {
      const response = await oneshot(fullPrompt, this._auth, options);

      const header = response.headers.get('content-disposition');
      let filename = `${filenameSlug || 'report'}.${outputFormat}`;

      if (header) {
        try {
          const disposition = parseContentDisposition(header);
          filename = disposition.parameters.filename || filename;
        } catch (e) {
          console.warn('Failed to parse Content-Disposition header:', header, e);
          captureMessage('Malformed Content-Disposition header in summary generation', {
            level: "warning",
            fingerprint: ["content-disposition-parse-error"],
            tags: {
              error_type: "content_disposition_parse",
              output_format: outputFormat,
            },
            context: {
              header: header,
              error: e.toString()
            }
          });
        }
      }

      const blob = await response.blob();
      saveBlob(blob, filename);
      onComplete(outputFormat);
    } catch (error) {
      console.error(`Failed to generate ${outputFormat}:`, error);
      const isTimeout = error.name === 'OneshotTimeoutError';
      captureMessage(`Summary generation ${isTimeout ? 'timed out' : 'failed'}: ${error.message}`, {
        level: "error",
        asException: true,
        errorName: isTimeout ? "SummaryGenerationTimeout" : "SummaryGenerationError",
        fingerprint: isTimeout ? ["summary-generation-timeout", outputFormat] : ["summary-generation-error", outputFormat],
        tags: {
          error_type: isTimeout ? "summary_timeout" : "summary_generation",
          output_format: outputFormat,
        },
        context: {
          summary_error: {
            error_message: error.message,
            output_format: outputFormat,
            verbose,
            conversation_turn_count: prunedData.turns.length,
            prompt_length: fullPrompt.length,
            conversation_json_length: conversationJSON.length,
          },
        },
        attachments: [
          {
            filename: "conversation.json",
            data: conversationJSON,
            contentType: "application/json",
          },
          {
            filename: "prompt.txt",
            data: fullPrompt,
            contentType: "text/plain",
          },
        ],
      });
      onError(outputFormat, error);
    }
  };

  dispose = () => {
    // Only stop streaming, don't clear conversation
    // The conversation should persist until explicitly discarded
    // or restored from storage on component remount.
    //
    // Set `active = false` BEFORE clearing streamId so the streamId
    // setter's `cancelAgents = this.active && _streamId !== null &&
    // streamId === null` evaluates false. dispose is called from
    // 'beforeunload' and the unmount cleanup — both of which want to
    // preserve the backend agent so attemptStreamResume on the next
    // page load can reattach within the BE's 30s SSE grace window. An
    // explicit cancel (DELETE /api/stream/<id>) here would race that grace
    // and kill the agent before the resume could reach it.
    this._preserveStreamActive = true;
    this.active = false;
    this.streamId = null;
  };
}
